import axios from "axios";
import type { MediaKitMetricsWindow, PodcastMediaKit } from "../api/podcasts";
import { ROUTES_NAMES } from "../components/routes";
import { isPodcastInvitePath } from "../utils/invitePaths";
import { queryClient } from "../queryClient";
import { clearAppSessionState } from "../auth/clearAppSessionState";
import type {
  MarketingEmailCampaignDto,
  MarketingEmailCampaignsResponse,
  MarketingEmailComposePayload,
  MarketingEmailPreview,
  MarketingEmailRecipient,
  MarketingEmailRecipientsResponse,
} from "./marketingEmailTypes";

export type {
  MarketingEmailCampaignDto,
  MarketingEmailCampaignsResponse,
  MarketingEmailComposePayload,
  MarketingEmailPreview,
  MarketingEmailRecipient,
  MarketingEmailRecipientsResponse,
} from "./marketingEmailTypes";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // 2 minutes - increased for heavy political data aggregations
  headers: {
    "Content-Type": "application/json",
  },
});

function filenameFromContentDisposition(
  contentDisposition: string | undefined,
  fallback: string,
): string {
  if (!contentDisposition) return fallback;
  const starMatch = contentDisposition.match(/filename\*=UTF-8''([^;\s]+)/i);
  if (starMatch?.[1]) return decodeURIComponent(starMatch[1]);
  const plainMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  if (plainMatch?.[1]) return plainMatch[1];
  return fallback;
}

function downloadBlobFile(
  blobData: Blob,
  contentType: string,
  contentDisposition: string | undefined,
  fallbackFilename: string,
): void {
  const filename = filenameFromContentDisposition(
    contentDisposition,
    fallbackFilename,
  );
  const blob = new Blob([blobData], { type: contentType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function downloadMiContenidoMetricsPdfBlob(
  blobData: Blob,
  contentDisposition?: string,
): void {
  downloadBlobFile(
    blobData,
    "application/pdf",
    contentDisposition,
    "informe-metricas.pdf",
  );
}

async function messageFromBlobError(error: unknown): Promise<string | null> {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (!(data instanceof Blob)) return null;
  try {
    const parsed = JSON.parse(await data.text()) as {
      error?: string;
      message?: string;
    };
    return parsed.error || parsed.message || null;
  } catch {
    return null;
  }
}

export { api as apiClient };

/** GETs a file from the API and saves it; JSON error bodies become thrown Error messages. */
export async function downloadApiFile(
  path: string,
  fallbackFilename: string,
): Promise<void> {
  try {
    const response = await api.get<Blob>(path, { responseType: "blob" });
    const blob = response.data;
    if (blob.type && blob.type.includes("json")) {
      const parsed = JSON.parse(await blob.text()) as {
        error?: string;
        message?: string;
      };
      throw new Error(parsed.error || parsed.message || "Export failed");
    }
    const headerType = response.headers["content-type"];
    downloadBlobFile(
      blob,
      blob.type ||
        (typeof headerType === "string" ? headerType : "application/octet-stream"),
      response.headers["content-disposition"],
      fallbackFilename,
    );
  } catch (error) {
    const fromBlob = await messageFromBlobError(error);
    if (fromBlob) throw new Error(fromBlob);
    if (error instanceof Error) throw error;
    throw new Error("Export failed");
  }
}

export type PodcastTaxonomyFilters = {
  category?: string;
  /** One type, or several. Empty and `all` leave every channel in the ranking. */
  channelType?: string | string[];
};

export type ExplorerSocialPlatform = "facebook" | "tiktok" | "instagram" | "youtubeShorts";

export type ExplorerSocialMetrics = {
  posts: number;
  clips: number;
  staticPosts: number;
  views: number;
  interactions: number;
  clipInteractions: number;
  postInteractions: number;
};

export type ExplorerSocialCollabPublisher = {
  publisher: string;
  platform: ExplorerSocialPlatform;
  clips: number;
  posts: number;
  views: number;
};

export type ExplorerSocialChannel = {
  _id: string;
  name: string;
  slug?: string;
  imageS3Key?: string;
  podcastId: string | null;
  connected: Record<ExplorerSocialPlatform, boolean>;
  handles: Record<ExplorerSocialPlatform, string | null>;
  followers: Record<ExplorerSocialPlatform, number | null>;
  total: ExplorerSocialMetrics;
  publication?: { owned: number; collab: number };
  publishers?: ExplorerSocialCollabPublisher[];
  facebook: ExplorerSocialMetrics | null;
  tiktok: ExplorerSocialMetrics | null;
  instagram: ExplorerSocialMetrics | null;
  youtubeShorts: ExplorerSocialMetrics | null;
};

export type ExplorerSocialRankingChannel = {
  _id: string;
  name: string;
  slug?: string;
  imageS3Key?: string;
  podcastId: string | null;
  posts: number;
  views: number;
  viewsPerPost: number | null;
  interactions: number;
  engagement: number | null;
};

export type ExplorerSocialPostingHeatmap = {
  channels: Array<{ _id: string; name: string; imageS3Key?: string }>;
  cells: Array<{
    dayIndex: number;
    hour: number;
    clips: number;
    channels: Array<{ _id: string; clips: number }>;
  }>;
  maxClips: number;
};

export type ExplorerSocialBrand = {
  _id: string;
  name: string;
  s3Key?: string;
  clips: number;
  channels: number;
  views: number;
  viewsPerClip: number | null;
  interactions: number;
};

export type ExplorerSocialBrandClip = {
  _id: string;
  channelId: string | null;
  channelName: string;
  channelImageS3Key?: string;
  platform: ExplorerSocialPlatform;
  publishedAt: string | null;
  title: string;
  views: number;
  interactions: number;
  detection?: string;
  placementType?: "tagged" | "placement" | "overlay";
  url?: string;
};

export type ExplorerSocialPost = {
  _id: string;
  platform: ExplorerSocialPlatform;
  publishedAt: string | null;
  title: string;
  views: number;
  interactions: number;
  kind: "clip" | "post";
  owned: boolean;
  url?: string;
};

export type ExplorerSocialPagination = {
  page: number;
  limit: number;
  total: number;
  pages: number;
};

export type ExplorerFollowedProgramSource =
  | "ondemand"
  | "livestreams"
  | "youtube_shorts"
  | "tiktok"
  | "facebook"
  | "instagram";

export type ExplorerFollowedProgramCounts = {
  ondemand: number;
  livestreams: number;
  clips: number;
};

export type ExplorerFollowedProgramMetricTotals = {
  items: number;
  views: number;
  /** likes + comments (content buckets) */
  interactions: number;
  /** Sum of stream averageViewers (livestreams) */
  avgViewers: number;
};

export type ExplorerFollowedProgramPlatformMetrics =
  ExplorerFollowedProgramMetricTotals & {
    platform: string;
  };

export type ExplorerFollowedProgramBucketMetrics =
  ExplorerFollowedProgramMetricTotals & {
    byPlatform: ExplorerFollowedProgramPlatformMetrics[];
  };

export type ExplorerFollowedProgramFeedMetrics = {
  ondemand: ExplorerFollowedProgramBucketMetrics;
  livestreams: ExplorerFollowedProgramBucketMetrics;
  clips: ExplorerFollowedProgramBucketMetrics;
};

export type ExplorerFollowedProgramChannel = {
  _id: string;
  title: string;
  imageS3Key?: string | null;
  imageUrl?: string | null;
};

export type ExplorerFollowedProgramProgram = {
  _id: string;
  name: string;
  podcastId?: string;
};

export type ExplorerFollowedProgram = {
  _id: string;
  name: string;
  channelIds: string[];
  podscopeProgramIds: string[];
  keywords: string[];
  hashtags: string[];
  sources: ExplorerFollowedProgramSource[];
  createdBy?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  weekCounts?: ExplorerFollowedProgramCounts;
  channels?: ExplorerFollowedProgramChannel[];
  programs?: ExplorerFollowedProgramProgram[];
};

export type ExplorerFollowedProgramInput = {
  channelIds: string[];
  podscopeProgramIds?: string[];
  keywords?: string[];
  hashtags?: string[];
  sources?: ExplorerFollowedProgramSource[];
};

export type ExplorerFollowedProgramFeedItem = {
  _id: string;
  bucket: "ondemand" | "livestreams" | "clips";
  platform?: string;
  mediaType?: string | null;
  contentKind?: "reel" | "post" | null;
  title: string;
  publishedAt: string | null;
  thumbnail?: string | null;
  views?: number | null;
  interactions?: number | null;
  avgViewers?: number | null;
  channelId?: string;
  channelName?: string;
  programId?: string | null;
  programName?: string | null;
  livestreamChannelId?: string | null;
  sourceId?: string | null;
  url?: string | null;
};

export type ExplorerFollowedProgramPreview = {
  weekCounts: ExplorerFollowedProgramCounts;
  items: ExplorerFollowedProgramFeedItem[];
  window: { start: string; end: string; key: string };
};

export type ExplorerFollowedProgramFeedResponse = {
  program: ExplorerFollowedProgram;
  counts: ExplorerFollowedProgramCounts;
  weekCounts: ExplorerFollowedProgramCounts;
  metrics: ExplorerFollowedProgramFeedMetrics;
  items: {
    ondemand: ExplorerFollowedProgramFeedItem[];
    livestreams: ExplorerFollowedProgramFeedItem[];
    clips: ExplorerFollowedProgramFeedItem[];
  };
  window: { start: string; end: string; key: string };
};

export type ContentTaxonomyChunkRow = {
  chunkIndex: number;
  startTime: number;
  endTime: number;
  text: string;
  action: "accept" | "reexamine" | "discard";
  rawProposals?: Array<{
    slug: string;
    display: string;
    criterion: string;
    confidence: number;
    subtopic?: string;
    subtopicDisplay?: string;
  }>;
  resolved?: Array<{
    originalSlug: string;
    slug: string;
    display: string;
    criterion: string;
    confidence: number;
    outcome: "merged" | "review" | "distinct";
    similarity?: number;
    matchSlug?: string;
    subtopic?: string;
    subtopicDisplay?: string;
  }>;
  scriptVersion: string;
  createdAt?: string;
};

export type ContentTaxonomyChunksResponse = {
  content: {
    _id: string;
    title: string;
    url?: string;
    source?: string;
    sourceId?: string;
    duration?: number;
    podcastId?: string;
    companyId?: string;
    mediaType?: string;
    taxonomyEnrichedAt?: string;
  };
  chunks: ContentTaxonomyChunkRow[];
  episodeTopics?: Array<{
    slug: string;
    display: string;
    mentions: number;
    avgConfidence: number;
  }>;
  episodeSubtopics?: Array<{
    slug: string;
    display: string;
    mentions: number;
    parentTopicSlug: string;
  }>;
};

export type PrivateContentPeopleResponse = {
  contentId: string;
  peopleEnrichedAt?: string;
  people: Array<{
    personId: string;
    canonicalName: string | null;
    source: string;
    role: string;
    confidence: number;
    extractedRaw: string;
  }>;
};

export type PodcastTaxonomySubtopicRow = {
  slug: string;
  display: string;
  mentions: number;
  episodeCount: number;
  parentTopicSlug: string;
  weightedMentions: number;
  mentionsPer1kViews: number | null;
  viewShare: number | null;
  viewsDriven: number;
  interactionsDriven: number;
};

export type PodcastTaxonomyTopicRow = {
  slug: string;
  display: string;
  mentions: number;
  episodeCount: number;
  subtopics: PodcastTaxonomySubtopicRow[];
  weightedMentions: number;
  mentionsPer1kViews: number | null;
  viewShare: number | null;
  viewsDriven: number;
  interactionsDriven: number;
  topEpisodes: Array<{
    contentId: string;
    title: string;
    views: number;
    mentions: number;
  }>;
};

export type MiContenidoMediaMode = "videos" | "clips";

export type MiContenidoPersonDriverRow = {
  personId: string;
  canonicalName: string;
  role: "host" | "guest";
  clipCount: number;
  viewsDriven: number;
  avgViews: number;
  interactionsDriven: number;
  avgInteractions: number;
};

export type MiContenidoPeopleDriversResponse = {
  people: MiContenidoPersonDriverRow[];
};

export type PodcastTaxonomyScopeMetrics = {
  episodeCount: number;
  episodesWithTaxonomy: number;
  totalViews: number;
  totalViewsDay7: number;
  totalInteractions: number;
  dateRange: { from: string; to: string } | null;
};

export type PodcastTaxonomySummaryResponse = {
  podcast: { _id: string; title: string };
  scope: {
    podcastId: string;
    programId: string | null;
    week: string | null;
  };
  metrics: PodcastTaxonomyScopeMetrics;
  topics: PodcastTaxonomyTopicRow[];
};

export type PodcastTaxonomyProgramRow = {
  programId: string | null;
  programName: string;
  metrics: PodcastTaxonomyScopeMetrics;
  topics: PodcastTaxonomyTopicRow[];
};

export type PodcastTaxonomyByProgramResponse = {
  podcast: { _id: string; title: string };
  scope: { podcastId: string; week: string | null };
  programs: PodcastTaxonomyProgramRow[];
};

export type PodcastTaxonomySubtopicChunkRow = {
  contentId: string;
  contentTitle: string;
  url?: string;
  source?: string;
  platformPostId?: string;
  views: number;
  interactions: number;
  liveViewersAtChunk?: number | null;
  collaborators?: string[];
  taggedAccounts?: string[];
  chunkIndex: number;
  startTime: number;
  endTime: number;
  text: string;
};

export type PodcastTaxonomySubtopicChunksResponse = {
  topicSlug: string;
  subtopicSlug: string;
  chunks: PodcastTaxonomySubtopicChunkRow[];
};

export type PodcastTaxonomyScopeContentTopicTag = {
  topicSlug: string;
  topicDisplay: string;
  subtopicSlug?: string;
  subtopicDisplay?: string;
};

export type PodcastTaxonomyScopeContentRow = {
  contentId: string;
  contentTitle: string;
  url?: string;
  source?: string;
  views: number;
  interactions: number;
  topics: PodcastTaxonomyScopeContentTopicTag[];
  collaborators?: string[];
  taggedAccounts?: string[];
};

export type PodcastTaxonomyScopeContentResponse = {
  content: PodcastTaxonomyScopeContentRow[];
};

export type AdminBrandTranscriptFingerprint = {
  mentionId: string;
  text: string;
  createdAt?: string | null;
  manual?: boolean;
};

export type AdminBrand = {
  _id: string;
  name: string;
  slug?: string | null;
  aliases: string[];
  antiAliases: string[];
  overlapSuppressAliases: string[];
  transcriptFingerprints?: AdminBrandTranscriptFingerprint[];
  description?: string | null;
  context?: string | null;
  category?: string | null;
  subcategory?: string | null;
  companyName?: string | null;
  website?: string | null;
  country?: string | null;
  isActive: boolean;
  logoUrl?: string | null;
  s3Key?: string | null;
  imageUpdatedAt?: string | null;
  primaryColor?: string | null;
  socialAccounts: string[];
  brandType?: string | null;
  industryTags: string[];
  canSponsor: boolean;
  sponsorshipNotes?: string | null;
  notes?: string | null;
  keywords: string[];
  isVerified: boolean;
  totalMentions: number;
  paidMentions: number;
  lastPaidMentionAt?: string | null;
  source?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  lastMentionAt?: string | null;
};

export type AdminBrandPatch = {
  name?: string;
  slug?: string | null;
  aliases?: string[];
  antiAliases?: string[];
  overlapSuppressAliases?: string[];
  description?: string | null;
  context?: string | null;
  category?: string | null;
  subcategory?: string | null;
  companyName?: string | null;
  website?: string | null;
  country?: string | null;
  isActive?: boolean;
  primaryColor?: string | null;
  socialAccounts?: string[];
  brandType?: string | null;
  industryTags?: string[];
  canSponsor?: boolean;
  sponsorshipNotes?: string | null;
  notes?: string | null;
  keywords?: string[];
  isVerified?: boolean;
};

export type AdminProgramKind = "podcast" | "livestream";

export type AdminProgram = {
  _id: string;
  kind?: AdminProgramKind;
  name: string;
  slug?: string | null;
  description?: string | null;
  aliases: string[];
  keywords: string[];
  imageS3Key?: string | null;
  imageUpdatedAt?: string | null;
  isActive: boolean;
  hidden: boolean;
  podcastId: string;
  podcast?: {
    _id: string;
    title: string;
    imageS3Key?: string | null;
    imageUrl?: string | null;
  } | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

export type AdminProgramAssignment = {
  _id: string;
  title: string;
  url: string | null;
  publishedAt: string | null;
  durationSeconds: number | null;
  /** Content source (youtube, tiktok, …) or stream status (completed, live, …). */
  label: string | null;
};

export type AdminProgramPatch = {
  name?: string;
  slug?: string;
  description?: string | null;
  aliases?: string[];
  keywords?: string[];
  isActive?: boolean;
  hidden?: boolean;
};

export type AdminChannelRow = {
  _id: string;
  title: string;
  slug?: string;
  description?: string;
  imageS3Key?: string;
  imageUrl?: string;
  category: "politics" | "other";
  type: "tv" | "noticias" | "radio" | "digital";
  youtubeChannelId?: string | null;
  singleProgramPodcast?: boolean;
  defaultProgramId?: string | null;
  defaultProgramName?: string | null;
};

export type MediaChannelRow = {
  _id: string;
  name: string;
  slug: string;
  description?: string;
  type: "tv" | "noticias" | "radio" | "digital";
  category: "politics" | "other";
  imageS3Key?: string;
  youtubeChannelId?: string | null;
  youtubeUsername?: string | null;
  instagramUsername?: string | null;
  facebookUsername?: string | null;
  tiktokUsername?: string | null;
  spotifyId?: string | null;
  hasLivestream: boolean;
  trackLivestream: boolean;
  legacyPodcastId?: string | null;
  legacyLivestreamChannelId?: string | null;
  podcastProgramCount: number;
  livestreamProgramCount: number;
  channelProgramCount: number;
  source: "both" | "podcast" | "livestream_only";
  accountCheck?: MediaChannelAccountCheck | null;
  /** Instagram, Facebook, TikTok and Spotify are each set or removed by an admin. */
  accountsComplete?: boolean;
  /** Platforms with an account whose profile has never been synced. */
  unsyncedPlatforms?: Array<"youtube" | "instagram" | "facebook" | "tiktok" | "spotify">;
  updatedAt?: string | null;
};

/** Last account discovery run; `review` only lists platforms that are still empty. */
export type MediaChannelAccountCheck = {
  checkedAt: string;
  error: string | null;
  filled: Array<"instagram" | "facebook" | "tiktok" | "spotify">;
  review: Array<{
    platform: "instagram" | "facebook" | "tiktok" | "spotify";
    status: "ambiguous" | "taken";
    value: string | null;
  }>;
};

export type MediaChannelAccountDiscoveryJob = {
  running: boolean;
  scope: "unchecked" | "all";
  total: number;
  processed: number;
  filled: number;
  needsReview: number;
  errors: number;
  currentChannel?: string;
  startedAt?: string;
  finishedAt?: string;
  stopRequested: boolean;
};

export type MediaChannelAccountDiscoveryStats = {
  totalChannels: number;
  youtubeChannels: number;
  checked: number;
  errors: number;
  needsReview: number;
  filledAccounts: number;
  platforms: Record<"youtube" | "instagram" | "facebook" | "tiktok" | "spotify", number>;
};

export type MediaChannelPodcastProgram = {
  _id: string;
  name: string;
  slug: string;
  description: string;
  aliases: string[];
  keywords: string[];
  hashtags: string[];
  socialAccounts: string[];
  youtubePlaylistIds: string[];
  imageS3Key?: string;
  isActive: boolean;
  hidden: boolean;
};

export type MediaChannelLivestreamProgram = {
  _id: string;
  name: string;
  description: string;
  imageS3Key?: string;
  keywords: string[];
  hashtags: string[];
  socialAccounts: string[];
  scheduleCount: number;
  activeScheduleCount: number;
  isActive: boolean;
  hidden: boolean;
};

export type MediaChannelProgram = {
  _id: string;
  channelId: string;
  name: string;
  slug: string;
  description: string;
  aliases: string[];
  keywords: string[];
  hashtags: string[];
  socialAccounts: string[];
  youtubePlaylistIds: string[];
  imageS3Key?: string;
  scheduleCount: number;
  activeScheduleCount: number;
  kinds: Array<"content" | "livestream">;
  isActive: boolean;
  hidden: boolean;
  legacyPodcastProgramId: string | null;
  legacyLivestreamProgramId: string | null;
};

export type MediaChannelProgramKind = MediaChannelProgram["kinds"][number];

export type MediaChannelProgramRow = MediaChannelProgram & {
  podcastProgramName: string | null;
  livestreamProgramName: string | null;
  cues: string[];
  isDigital: boolean;
  contentCount: number;
};

type LinkedChannelProgram = { _id: string; name: string } | null;

export type MediaChannelLinkablePodcastProgram = {
  _id: string;
  name: string;
  slug: string;
  isActive: boolean;
  hidden: boolean;
  contentCount: number;
  channelProgram: LinkedChannelProgram;
};

export type MediaChannelLinkableLivestreamProgram = {
  _id: string;
  name: string;
  isActive: boolean;
  hidden: boolean;
  scheduleCount: number;
  streamCount: number;
  /** Streams of this program whose own livestream channel is a different channel. */
  foreignStreamChannels: Array<{ channelId: string; channelName: string; count: number }>;
  channelProgram: LinkedChannelProgram;
};

export type MediaChannelProgramSuggestion = {
  value: string;
  /** Share of the program's recent episodes / streams carrying it (1 = from the program itself). */
  share: number;
  source: "program" | "title" | "description" | "tags";
};

export type MediaChannelProgramInput = {
  name: string;
  slug?: string;
  aliases?: string[];
  keywords?: string[];
  cues?: string[];
  hashtags?: string[];
  youtubePlaylistIds?: string[];
  isActive?: boolean;
  hidden?: boolean;
  isDigital?: boolean;
  /** podcast_programs id to link; null unlinks. */
  podcastProgramId?: string | null;
  /** livestream_programs id to link; null unlinks. */
  livestreamProgramId?: string | null;
};

export type MediaChannelPlatforms = {
  youtube?: { channelId?: string; username?: string } & MediaChannelSyncedFields;
  instagram?: { username?: string } & MediaChannelSyncedFields;
  facebook?: { username?: string } & MediaChannelSyncedFields;
  tiktok?: { username?: string } & MediaChannelSyncedFields;
  spotify?: { id?: string } & MediaChannelSyncedFields;
};

export type MediaChannelMainImageSyncResult = {
  status: "updated" | "unchanged" | "error";
  source?: "podcast" | "youtube";
  imageS3Key?: string;
  error?: string;
};

export type MediaChannelSyncedFields = {
  name?: string;
  imageS3Key?: string;
  followers?: number;
  lastSyncedAt?: string;
};

export type MediaChannelImageSyncResult = {
  platform: "youtube" | "instagram" | "facebook" | "tiktok" | "spotify";
  status: "updated" | "skipped" | "error";
  imageS3Key?: string;
  name?: string;
  username?: string;
  followers?: number;
  lastSyncedAt?: string;
  nextSyncAt?: string;
  error?: string;
};

export type MediaChannelAccountDiscoveryResult = {
  platform: "instagram" | "facebook" | "tiktok" | "spotify";
  status: "filled" | "found" | "existing" | "not_found" | "ambiguous" | "taken" | "unverified" | "removed";
  value?: string;
  existing?: string;
  candidates?: string[];
  takenBy?: string;
};

export type MediaChannelAccountSuggestion = {
  value: string;
  title?: string;
  link: string;
  hits: number;
  exists: boolean;
  takenBy?: { id: string; name?: string };
};

export type MediaChannelAccountSearchResult = {
  platform: "instagram" | "facebook" | "tiktok" | "spotify";
  query: string;
  suggestions: MediaChannelAccountSuggestion[];
};

export type MediaChannelDetail = {
  _id: string;
  name: string;
  slug: string;
  description?: string;
  type?: string;
  category?: string;
  imageS3Key?: string;
  config?: { trackLivestream?: boolean };
  platforms?: MediaChannelPlatforms;
  /** Platforms an admin deleted; sync and account discovery leave them empty. */
  removedPlatforms?: Array<"instagram" | "facebook" | "tiktok" | "spotify">;
  legacyPodcastId?: string;
  legacyLivestreamChannelId?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type AdminChannelProgramRow = {
  _id: string;
  kind?: AdminProgramKind;
  name: string;
  slug?: string;
  description?: string;
  aliases?: string[];
  keywords?: string[];
  cues?: string[];
  hashtags?: string[];
  socialAccounts?: string[];
  youtubePlaylistIds?: string[];
  isActive: boolean;
  hidden?: boolean;
  isDigital?: boolean;
  contentCount: number;
};

export type AdminChannelProgramCatalogRow = {
  _id: string;
  name: string;
  isActive: boolean;
  hidden?: boolean;
  isDigital?: boolean;
};

export type AdminChannelProgramCreatePayload = {
  name: string;
  slug?: string;
  description?: string;
  aliases?: string[];
  keywords?: string[];
  cues?: string[];
  hashtags?: string[];
  socialAccounts?: string[];
  youtubePlaylistIds?: string[];
  isActive?: boolean;
  hidden?: boolean;
  isDigital?: boolean;
};

export type AdminChannelProgramPatchPayload = {
  name?: string;
  slug?: string;
  description?: string;
  aliases?: string[];
  keywords?: string[];
  cues?: string[];
  hashtags?: string[];
  socialAccounts?: string[];
  youtubePlaylistIds?: string[];
  isActive?: boolean;
  hidden?: boolean;
  isDigital?: boolean;
};

export type AdminChannelProgramContentRow = {
  _id: string;
  title: string;
  description?: string;
  url?: string | null;
  source?: string;
  sourceId?: string | null;
  publishedAt?: string;
  mediaType?: string;
  /** Resolved from channel_playlists catalog via content.channel_playlist_ids */
  playlistNames?: string[];
  /** Matching livestream_streams._id when content.sourceId === stream.videoId */
  livestreamStreamId?: string | null;
  livestreamTitle?: string | null;
  livestreamProgramId?: string | null;
  livestreamProgramName?: string | null;
};

export type AdminChannelPlaylistRow = {
  _id: string;
  playlistId: string;
  title: string;
  description?: string;
  itemCount: number;
  contentCount: number;
  programId?: string | null;
  programName?: string | null;
  lastSyncedAt?: string;
};

export type AdminChannelPlaylistAssignPayload = {
  playlistId: string;
  toProgramId?: string;
  createProgram?: boolean;
  programName?: string;
  programSlug?: string;
};

export type AdminChannelPatchPayload = {
  title?: string;
  slug?: string;
  description?: string | null;
  category?: "politics" | "other";
  type?: "tv" | "noticias" | "radio" | "digital";
  singleProgramPodcast?: boolean;
  defaultProgramId?: string | null;
};

export type AdminChannelCreatePayload = {
  title: string;
  slug?: string;
  description?: string;
  category?: "politics" | "other";
  type?: "tv" | "noticias" | "radio" | "digital";
  /** Required — used to fetch the channel's main YouTube icon into S3. */
  youtubeChannelId: string;
};

function appendPodcastTaxonomyParams(
  params: URLSearchParams,
  filters?: PodcastTaxonomyFilters,
  opts?: { channelTypeQueryKey?: "type" | "channelType" },
) {
  if (filters?.category && filters.category !== "all") {
    params.append("category", filters.category);
  }
  const channelType = taxonomyChannelTypeParam(filters?.channelType);
  if (channelType) {
    params.append(opts?.channelTypeQueryKey ?? "type", channelType);
  }
}

function taxonomyChannelTypeParam(
  channelType: string | string[] | undefined,
): string | undefined {
  if (channelType == null) return undefined;
  const parts = (Array.isArray(channelType) ? channelType : [channelType])
    .flatMap((value) => value.split(","))
    .map((value) => value.trim())
    .filter((value) => value && value !== "all");
  return parts.length ? parts.join(",") : undefined;
}

function isAuthPublicPath(pathname: string): boolean {
  return (
    pathname === "/auth" ||
    pathname.startsWith("/auth/invitacion") ||
    pathname.startsWith("/auth/olvido-contrasena") ||
    pathname.startsWith("/auth/restablecer-contrasena") ||
    isPodcastInvitePath(pathname)
  );
}

// Add request interceptor to include JWT token for authenticated routes
api.interceptors.request.use(
  (config) => {
    // Still on /auth or /auth/invitacion after sign-in / invite accept: we must send the token
    // so GET /api/companies/me and other protected calls succeed (otherwise company products stay empty).
    const currentPath = window.location.pathname;
    const isPublicSponsorLink =
      currentPath.includes("/colecciones/") && currentPath.includes("/brands/");
    const isPublicTonyBetExplorer = currentPath.startsWith("/tonybet");
    const isPublicBrandReport = currentPath.startsWith("/reportes/marcas/");
    const isPapiRickyFeed = currentPath === "/papi-ricky";

    let token: string | null = null;
    try {
      token = localStorage.getItem("authToken");
    } catch {
      token = null;
    }
    if (
      token &&
      !isPublicSponsorLink &&
      !isPublicTonyBetExplorer &&
      !isPublicBrandReport &&
      !isPapiRickyFeed &&
      !isPodcastInvitePath(currentPath)
    ) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Add response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Check if we're on a public page - don't redirect to auth
      const currentPath = window.location.pathname;
      const isPublicSponsorLink =
        currentPath.includes("/colecciones/") &&
        currentPath.includes("/brands/");
      const isLajunta = currentPath.startsWith("/lajunta");
      const isPublicTonyBetExplorer = currentPath.startsWith("/tonybet");
      const isPublicBrandReport = currentPath.startsWith("/reportes/marcas/");
      const isPapiRickyFeed = currentPath === "/papi-ricky";
      const skipAuthRedirect = isAuthPublicPath(currentPath);

      if (
        !isPublicSponsorLink &&
        !isLajunta &&
        !isPublicTonyBetExplorer &&
        !isPublicBrandReport &&
        !isPapiRickyFeed &&
        !skipAuthRedirect
      ) {
        // Clear invalid session and persisted app state
        clearAppSessionState(queryClient);
        try {
          localStorage.removeItem("authToken");
          localStorage.removeItem("user");
        } catch {
          // Safari private / in-app browsers can block storage.
        }
        window.location.href = ROUTES_NAMES.AUTH;
      }
    }
    return Promise.reject(error);
  },
);

// Types
export type FavoriteEntityType =
  | "program"
  | "livestream_channel"
  | "sponsor"
  | "collection"
  | "company"
  | "podcast";

export interface UserFavorite {
  _id: string;
  userId: string;
  entityType: FavoriteEntityType;
  entityId: string;
  createdAt: string;
}

export type CampaignPlatformKey =
  | "youtube_videos"
  | "youtube_shorts"
  | "instagram"
  | "facebook"
  | "tiktok";

export interface CampaignPlatformSummary {
  key: CampaignPlatformKey;
  label: string;
  views: number;
  avgViews: number;
  interactions: number;
  avgInteractions: number;
  contentCount: number;
}

export interface CompanyProfileBrand {
  brandKey: string;
  brandId: string | null;
  name: string;
  logo: string | null;
  presence_count: number;
  mention_count: number;
  platform_counts: {
    youtube: number;
    youtube_shorts: number;
    instagram: number;
    facebook: number;
    tiktok: number;
  };
  recentAppearances: Array<{
    contentId: string;
    title: string;
    publishedAt: string | null;
    source: "youtube" | "instagram" | "facebook" | "tiktok" | null;
    viaPresence: boolean;
    viaMention: boolean;
  }>;
}

export interface CompanyProfileBrandsResponse {
  brands: CompanyProfileBrand[];
}

export type CampaignComparePlatform =
  | "youtube_videos"
  | "youtube_shorts"
  | "instagram"
  | "facebook"
  | "tiktok";

export interface CampaignComparePlatformMetrics {
  contentCount: number;
  totalViews: number;
  avgViews: number;
  totalInteractions: number;
  avgInteractions: number;
}

export interface CampaignCompareRow {
  key: string;
  label: string;
  platforms: Record<CampaignComparePlatform, CampaignComparePlatformMetrics>;
  totals: CampaignComparePlatformMetrics;
}

export type CampaignCompareDimension =
  | "program"
  | "keyword"
  | "brand_mentioned"
  | "brand_placement"
  | "brand_tagged";

export interface CampaignCompareResponse {
  startDate: string;
  endDate: string;
  dimension: CampaignCompareDimension;
  platforms: CampaignComparePlatform[];
  rows: CampaignCompareRow[];
  brandRows: CampaignCompareRow[] | null;
}

export interface User {
  _id: string;
  name: string;
  email: string;
  company?: string;
  role: "user" | "admin";
  isActive: boolean;
  lastLogin?: string;
  createdAt: string;
  updatedAt: string;
  companyId?: string;
  /** Direct podcast assignments when user has no company membership (admin tooling). */
  podcastIds?: string[];
  companyRole?: "admin" | "member";
  userProducts?: string[];
  /** Explorador CSV export. Missing/undefined = on (legacy); new users default false. */
  explorerCsvExportEnabled?: boolean;
  /**
   * Per-user filter overrides. Null/undefined = use company config.
   * For explorerSponsorCategoryFilter, "" means force "Todas".
   */
  dateFilterMinStartDate?: string | null;
  dateFilterMaxEndDate?: string | null;
  miContenidoDateFilterMinStartDate?: string | null;
  miContenidoDateFilterMaxEndDate?: string | null;
  explorerSponsorCategoryFilter?: string | null;
  /** Demo account with a prefilled login link. */
  isDemo?: boolean;
  demoInviteSentAt?: string | null;
}

export type ExplorerBrandInvestmentPoints = {
  credits: number;
  content: number;
  banner: number;
  placement: number;
};

export type ExplorerBrandInvestment = {
  enabled: boolean;
  points?: ExplorerBrandInvestmentPoints;
  /** CLP value of one point; when set, Explorer shows investment in CLP. */
  clpPerPoint?: number | null;
};

export type CompanyChatKind = "medio" | "medio_tv" | "agencia" | "marca";

export interface Company {
  _id: string;
  name: string;
  slug: string;
  ownerUserId?: string;
  imageUrl?: string;
  imageS3Key?: string;
  products?: string[];
  enablePerUserProductPermissions?: boolean;
  dateFilterMinStartDate?: string | null;
  dateFilterMaxEndDate?: string | null;
  miContenidoDateFilterMinStartDate?: string | null;
  miContenidoDateFilterMaxEndDate?: string | null;
  trackerBrandId?: string | null;
  /** When non-empty, Mi Contenido only shows these brands for company users. */
  miContenidoBrandIds?: string[];
  explorerSponsorCategoryFilter?: string | null;
  /** Per-company Explorer brand investment estimates (points per mention type). */
  explorerBrandInvestment?: ExplorerBrandInvestment | null;
  comparadorPodcastIds?: string[];
  sidebarGuidesUrl?: string | null;
  sidebarGuidesLabel?: string | null;
  /** Chat audience. Notes stay on the admin chat-profile endpoint. */
  chatKind?: CompanyChatKind | null;
  createdAt: string;
  updatedAt: string;
}

export type CompanyChatSearchPreference = "auto" | "mi_contenido_first";

export interface CompanyChatProfile {
  kind: CompanyChatKind | null;
  searchPreference: CompanyChatSearchPreference;
  notes: string | null;
}

export type SocialPlatform = "meta" | "tiktok" | "youtube" | "spotify";

export interface SocialAccount {
  _id: string;
  companyId: string;
  podcastId: string;
  platform: SocialPlatform;
  accountId: string;
  username?: string;
  profileUrl?: string;
  metaPageId?: string;
  grantedPermissions?: string[];
  tokenExpiresAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AdminSocialAccount {
  _id: string;
  companyId: string;
  companyName: string;
  podcastId?: string;
  podcastTitle?: string;
  platform: SocialPlatform;
  accountId: string;
  username?: string;
  secretId?: string;
  tokenExpiresAt?: string;
  createdAt?: string;
}

export type PublicSocialPlatform = "instagram" | "tiktok";
export type PublicSocialAccountType = "podcast" | "program";

export interface PublicSocialAccount {
  _id: string;
  platform: PublicSocialPlatform;
  username: string;
  displayName?: string;
  accountType: PublicSocialAccountType;
  podcastId?: string;
  programId?: string;
  isActive: boolean;
  lastFetchedAt?: string;
  lastFetchError?: string;
  followerCount?: number;
  followingCount?: number;
  profilePicUrl?: string;
  profilePicS3Key?: string;
  bio?: string;
  externalUrl?: string;
  isVerified?: boolean;
  isBusinessAccount?: boolean;
  category?: string;
  contentCount: number;
  totalViews?: number;
  totalLikes?: number;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  // Populated fields
  podcast?: {
    _id: string;
    title: string;
    imageUrl?: string;
    imageS3Key?: string;
    slug?: string;
  };
  program?: {
    _id: string;
    name: string;
    imageS3Key?: string;
  };
}

export interface PublicSocialContent {
  _id: string;
  publicSocialAccountId: string;
  platform: PublicSocialPlatform;
  platformPostId: string;
  url: string;
  title: string;
  publishedAt: string;
  mediaType: "video" | "reel" | "image";
  duration?: number;
  views?: number;
  likes?: number;
  audioS3Key?: string;
  transcriptS3Key?: string;
  analyzedAt?: string;
  detectedBrands?: Array<{
    brandName: string;
    confidence: number;
    mentionType: "paid" | "organic" | "neutral";
    segmentStart: number;
    segmentEnd: number;
    context: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

export interface CompanyPodcastProgramRow {
  _id: string;
  name: string;
  slug: string;
  isActive?: boolean;
  hidden?: boolean;
  enabled: boolean;
  livestreamDataEnabled: boolean;
  demographicDataEnabled: boolean;
  imageS3Key?: string;
  updatedAt?: string;
  imageUpdatedAt?: string | null;
}

export interface Podcast {
  _id: string;
  title: string;
  slug?: string;
  description?: string;
  author?: string;
  imageUrl?: string;
  imageS3Key?: string;
  category?: "politics" | "other";
  type?: "tv" | "noticias" | "radio" | "digital";
  language?: string;
  /** Unified `channels` row when the podcast was migrated. */
  channelId?: string;
  socialAccounts?: {
    instagram?: string;
    tiktok?: string;
    facebook?: string;
  };
  lastUpdated?: string;
  episodeCount?: number;
  lastFetchTriggeredAt?: string;
  createdAt?: string;
  livestreamDataEnabled?: boolean;
  /** Public mediakit switch from company podcast list API */
  mediaKitEnabled?: boolean;
  /** Media kit days-after-published window from company podcast list API */
  mediaKitMetricsWindow?: MediaKitMetricsWindow;
  /** When listing company podcasts with includePrograms */
  programs?: CompanyPodcastProgramRow[];
  monthlyEpisodes?: {
    jan?: number;
    feb?: number;
    mar?: number;
    apr?: number;
    may?: number;
    jun?: number;
  };
  youtubeData?: {
    channelId?: string;
    snippet?: {
      title?: string;
      description?: string;
      customUrl?: string;
      publishedAt?: string;
      thumbnails?: {
        default?: { url: string; width?: number; height?: number };
        medium?: { url: string; width?: number; height?: number };
        high?: { url: string; width?: number; height?: number };
      };
      defaultLanguage?: string;
      country?: string;
    };
    contentDetails?: {
      relatedPlaylists?: {
        uploads?: string;
        likes?: string;
      };
    };
    statistics?: {
      viewCount?: number;
      subscriberCount?: number;
      videoCount?: number;
      hiddenSubscriberCount?: boolean;
    };
  };
}

export interface Episode {
  _id: string;
  title: string;
  description?: string;
  podcastId: string;
  episodeNumber?: number;
  season?: number;
  duration?: number;
  publishedAt?: string;
  sponsorCount?: number;
  hasContentAnalysis?: boolean;
}

export interface PodcastEpisodesResponse {
  episodes: Episode[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface Sponsor {
  _id: string;
  name: string;
  website?: string;
  logo?: string;
  categories?: string[];
  description?: string;
}

// Studio types for grouped content
export interface StudioEpisode {
  _id: string;
  podcastId: string;
  title: string;
  url: string;
  publishedAt: string;
  views: number;
  likes: number;
  comments: number;
  type?: string;
  durationInSeconds?: number;
  mentionCount?: number;
  sponsors: Array<{
    _id: string;
    name: string;
  }>;
  thumbnail?: string;
  imageUrl?: string;
  source: string;
  contentAnalysis?: {
    content_type:
      | "educational"
      | "entertainment"
      | "promotional"
      | "news"
      | "lifestyle"
      | "tutorial"
      | "review";
    tone: "formal" | "casual" | "humorous" | "serious" | "inspirational";
    target_audience: string;
    key_topics: string[];
    main_message: string;
    call_to_action: string | null;
    content_context: string;
  };
  analyzedAt?: string;
}

// Standardized insights interface for all platforms
export interface SocialMediaInsights {
  reach?: number;
  views?: number;
  likes?: number;
  comments?: number;
  shares?: number;
  saves?: number;
  interactions?: number;
  collectedAt?: Date;
}

export interface StudioPost {
  _id: string;
  podcastId: string;
  title: string;
  url: string;
  publishedAt: string;
  source: string;
  type?: "video" | "short";
  mentionCount?: number;
  instagramData?: {
    postId?: string;
    postType?: string;
    mediaType?: string;
    caption?: string;
    mediaUrl?: string;
    audioUrl?: string;
    frameUrls?: string[];
    coverUrl?: string;
    commentsCount?: number;
    likesCount?: number;
    videoViewCount?: number;
    videoPlayCount?: number;
    videoDuration?: number;
    displayUrl?: string;
    insights?: SocialMediaInsights;
  };
  tiktokData?: {
    postId?: string;
    text?: string;
    coverUrl?: string;
    originalCoverUrl?: string;
    webVideoUrl?: string;
    playCount?: number;
    diggCount?: number;
    commentCount?: number;
    shareCount?: number;
    insights?: SocialMediaInsights;
  };
  facebookData?: {
    postId?: string;
    text?: string;
    coverUrl?: string;
    webVideoUrl?: string;
    playCount?: number;
    likesCount?: number;
    commentsCount?: number;
  };
  youtubeData?: {
    videoId?: string;
    title?: string;
    description?: string;
    thumbnailUrl?: string;
    channelId?: string;
    channelTitle?: string;
    publishedAt?: Date;
    duration?: string;
    viewCount?: number;
    likeCount?: number;
    commentCount?: number;
    insights?: SocialMediaInsights;
  };
  contentAnalysis?: {
    content_type:
      | "educational"
      | "entertainment"
      | "promotional"
      | "news"
      | "lifestyle"
      | "tutorial"
      | "review";
    tone: "formal" | "casual" | "humorous" | "serious" | "inspirational";
    target_audience: string;
    key_topics: string[];
    main_message: string;
    call_to_action: string | null;
    content_context: string;
  };
  analyzedAt?: string;
}

export interface PrivateContent {
  _id: string;
  socialAccountId: string;
  companyId: string;
  podcastId: string;
  /** Linked `PodcastProgram` for Mi Contenido / collections. */
  programId?: string;
  /**
   * When set by GET /api/collections/content/:id (authenticated), reflects
   * Company.companyProgramSettings Demográficos for this episode's program (`isDemographicEnabledForProgram`).
   */
  programDemographicDataEnabled?: boolean;

  // Basic content info
  title: string;
  description?: string;
  summary?: string;
  url?: string;
  publishedAt: string;

  // Content classification
  source: "rss" | "youtube" | "instagram" | "tiktok" | "facebook" | "spotify";
  mediaType: "video" | "short" | "audio" | "image" | "carousel" | "reel";

  // Platform-specific unique identifier (Instagram post ID, YouTube video ID, TikTok video ID, etc.)
  platformPostId: string;

  // Social media username (e.g., Instagram username, TikTok username)
  username?: string;

  /** Enriched on public collection content for sponsor-link dialogs. */
  publisherAccount?: {
    name: string;
    imageUrl?: string;
  };

  // Common metrics (normalized across all sources)
  views?: number;
  likes?: number;
  comments?: number;
  shares?: number;
  saves?: number;
  reach?: number;

  // Common media
  thumbnail?: string;
  imageUrl?: string;
  duration?: number;

  // Asset availability and processing status
  audioS3Key?: string;
  transcriptS3Key?: string;
  imageS3Key?: string;

  // Analysis status
  sponsorAnalyzedAt?: string;
  contentAnalyzedAt?: string;
  transcribedAt?: string;

  // Livestream association (enriched when content matches a livestream by video ID)
  livestreamId?: string;
  livestreamTitle?: string;

  // Platform-specific insights (private analytics data)
  insights?: {
    // Common metrics
    views?: number;
    likes?: number;
    comments?: number;
    shares?: number;
    saves?: number;
    reach?: number;

    // Spotify-specific metrics
    starts?: number;
    streams?: number;
    uniqueListeners?: number;
    averageListenPct?: number;
    impressions?: number;
    followersGained?: number;
    uploadedAt?: Date;
    source?: string;

    // YouTube OAuth (Analytics + Reporting API) — flat on insights; period views use dailyMetrics / insights.views
    analyticsStartDate?: string;
    analyticsEndDate?: string;
    videoId?: string;
    collectedAt?: Date | string;
    demographics?: {
      gender: Array<{ type: string; confidence: number }>;
      ageRanges: Array<{ range: string; confidence: number }>;
    };
    thumbnailImpressions?: number | null;
    thumbnailImpressionsClickRate?: number | null;
    /** YouTube Analytics API engaged views for the analytics window. */
    engagedViews?: number | null;
    uniqueViewers?: number | null;

    /** YouTube OAuth audience retention curve (elapsedVideoTimeRatio 0–1, audienceWatchRatio 0–1). */
    audienceRetention?: Array<{
      elapsedVideoTimeRatio: number;
      audienceWatchRatio: number;
    }>;
    averageViewDuration?: number;
    averageViewPercentage?: number;

    [key: string]: any;
  };

  // Time-series metrics (daily snapshots); use latest for fresh views/interactions
  dailyMetrics?: Array<{
    collectedAt: string | Date;
    daysSincePublished: number;
    views: number;
    likes: number;
    comments: number;
  }>;

  // Analysis & processing
  contentAnalysis?: any;
  sponsorMentions?: any[];
}

export interface Brand {
  _id: string;
  name: string;
  aliases: string[];
  antiAliases?: string[];
  category: string;
  website: string | null;
  logoUrl?: string; // Legacy field - will be deprecated
  s3Key?: string; // New S3 key field
  isActive: boolean;
  brandType: string;
  canSponsor: boolean;
  isVerified: boolean;
  source: string;
  createdAt: string;
  updatedAt: string;
}

export interface BrandMention {
  _id: string;
  brandName: string;
  classification: string;
  commercialConfidence: number;
  confidence: number;
  segmentStart?: number;
  segmentEnd?: number;
  startTime: number;
  endTime: number;
  context: string;
  contentId: string;
  logoUrl?: string; // Legacy field - will be deprecated
  s3Key?: string; // New S3 key field
  aliases?: string[];
  brandId?: string;
  placementType?:
    | "content"
    | "opening_credits"
    | "closing_credits"
    | "mid_credits"
    | "physical_placement"
    | "banner"
    | "overlay";
  visualCue?:
    | "banner"
    | "curtain"
    | "logo_overlay"
    | "lateral_banner"
    | "sponsor_overlay"
    | null;
  evidenceType?: "audio" | "visual" | "audio+visual" | null;
  feedback?: {
    status: "confirmed" | "rejected" | "approved" | null;
    reason?: string;
    context?: string;
    reviewedBy?: string;
    reviewedAt?: string;
  } | null;
  review?: {
    original?: {
      brandName?: string;
      segmentStart?: number;
      segmentEnd?: number;
      mentionType?: string;
      placementType?: string;
      commercialConfidence?: number;
      visualCue?: string | null;
    };
    reviewedBy?: string;
    reviewedAt?: string;
    humanAdded?: boolean;
  } | null;
  aiClassification?: {
    model?: string;
    processedAt?: string;
  } | null;
  transcriptExcerpt?: Array<{ start: number; end: number; text: string }>;
}

export interface BlockFrameUrl {
  tsec: number;
  url?: string;
  fileName?: string;
  isRepresentative?: boolean;
}

export type MentionOrigin = 'ai_detected' | 'human_edited' | 'human_added';

export interface PodcastLearningExampleRow {
  _id: string;
  podcastId: string;
  contentId: string;
  mentionId: string;
  outcome: 'confirmed' | 'rejected';
  flow: string;
  failureMode: string;
  failureModeLabel?: string;
  flowLabel?: string;
  reasonPreset?: string;
  reasonText?: string;
  brandName: string;
  brandId?: string;
  segmentStart: number;
  segmentEnd: number;
  placementType?: string;
  visualCue?: string;
  evidence?: {
    mentionText?: string;
    context?: string;
    transcriptText?: string;
    frameS3Key?: string;
    frameS3Keys?: string[];
    creditWindowStart?: number;
    creditWindowEnd?: number;
  };
  frameUrl?: string;
  frameUrls?: string[];
  blockFrameCount?: number;
  mentionOrigin?: MentionOrigin;
  mentionOriginLabel?: string;
  correctedFields?: string[];
  dedupeKey: string;
  reviewedAt: string;
  includeInAlgorithm?: boolean;
  excludeFromLearning?: boolean;
  podcastTitle?: string | null;
  contentTitle?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface PodcastLearningsFilterPodcast {
  podcastId: string;
  title: string;
  exampleCount: number;
}

export type PodcastLearningEvidenceFilter =
  | 'transcript'
  | 'frame'
  | 'both'
  | 'none';

export interface PodcastLearningsStats {
  totalExamples: number;
  feedbackMentionCount: number;
  profileCount: number;
  withFrame: number;
  includedInAlgorithm: number;
  byOutcome: Record<string, number>;
  byFlow: Record<string, number>;
  byFailureMode: Record<string, number>;
  failureModeLabels: Record<string, string>;
  flowLabels: Record<string, string>;
}

export interface PodcastLearningProfileCategory {
  failureMode: string;
  failureModeLabel: string;
  rejectedCount: number;
  confirmedCount: number;
  lastReviewedAt?: string | null;
  topExampleIds: string[];
  promotedRule?: { type: string; value: string; approvedAt?: string } | null;
}

export interface PodcastLearningFeedbackGap {
  _id: string;
  podcastId: string;
  contentId: string;
  brandName: string;
  segmentStart: number;
  feedback?: { status?: string; reason?: string | null; reviewedAt?: string };
  visualCue?: string;
  placementType?: string;
  evidenceType?: string;
  updatedAt?: string;
}

export type PgmCueKind = 'curtain' | 'banner' | 'lateral_banner';
export type PgmFixturePool = 'positive' | 'negative';

export interface PgmFixtureStatus {
  cue: PgmCueKind;
  pool: PgmFixturePool;
  mentionIds: string[];
  sourceKeys: string[];
  caseIds: string[];
}

export interface CreatePgmFixtureFrameInput {
  learningExampleId: string;
  mentionId: string;
  contentId: string;
  tsec: number;
}

export interface CommitPgmFixtureFrameResult extends CreatePgmFixtureFrameInput {
  ok: boolean;
  sourceKey: string;
  reason?: string;
}

/** @deprecated Use CommitPgmFixtureFrameResult — commit is DB-only now. */
export interface CreatePgmFixtureFrameResult extends CreatePgmFixtureFrameInput {
  ok: boolean;
  skipped?: boolean;
  caseId?: string;
  reason?: string;
}

export interface PgmFixtureSelection {
  cue: PgmCueKind;
  pool: PgmFixturePool;
  learningExampleId: string;
  mentionId: string;
  contentId: string;
  tsec: number;
  stamp: string;
  sourceKey: string;
  selectedAt: string;
  pgmPass?: boolean;
  pgmRejectReason?: string;
  auditSource?: 'block_frames' | 'pgm_audit';
  committedCaseId?: string;
  committedAt?: string;
  commitError?: string;
}

export interface PgmAuditListItem {
  contentId: string;
  title?: string;
  url?: string;
  cue: PgmCueKind | 'all';
  fps: number;
  sampledAt: string;
  frameCount: number;
  passCount?: number;
  failCount?: number;
  passRate: number;
  passCountByCue?: Partial<Record<PgmCueKind, number>>;
  framesDir?: string;
  localPath?: string;
}

export type PgmAuditCueResult = {
  pass: boolean;
  headline: string;
  rejectReason?: string;
  metrics?: Record<string, unknown>;
};

export interface PgmAuditFrame {
  tsec: number;
  stamp: string;
  pass: boolean;
  headline: string;
  rejectReason?: string;
  metrics: Record<string, unknown>;
  jpg: string;
  pgm: string;
  imageUrl: string;
  passedCues?: PgmCueKind[];
  cues?: Partial<Record<PgmCueKind, PgmAuditCueResult>>;
}

export interface PgmAuditLinkedMention {
  learningExampleId: string;
  mentionId: string;
  brandName: string;
  podcastTitle: string;
  segmentStart: number;
  segmentEnd: number;
}

export interface PgmAuditAnchor {
  learningExampleId: string;
  mentionId: string;
  synthetic: boolean;
  brandName?: string;
  podcastTitle?: string;
}

export interface PgmAuditDetail {
  meta: PgmAuditListItem;
  frames: PgmAuditFrame[];
  linkedMentions: PgmAuditLinkedMention[];
  auditAnchor: PgmAuditAnchor;
  selections: PgmFixtureSelection[];
}

export interface Program {
  _id: string;
  name: string;
  imageS3Key?: string;
  imageUrl?: string;
  keywords?: string[];
  hashtags?: string[];
  socialAccounts?: string[];
}

export interface DailyMetric {
  collectedAt: string;
  daysSincePublished: number;
  views: number;
  likes: number;
  comments: number;
}

export interface ContentDailyMetricRow {
  collectedAt: string | Date;
  daysSincePublished?: number;
  views: number;
  likes: number;
  comments: number;
}

export interface ContentDailyMetricsResponse {
  _id: string;
  title: string;
  publishedAt?: string;
  dailyMetrics: ContentDailyMetricRow[];
}

export interface LivestreamTimelineChatMessage {
  messageId: string;
  authorName: string;
  message: string;
  publishedAt: Date | string;
}

export interface LivestreamTimelineSegment {
  startSeconds: number;
  endSeconds: number;
  startTime: string;
  endTime: string;
  absoluteStartTime?: string;
  absoluteEndTime?: string;
  text: string;
  viewers?: number;
  likes?: number;
  videoViewCount?: number;
  chatCount?: number;
  viewerDelta?: number;
  chatMessageCount: number;
  chatMessages?: LivestreamTimelineChatMessage[];
}

export interface LivestreamTimelineResponse {
  livestreamId: string;
  title: string;
  duration: number;
  actualStartTime?: string;
  segments: LivestreamTimelineSegment[];
  peakViewers?: number;
  peakViewersTime?: string;
  totalChatMessages: number;
  averageViewers?: number;
}

export interface ContentItem {
  _id: string;
  title: string;
  publishedAt?: string;
  source: string;
  mediaType: string;
  duration?: number;
  url?: string;
  thumbnail?: string;
  isTranscribed: boolean;
  isAnalyzed: boolean;
  brandMentionsCount?: number;
  lastAnalyzedAt?: string;
  program?: Program;
  dailyMetrics?: DailyMetric[];
  views?: number;
  likes?: number;
  comments?: number;
  selectedMetricDate?: string;
  reviewReadyAt?: string;
}

export interface PrivateBrandMention {
  _id: string;
  contentId: string;
  companyId: string;
  podcastId: string;
  segmentStart: number;
  segmentEnd: number;
  duration: number;
  brandId?: string;
  brandName: string;
  brand?: {
    _id: string;
    name: string;
    logoUrl?: string;
    s3Key?: string;
    aliases?: string[];
    category?: string;
  }; // Full brand object
  rawMentionName?: string;
  brandCategory?: string;
  transcription?: string;
  mentionText: string;
  context: string;
  confidence: number;
  detectionConfidence?: number;
  commercialConfidence?: number;
  aiClassification: {
    model: string;
    processedAt: string;
    gemini?: {
      confidence: 'high' | 'medium' | 'low';
      description: string;
      reasoning: string;
      brands: Array<{ brand: string; confidence: 'high' | 'medium' | 'low' }>;
      isSponsorStrap?: boolean;
      isSponsorCurtain?: boolean;
      rawJson?: unknown;
    };
    gpt?: {
      detection: {
        model: string;
        brandName: string;
        matchedBrandName?: string;
        fullMentionText: string;
        context: string;
        mentionType: string;
        detectionConfidence: number;
        commercialConfidence: number;
        segmentIndex: number;
      };
      rescore?: {
        model: string;
        mentionType: string;
        commercialConfidence: number;
        previousMentionType: string;
        previousCommercialConfidence: number;
        gptMentionType?: string;
        gptCommercialConfidence?: number;
      };
      final?: {
        mentionType: string;
        detectionConfidence: number;
        commercialConfidence: number;
      };
    };
  };
  mentionType?: "paid" | "organic" | "neutral";
  sentiment?: "positive" | "negative" | "neutral";
  placementType?:
    | "content"
    | "opening_credits"
    | "closing_credits"
    | "mid_credits"
    | "physical_placement"
    | "banner"
    | "overlay";
  videoS3Key?: string;
  framesS3Key?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface PrivateBrandPresence {
  _id: string;
  contentId: string;
  companyId: string;
  podcastId: string;
  brandId?: string;
  brandName: string;
  brand?: {
    _id: string;
    name: string;
    s3Key?: string;
    aliases?: string[];
    category?: string;
  }; // Full brand object
  brandWebsite?: string;
  brandCategory?: string;
  brandAliases?: string[];
  brandSocialAccounts?: string[];
  rawMentionText: string;
  normalizedBrandName: string;
  detectionSource: "description" | "title" | "hashtags" | "tags";
  detectionMethod:
    | "at_mention"
    | "hashtag"
    | "url_pattern"
    | "exact_match"
    | "fuzzy_match";
  confidence: number;
  presenceType:
    | "sponsor_tag"
    | "brand_mention"
    | "collaboration"
    | "partnership";
  /** Short-form presences only; disjoint from long-form mention `placementType`. */
  placementType?: "tagged" | "placement" | "overlay";
  context?: string;
  position: number;
  verified: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface GroupedContent {
  groups: {
    _id: string;
    title: string;
    content: Array<StudioPost | StudioEpisode>;
  }[];
  allContent: {
    youtubeShorts: StudioEpisode[];
    instagramReels: StudioPost[];
    instagramPosts: StudioPost[];
    instagramContent?: any[];
    tiktokPosts: StudioPost[];
    tiktokContent?: any[];
    youtubeContent?: any[];
    facebookPosts: StudioPost[];
    youtubePosts: StudioPost[];
    videoEpisodes: StudioEpisode[];
  };
}

export interface ContentSection {
  id: string;
  name: string;
  contentIds: string[];
}

/** Rule-based membership for `collectionType: 'dynamic'` (resolved server-side). */
export interface DynamicCollectionCriteria {
  brandIds?: string[];
  programIds?: string[];
  startDate?: string;
  endDate?: string;
  /** Platform keys to restrict content. Empty/omitted = all platforms. Values: 'youtube_videos' | 'youtube_shorts' | 'instagram' | 'facebook' | 'tiktok' */
  sources?: string[];
  /** Content matches if title or description contains any keyword (OR). */
  keywords?: string[];
  /** Lowercase hashtags without `#`; collection-wide (also with channelScopes). OR across tags. */
  hashtags?: string[];
  /** Per-channel scope: OR across entries; empty programIds = all programs in that channel. */
  channelScopes?: Array<{
    podcastId: string;
    programIds: string[];
    brandIds?: string[];
    keywords?: string[];
    startDate?: string;
    endDate?: string;
    sources?: string[];
  }>;
}

export interface ShareLinkPreset {
  presetId: string;
  name?: string;
  brandIds: string[];
}

export interface ClipsPlatformProfile {
  handle: string;
  displayName?: string;
  avatarUrl?: string;
  avatarS3Key?: string;
  followers?: number;
  isVerified?: boolean;
  profileUrl?: string;
  fetchedAt?: string;
}

export interface ClipsBrandConfig {
  name: string;
  hashtags: string[];
  instagramHandle?: string;
  tiktokHandle?: string;
  instagramProfile?: ClipsPlatformProfile;
  tiktokProfile?: ClipsPlatformProfile;
}

export interface ClipsInfluencerConfig {
  name?: string;
  instagramHandle?: string;
  tiktokHandle?: string;
  instagramProfile?: ClipsPlatformProfile;
  tiktokProfile?: ClipsPlatformProfile;
}

export interface ClipsCollectionConfig {
  brand: ClipsBrandConfig;
  influencers: ClipsInfluencerConfig[];
  /** YYYY-MM-DD — influencer ingest from this day through today. */
  startDate: string;
}

export interface ClipsImportSummary {
  imported: Array<{
    contentId: string;
    url: string;
    platform: "instagram" | "tiktok" | "youtube";
    created: boolean;
  }>;
  skipped: Array<{
    contentId: string;
    url: string;
    platform: "instagram" | "tiktok" | "youtube";
  }>;
  failed: Array<{ url: string; reason: string }>;
  attached: number;
}

export interface PrivateCollection {
  _id: string;
  title: string;
  /** Discriminator: manual = explicit items; dynamic = criteria; clips = Clips product. */
  collectionType?: "manual" | "dynamic" | "clips";
  /** Mirrors `collectionType` for convenience (`manual` | `dynamic` | `clips`). */
  type?: "manual" | "dynamic" | "clips";
  dynamicCriteria?: DynamicCollectionCriteria;
  /** Clips: brand metadata + influencer accounts. */
  clipsConfig?: ClipsCollectionConfig;
  podcastId?: string;
  podcastIds?: string[]; // For multi-podcast collections
  companyId: string;
  assignedBrandId?: string;
  assignedBrandIds?: string[];
  includeEstimatedViewers?: boolean;
  includeInstagramReach?: boolean;
  includeLiveViewers?: boolean;
  /** When true, show livestream chat columns in collection reports/PDF. Default false (opt-in). */
  includeLivestreamChat?: boolean;
  /**
   * Narrow short-form brand presence by `placementType` (1–2 values). Omitted / all three = no filter.
   * Persisted on the collection for public sponsor view and PDF.
   */
  brandPresencePlacementTypes?: ("tagged" | "placement" | "overlay")[];
  /**
   * Narrow long-form brand mentions by placement bucket (1–2 values). Omitted / all three = no filter.
   * `content` = spoken content mentions, `credits` = opening/closing credits (incl. legacy mid_credits), `physical` = on-screen placements.
   */
  brandMentionBuckets?: ("content" | "credits" | "physical")[];
  includeBrands?: boolean;
  /** When true, show mention-type summary cards above the mentions table. Default false (legacy). */
  includeMentionSummary?: boolean;
  /** Company + program entitlements; from GET collection detail. */
  demographicDataEnabled?: boolean;
  /** Collection's company has a YouTube OAuth social account; from GET collection detail. */
  youtubeOAuthConnected?: boolean;
  /** Collection's company has a Meta (Instagram) OAuth social account; from GET collection detail. */
  instagramOAuthConnected?: boolean;
  /** When true, show demographics / audiencia OAuth aggregates in reports. Default false (opt-in). */
  includeDemographicsInReports?: boolean;
  /**
   * When true, show episode audience-retention section (25% / 50% / 75%) in public reports.
   * Default false (opt-in).
   */
  includeEpisodeRetentionInReports?: boolean;
  /**
   * When true, show YouTube Analytics engaged views in public reports and PDF.
   * Default false (opt-in).
   */
  includeEngagedViewsInReports?: boolean;
  livestreamDataEnabled?: boolean;
  displayPodcastName?: string;
  isMultiPodcast?: boolean;
  /** Which product owns this collection (`clips` vs default Mi contenido). */
  productSource?: "micontenido" | "clips";
  sections?: ContentSection[];
  shareLinkPresets?: ShareLinkPreset[];
  createdAt: string;
  updatedAt: string;
  /** Present on create response after influencer ingest. */
  importSummary?: ClipsImportSummary;
}

export interface PrivateCollectionWithContent extends PrivateCollection {
  content: PrivateContent[];
}

export interface PrivateCollectionsContent {
  collections: PrivateCollectionWithContent[];
  allContent: {
    instagramContent: PrivateContent[];
    tiktokContent: PrivateContent[];
    youtubeContent: PrivateContent[];
    spotifyContent: PrivateContent[];
    facebookContent: PrivateContent[];
  };
  /**
   * When content was filtered by brandId, program names for programs present
   * in that content only (e.g. one of several dynamic-criteria programs).
   */
  displayPodcastName?: string;
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export interface CompanyCollection {
  _id: string;
  title: string;
  collectionType?: string;
  productSource?: string | null;
  podcastId?: {
    _id: string;
    title: string;
    slug?: string;
    imageS3Key?: string;
    imageUrl?: string;
  };
  companyId: string;
  isMultiPodcast?: boolean;
  assignedBrandId?: string;
  assignedBrandIds?: string[];
  createdAt: string;
  updatedAt: string;
  itemCount: number;
  clipsConfig?: ClipsCollectionConfig;
  /** Ready-to-display Clips brand cover (profile avatar or Brand logo). */
  clipsBrandImageUrl?: string | null;
}

export interface CompanyCollectionsResponse {
  collections: CompanyCollection[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface StudioEpisodeSponsorMention {
  _id: string;
  startTime: number;
  endTime: number;
  text: string;
  confidence: number;
  verified: boolean;
  paidProbability?: number;
  classification?: "paid" | "organic";
  mentionType: "conversation" | "segment" | "visual";
  standardized_qualitative?: {
    mention_type: "paid" | "organic" | "exchange";
    tone: "positive" | "neutral" | "negative";
    host_involvement: "scripted" | "personal_recommendation" | "casual_mention";
    position_in_content: "start" | "middle" | "end" | "throughout";
    message_clarity: boolean;
    video_style: "testimonial" | "comedy" | "informative" | "other";
    branding_format: "logo" | "product_shown" | "none";
    brand_visible: boolean;
    podcast_context: string;
    content_context: string;
  };
  sponsor: {
    _id: string;
    name: string;
    website?: string;
    categories?: string[];
    logo?: string;
  };
  episode: {
    _id: string;
    title: string;
    publishedAt: string;
    type?: string;
    url?: string;
    source?: string;
    views?: number;
    likes?: number;
    comments?: number;
  };
}

export interface ExtendedStudioEpisodeSponsorMention extends StudioEpisodeSponsorMention {
  source: "youtube" | "instagram" | "tiktok" | "facebook";
  transcript: string;
  frames?: string[];
  s3VideoUrl?: string;
  post?: StudioPost;
}

export interface SponsorMention {
  _id: string;
  episodeId: string;
  sponsorId: string;
  startTime: number;
  endTime: number;
  duration: number;
  text: string;
  confidence: number;
  verified: boolean;
  detectedAt: string;
  createdAt: string;
  updatedAt: string;
  sponsor: {
    _id: string;
    name: string;
    website?: string;
    categories?: string[];
    logo?: string;
  };
  episode: {
    _id: string;
    title: string;
    publishedAt: string;
    type?: string;
    url?: string;
  };
  podcast: {
    _id: string;
    title: string;
    imageUrl?: string;
  };
}

export interface SponsorFrame {
  _id: string;
  sponsorId: string;
  episodeId: string;
  mentionId: string;
  timestamps: number[];
  filePaths: string[];
  videoPath?: string;
  s3VideoUrl?: string;
  s3FrameUrls?: string[];
  extractedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface SponsorDetails {
  sponsor: {
    _id: string;
    name: string;
    website?: string;
    logo?: string;
    categories?: string[];
    description?: string;
  };
  stats: {
    totalMentions: number;
    totalEpisodes: number;
    totalDuration: number;
    totalViews: number;
  };
  episodes: Array<{
    _id: string;
    episode: {
      _id: string;
      title: string;
      description?: string;
      publishedAt?: string;
      duration?: number;
      views?: string;
      likes?: string;
      comments?: string;
      url?: string;
      thumbnail?: string;
    };
    podcast: {
      _id: string;
      title: string;
      slug: string;
      imageUrl?: string;
      youtubeData?: any;
    };
    mentionCount: number;
    totalDuration: number;
    mentions: Array<{
      _id: string;
      startTime: number;
      endTime: number;
      duration: number;
      text?: string;
      confidence: number;
      mentionType: string;
      detectedAt: string;
    }>;
  }>;
}

export interface PoliticalMention {
  _id: string;
  episodeId: string;
  podcastId: string;
  candidateId?: string;
  primaryTopicId?: string;
  entityType: "candidate" | "topic";
  entity: string;
  segmentStart: number;
  segmentEnd: number;
  duration: number;
  mentionText: string;
  context: string;
  confidence: number;
  subtopics: string[];
  aiClassification: {
    model: string;
    processedAt: string;
  };
  speakerType: string;
  sentiment: string;
  sentimentScore: number;
  createdAt: string;
  updatedAt: string;
  episode: {
    _id: string;
    title: string;
    publishedAt: string;
    podcastId: string;
  };
  podcast: {
    _id: string;
    title: string;
    imageUrl?: string;
    type?: "show" | "network";
  };
}

export interface SponsorWithMentions {
  _id: string;
  name: string;
  website?: string;
  logo?: string;
  categories?: string[];
  totalMentions: number;
  verifiedMentions: number;
  unverifiedMentions: number;
  firstMention: string;
  lastMention: string;
  episodeCount: number;
  podcastCount: number;
  podcasts: Array<{
    _id: string;
    title: string;
    imageUrl?: string;
  }>;
}

export interface ContentSponsorRequest {
  contentIds: string[];
  contentType: "episode" | "post";
}

export interface ContentSponsorResponse {
  [contentId: string]: StudioEpisodeSponsorMention[];
}

export interface CreatePostGroupRequest {
  podcastId: string;
  title: string;
}

export interface AddPostGroupItemsRequest {
  items: {
    contentId: string;
    contentType:
      | "instagram"
      | "tiktok"
      | "facebook"
      | "youtube_short"
      | "video";
    contentCategory: "episode" | "post";
  }[];
}

export interface PostGroup {
  _id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  posts: StudioPost[];
  episodes: StudioEpisode[];
}

export interface PaginatedResponse<T> {
  podcasts?: T[];
  episodes?: T[];
  sponsors?: T[];
  posts?: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface DashboardStats {
  totalPodcasts: number;
  totalEpisodes: number;
  lastUpdated: string;
}

export interface AuthResponse {
  message: string;
  user: User;
  token?: string;
}

export interface ProfileResponse {
  user: User;
}

export interface AnalysisStatus {
  completed: number;
  pending: number;
  failed: number;
}

export interface PodcastAnalysisSummary {
  _id: string;
  title: string;
  imageUrl?: string;
  imageS3Key?: string;
  author?: string;
  companyIds?: string[];
  /** @deprecated Prefer companyIds */
  companyId?: string;
  totalContent: number;
  analyzedContent: number;
  transcribedContent: number;
  readyContent: number;
  readyProgress: number;
  brandMentionsCount: number;
  paidMentionsCount: number;
  uniqueBrands: number;
  uniquePaidBrands: number;
  /** Mentions not yet confirmed or rejected by a human. */
  pendingMentionsCount?: number;
  /** Episodes with at least one pending mention. */
  pendingMentionsContent?: number;
  lastAnalyzedAt?: string;
  analysisProgress: number;
  totalViews?: number;
  totalDuration?: number;
  avgViewsPerContent?: number;
  avgUniquePaidBrandsPerContent?: number;
  content?: any[]; // Array of content items
}

export interface EpisodeAnalysisSummary {
  _id: string;
  title: string;
  publishedAt: string;
  source: string;
  mediaType: string;
  duration?: number;
  url?: string;
  thumbnail?: string;
  isTranscribed: boolean;
  isContentAnalyzed: boolean;
  isSponsorAnalyzed: boolean;
  brandMentionsCount: number;
  transcribedAt?: string;
  contentAnalyzedAt?: string;
  sponsorAnalyzedAt?: string;
  analysisErrors: string[];
}

// Session management
export const sessionService = {
  setSession(user: User, token?: string) {
    try {
      localStorage.setItem("user", JSON.stringify(user));
      if (token) {
        localStorage.setItem("authToken", token);
      }
    } catch {
      // Safari private / in-app browsers can block storage.
    }
  },

  clearSession() {
    try {
      localStorage.removeItem("user");
      localStorage.removeItem("authToken");
    } catch {
      // ignore
    }
  },

  getSession() {
    try {
      const user = localStorage.getItem("user");
      const token = localStorage.getItem("authToken");
      return {
        user: user ? JSON.parse(user) : null,
        token: token || null,
      };
    } catch {
      return { user: null, token: null };
    }
  },

  isSessionValid() {
    const { user } = this.getSession();
    return !!(user && user._id);
  },
};

export type ExplorerLivestreamProgramViewerShapePoint = {
  minute: number;
  avgViewers: number;
  episodeCount: number;
};

export type ExplorerLivestreamProgramViewerShapeSeries = {
  livestreamId: string;
  title: string;
  startedAt: string;
  points: Array<{ minute: number; avgViewers: number }>;
};

export type ExplorerLivestreamProgramViewerShape = {
  points: ExplorerLivestreamProgramViewerShapePoint[];
  series?: ExplorerLivestreamProgramViewerShapeSeries[];
};

export type ExplorerLivestreamPeriodMetrics = {
  youtube: {
    summary: {
      views: number;
      likes: number;
      comments: number;
      interactions: number;
    };
    daily: Array<{
      date: string;
      views: number;
      likes: number;
      comments: number;
    }>;
  };
  live: {
    summary: {
      episodeCount: number;
      avgViewers: number;
      avgPeakViewers: number;
    };
    episodes: Array<{
      date: string;
      avgViewers: number;
      peakViewers: number;
    }>;
  };
};

export type ExplorerLivestreamCompetingCategory = "tv" | "noticias" | "radio" | "digital";

export type ExplorerLivestreamCompetitionCategory =
  | ExplorerLivestreamCompetingCategory
  | "all";

export type ExplorerLivestreamCompetingChannel = {
  name: string;
  imageS3Key?: string;
  color?: string;
  category?: ExplorerLivestreamCompetingCategory;
};

export type ExplorerLivestreamCompetingWindow = {
  channelName: string;
  start: string;
  end: string;
};

export type ExplorerLivestreamCompetingWindows = {
  category?: ExplorerLivestreamCompetingCategory;
  channels: Record<string, ExplorerLivestreamCompetingChannel>;
  windows: ExplorerLivestreamCompetingWindow[];
};

export type ExplorerLivestreamCompetitionMinute = {
  at: string;
  viewers: number;
};

export type ExplorerLivestreamCompetitionStream = {
  id: string;
  title: string;
  programName: string | null;
  channelName: string;
  imageS3Key?: string;
  color?: string;
  videoId?: string;
  url?: string;
  startedAt: string;
  endedAt: string;
  avgViewers: number;
  peakViewers: number;
  currentViewers?: number | null;
  live?: boolean;
  minutes: ExplorerLivestreamCompetitionMinute[];
};

export type ExplorerLivestreamCompetition = {
  category: ExplorerLivestreamCompetitionCategory;
  graphStartedAt: string;
  graphEndedAt: string;
  streams: ExplorerLivestreamCompetitionStream[];
};

export type ExplorerLivestreamCompetitionQuery = {
  start?: string;
  end?: string;
  id?: string;
  category?: string;
};

// API functions
export const apiService = {
  // Health check
  async getHealth() {
    const response = await api.get("/health");
    return response.data;
  },

  // Authentication
  async signUp(
    name: string,
    email: string,
    password: string,
    company?: string,
  ): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>("/api/auth/signup", {
      name,
      email,
      password,
      company,
    });
    return response.data;
  },

  async signIn(email: string, password: string): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>("/api/auth/signin", {
      email,
      password,
    });
    return response.data;
  },

  async getProfile(email: string): Promise<ProfileResponse> {
    const response = await api.get<ProfileResponse>(
      `/api/auth/profile?email=${encodeURIComponent(email)}`,
    );
    return response.data;
  },

  async validateSession(): Promise<boolean> {
    try {
      const { user, token } = sessionService.getSession();
      if (!user || !token) return false;

      // For JWT, we can validate by checking if the token exists and user data is present
      // The actual validation happens on the server side with each request
      return true;
    } catch (error) {
      return false;
    }
  },

  async getCurrentUser(): Promise<User> {
    const response = await api.get<User>("/api/auth/me");
    return response.data;
  },

  async changePassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<{ message: string }> {
    const response = await api.put<{ message: string }>(
      "/api/auth/change-password",
      {
        currentPassword,
        newPassword,
      },
    );
    return response.data;
  },

  async getCompanyInvitationPreview(token: string): Promise<{
    kind: "email" | "link";
    companyName: string;
    email: string | null;
    emailMasked: string | null;
    expiresAt: string;
    companyRole: "admin" | "member";
    hasAccount: boolean;
    alreadyInCompany: boolean;
    inOtherCompany: boolean;
  }> {
    const response = await api.get(
      `/api/auth/invitations/${encodeURIComponent(token)}`,
    );
    return response.data;
  },

  async getDemoLoginPreview(token: string): Promise<{
    email: string;
    name: string;
    companyName: string | null;
  }> {
    const response = await api.get(
      `/api/auth/demo-login/${encodeURIComponent(token)}`,
    );
    return response.data;
  },

  async demoLogin(token: string): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>("/api/auth/demo-login", {
      token,
    });
    return response.data;
  },

  async acceptCompanyInvitation(body: {
    token: string;
    name?: string;
    password?: string;
    email?: string;
  }): Promise<AuthResponse & { alreadyMember?: boolean }> {
    const response = await api.post<AuthResponse & { alreadyMember?: boolean }>(
      "/api/auth/invitations/accept",
      body,
    );
    return response.data;
  },

  async forgotPassword(email: string): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>(
      "/api/auth/forgot-password",
      { email, inviteBaseUrl: window.location.origin },
    );
    return response.data;
  },

  async resetPassword(
    token: string,
    newPassword: string,
  ): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>(
      "/api/auth/reset-password",
      { token, newPassword },
    );
    return response.data;
  },

  // Podcasts
  async getPodcasts(
    page = 1,
    limit = 10,
    search?: string,
    category?: string,
    sortBy?: string,
  ) {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (search) params.append("search", search);
    if (category) params.append("category", category);
    if (sortBy) params.append("sortBy", sortBy);

    const response = await api.get<PaginatedResponse<Podcast>>(
      `/api/podcasts?${params}`,
    );
    return response.data;
  },

  async getPodcast(id: string) {
    const response = await api.get<Podcast>(`/api/podcasts/${id}`);
    return response.data;
  },

  async getPodcastPrograms(
    podcastId: string,
    options?: { monthFilter?: string | null; week?: string | null },
  ) {
    const params = new URLSearchParams();
    if (options?.monthFilter) {
      params.append("monthFilter", options.monthFilter);
    }
    if (options?.week) {
      params.append("week", options.week);
    }
    const queryString = params.toString();
    const url = `/api/podcasts/${podcastId}/programs${queryString ? `?${queryString}` : ""}`;
    const response = await api.get<{
      programs: Array<
        Program & { totalContent?: number; readyContent?: number }
      >;
      unassigned?: { totalContent: number; readyContent: number };
      totals?: { totalContent: number; readyContent: number };
    }>(url);
    return response.data;
  },

  async setContentReviewReady(contentId: string, ready: boolean) {
    const response = await api.patch<{
      success: boolean;
      ready: boolean;
      reviewReadyAt: string | null;
    }>(`/api/content/${contentId}/review-ready`, { ready });
    return response.data;
  },

  async getContentTaxonomyChunks(contentId: string) {
    const response = await api.get<ContentTaxonomyChunksResponse>(
      `/api/content/${contentId}/taxonomy-chunks`,
    );
    return response.data;
  },

  async getPrivateContentTaxonomyChunks(privateContentId: string) {
    const response = await api.get<ContentTaxonomyChunksResponse>(
      `/api/private-content/${privateContentId}/taxonomy-chunks`,
    );
    return response.data;
  },

  async getPrivateContentPeople(privateContentId: string) {
    const response = await api.get<PrivateContentPeopleResponse>(
      `/api/private-content/${privateContentId}/people`,
    );
    return response.data;
  },

  async getPrivateContentTranscript(privateContentId: string) {
    const response = await api.get<{ body: string; s3Key: string }>(
      `/api/private-content/${privateContentId}/transcript`,
    );
    return response.data;
  },

  async getPodcastTaxonomySummary(
    podcastId: string,
    options?: { week?: string; programId?: string },
  ) {
    const params = new URLSearchParams();
    if (options?.week) params.append("week", options.week);
    if (options?.programId) params.append("programId", options.programId);
    const qs = params.toString();
    const response = await api.get<PodcastTaxonomySummaryResponse>(
      `/api/podcasts/${podcastId}/taxonomy-summary${qs ? `?${qs}` : ""}`,
    );
    return response.data;
  },

  async getPodcastTaxonomyByProgram(
    podcastId: string,
    options?: { week?: string },
  ) {
    const params = new URLSearchParams();
    if (options?.week) params.append("week", options.week);
    const qs = params.toString();
    const response = await api.get<PodcastTaxonomyByProgramResponse>(
      `/api/podcasts/${podcastId}/taxonomy-by-program${qs ? `?${qs}` : ""}`,
    );
    return response.data;
  },

  async getPodcastTaxonomySubtopicChunks(
    podcastId: string,
    options: {
      topicSlug: string;
      subtopicSlug: string;
      week?: string;
      programId?: string;
    },
  ) {
    const params = new URLSearchParams();
    params.append("topicSlug", options.topicSlug);
    params.append("subtopicSlug", options.subtopicSlug);
    if (options.week) params.append("week", options.week);
    if (options.programId) params.append("programId", options.programId);
    const response = await api.get<PodcastTaxonomySubtopicChunksResponse>(
      `/api/podcasts/${podcastId}/taxonomy-subtopic-chunks?${params.toString()}`,
    );
    return response.data;
  },

  async getMiContenidoTaxonomySummary(
    podcastId: string,
    startDate: string,
    endDate: string,
    programId?: string,
    mediaMode: MiContenidoMediaMode = "videos",
  ) {
    const params = new URLSearchParams();
    params.append("startDate", startDate);
    params.append("endDate", endDate);
    if (programId) params.append("programId", programId);
    if (mediaMode === "clips") params.append("mediaMode", "clips");
    const response = await api.get<PodcastTaxonomySummaryResponse>(
      `/api/companies/me/podcasts/${podcastId}/taxonomy-summary?${params.toString()}`,
    );
    return response.data;
  },

  async getMiContenidoTaxonomySubtopicChunks(
    podcastId: string,
    startDate: string,
    endDate: string,
    topicSlug: string,
    subtopicSlug: string,
    programId?: string,
    mediaMode: MiContenidoMediaMode = "videos",
  ) {
    const params = new URLSearchParams();
    params.append("startDate", startDate);
    params.append("endDate", endDate);
    params.append("topicSlug", topicSlug);
    params.append("subtopicSlug", subtopicSlug);
    if (programId) params.append("programId", programId);
    if (mediaMode === "clips") params.append("mediaMode", "clips");
    const response = await api.get<PodcastTaxonomySubtopicChunksResponse>(
      `/api/companies/me/podcasts/${podcastId}/taxonomy-subtopic-chunks?${params.toString()}`,
    );
    return response.data;
  },

  async getMiContenidoTaxonomyScopeContent(
    podcastId: string,
    startDate: string,
    endDate: string,
    programId?: string,
    mediaMode: MiContenidoMediaMode = "videos",
  ) {
    const params = new URLSearchParams();
    params.append("startDate", startDate);
    params.append("endDate", endDate);
    if (programId) params.append("programId", programId);
    if (mediaMode === "clips") params.append("mediaMode", "clips");
    const response = await api.get<PodcastTaxonomyScopeContentResponse>(
      `/api/companies/me/podcasts/${podcastId}/taxonomy-scope-content?${params.toString()}`,
    );
    return response.data;
  },

  async getMiContenidoPeopleDrivers(
    podcastId: string,
    startDate: string,
    endDate: string,
    programId?: string,
    mediaMode: MiContenidoMediaMode = "clips",
  ) {
    const params = new URLSearchParams();
    params.append("startDate", startDate);
    params.append("endDate", endDate);
    if (programId) params.append("programId", programId);
    if (mediaMode === "clips") params.append("mediaMode", "clips");
    const response = await api.get<MiContenidoPeopleDriversResponse>(
      `/api/companies/me/podcasts/${podcastId}/people-drivers?${params.toString()}`,
    );
    return response.data;
  },

  async unlinkProgramFromContent(contentId: string): Promise<void> {
    await api.patch(`/api/content/${contentId}/unlink-program`);
  },

  async linkProgramToContent(
    contentId: string,
    programId: string | null,
  ): Promise<{ success: boolean; message: string; programId?: string }> {
    const response = await api.patch<{
      success: boolean;
      message: string;
      programId?: string;
    }>(`/api/content/${contentId}/program`, { programId });
    return response.data;
  },

  async bulkAssignProgramToContent(
    podcastId: string,
    contentIds: string[],
    programId: string | null,
  ): Promise<{
    matchedCount: number;
    modifiedCount: number;
    programId: string | null;
  }> {
    const response = await api.patch<{
      matchedCount: number;
      modifiedCount: number;
      programId: string | null;
    }>(`/api/admin/channels/${podcastId}/content/program`, {
      contentIds,
      programId,
    });
    return response.data;
  },

  async updatePrivateContentProgram(
    contentId: string,
    programId: string | null,
  ): Promise<{
    success: boolean;
    message?: string;
    programId: string | null;
    content?: PrivateContent;
  }> {
    const response = await api.patch<{
      success: boolean;
      message?: string;
      programId: string | null;
      content?: PrivateContent;
    }>(`/api/collections/content/${contentId}/program`, { programId });
    return response.data;
  },

  async updateContentIsPodcast(
    contentId: string,
    isPodcast: boolean,
  ): Promise<{ success: boolean; isPodcast: boolean }> {
    const response = await api.patch<{ success: boolean; isPodcast: boolean }>(
      `/api/content/${contentId}/is-podcast`,
      { isPodcast },
    );
    return response.data;
  },

  async getPodcastBySlug(slug: string) {
    const response = await api.get<Podcast>(`/api/podcasts/slug/${slug}`);
    return response.data;
  },

  // Media Kit
  async getPodcastMediaKit(slug: string, companySlug?: string) {
    const params = companySlug
      ? `?companySlug=${encodeURIComponent(companySlug)}`
      : "";
    const response = await api.get(
      `/api/podcasts/slug/${encodeURIComponent(slug)}/media-kit${params}`,
    );
    return response.data;
  },

  async getMediaKitExample() {
    const response = await api.get(`/api/podcasts/media-kit/example`);
    return response.data;
  },

  async getProgramMediaKit(programSlug: string) {
    const response = await api.get(
      `/api/podcasts/program/${encodeURIComponent(programSlug)}/media-kit`,
    );
    return response.data;
  },

  async getProgramMediaKitByPath(
    companySlug: string,
    podcastSlug: string,
    programSlug: string,
  ) {
    const response = await api.get(
      `/api/podcasts/${encodeURIComponent(companySlug)}/${encodeURIComponent(podcastSlug)}/${encodeURIComponent(programSlug)}`,
    );
    return response.data;
  },

  async getAdminPrixiEntities(page = 1, limit = 25, search?: string) {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    if (search?.trim()) params.append("search", search.trim());
    const response = await api.get<{
      items: Array<{
        companyId: string;
        companyName: string;
        companySlug: string;
        prixiCompanyId: string;
        prixiContactEmail?: string;
        createdAt?: string;
        podcasts: Array<{
          podcastId: string;
          title: string;
          slug: string;
          on_prixi: boolean;
          prixi_company_id?: string;
          prixiPodcastId?: string;
          socialAccounts: Array<{ platform: string; username?: string }>;
        }>;
      }>;
      pagination: { page: number; limit: number; total: number; pages: number };
    }>(`/api/admin/prixi-entities?${params}`);
    return response.data;
  },

  async getAdminPrixiCampaigns(opts?: {
    page?: number;
    limit?: number;
    search?: string;
    companyId?: string;
  }) {
    const page = opts?.page ?? 1;
    const limit = opts?.limit ?? 25;
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    if (opts?.search?.trim()) params.append("search", opts.search.trim());
    if (opts?.companyId?.trim()) params.append("companyId", opts.companyId.trim());
    const response = await api.get<{
      items: Array<{
        id: string;
        prixiCampaignId: string;
        name: string | null;
        label: string | null;
        podscopeCompanyId: string;
        companyName: string | null;
        companySlug: string | null;
        podscopeBrandId: string;
        brandName: string | null;
        periodStart: string;
        periodEnd: string;
        enabled: boolean;
        pipelineStatus: string;
        lastPipelineRunAt: string | null;
        lastPipelineError: string | null;
        scopeCount: number;
        programCount: number;
        scopes: Array<{
          podcastId: string;
          podcastTitle: string | null;
          programIds: string[];
          brandIds: string[];
        }>;
        pipelineHint: string;
        createdAt: string | null;
        updatedAt: string | null;
      }>;
      pagination: { page: number; limit: number; total: number; pages: number };
    }>(`/api/admin/prixi-campaigns?${params}`);
    return response.data;
  },

  async getPodcastMediaKitSettings(slug?: string) {
    const response = await api.get<{
      mediaKit: any;
      tiktokAudienceDemographics?: {
        gender?: Array<{ type: string; percentage: number }>;
        ageRanges?: Array<{ range: string; percentage: number }>;
      } | null;
    }>(
      `/api/podcasts/slug/${encodeURIComponent(slug || "")}/media-kit/settings`,
    );
    return response.data;
  },

  async getPodcastMediaKitLinkedPrograms(
    podcastSlug: string,
    months?: string[],
  ) {
    const params =
      months != null && months.length > 0
        ? { months: months.join(",") }
        : undefined;
    const response = await api.get<{
      programs: Array<{ slug: string; name: string }>;
    }>(
      `/api/podcasts/slug/${encodeURIComponent(podcastSlug)}/media-kit/linked-programs`,
      { params },
    );
    return response.data;
  },

  async getPodcastMediaKitYoutubeCatalog(
    podcastSlug: string,
    options?: {
      months?: string[];
      programSlug?: string;
      companySlug?: string;
      format?: "longForm" | "shorts";
    },
  ) {
    const params: Record<string, string> = {
      format: options?.format ?? "longForm",
    };
    if (options?.months != null && options.months.length > 0)
      params["months"] = options.months.join(",");
    if (options?.programSlug) params["programSlug"] = options.programSlug;
    if (options?.companySlug) params["companySlug"] = options.companySlug;
    const response = await api.get<{
      videos: Array<{
        id: string;
        title: string;
        url: string | null;
        thumbnail: string | null;
        imageS3Key: string | null;
        publishedAt: string | null;
        views: number;
        interactions: number;
        engagementRate: number | null;
        inProgress?: boolean;
      }>;
    }>(
      `/api/podcasts/slug/${encodeURIComponent(podcastSlug)}/media-kit/youtube-catalog`,
      { params },
    );
    return response.data;
  },

  async getPodcastMediaKitContentCounts(
    podcastSlug: string,
    months?: string[],
    programSlug?: string,
    companySlug?: string,
  ) {
    const params: Record<string, string> = {};
    if (months != null && months.length > 0)
      params["months"] = months.join(",");
    if (programSlug) params["programSlug"] = programSlug;
    if (companySlug) params["companySlug"] = companySlug;
    const response = await api.get<{
      youtube?: { videos: number; shorts: number };
      instagram?: { posts: number };
      tiktok?: { videos: number };
      streaming?: { count: number };
    }>(
      `/api/podcasts/slug/${encodeURIComponent(podcastSlug)}/media-kit/content-counts`,
      { params },
    );
    return response.data;
  },

  /** Partial update: on/off and/or metrics window; keeps months and metric toggles as saved. */
  async patchPodcastMediaKitSettings(
    slug: string,
    patch: { enabled?: boolean; metricsWindow?: Partial<MediaKitMetricsWindow> },
  ) {
    const response = await api.patch<{ mediaKit: any }>(
      `/api/podcasts/slug/${encodeURIComponent(slug)}/media-kit/settings`,
      patch,
    );
    return response.data;
  },

  async updatePodcastMediaKitSettings(
    slug?: string,
    payload?: {
      mediaKit?: any;
      tiktokAudienceDemographics?: {
        gender?: Array<{ type: string; percentage: number }>;
        ageRanges?: Array<{ range: string; percentage: number }>;
      } | null;
    },
  ) {
    const response = await api.put<{
      mediaKit: any;
      tiktokAudienceDemographics?: {
        gender?: Array<{ type: string; percentage: number }>;
        ageRanges?: Array<{ range: string; percentage: number }>;
      } | null;
    }>(
      `/api/podcasts/slug/${encodeURIComponent(slug || "")}/media-kit/settings`,
      payload ?? {},
    );
    return response.data;
  },

  // Podcast Analysis
  async getPodcastAnalysisSummary(
    page = 1,
    limit = 20,
    search?: string,
    analysisStatus?: string,
    hasMentions?: string,
    source?: string,
    sortBy?: string,
    timePeriod?: string,
    week?: string,
    category?: "other" | "politics" | "tv" | "noticias" | "radio",
  ) {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (search) params.append("search", search);
    if (analysisStatus) params.append("analysisStatus", analysisStatus);
    if (hasMentions) params.append("hasMentions", hasMentions);
    if (source) params.append("source", source);
    if (sortBy) params.append("sortBy", sortBy);
    if (timePeriod) params.append("timePeriod", timePeriod);
    if (week) params.append("week", week);
    if (category) params.append("category", category);

    const response = await api.get<{
      podcasts: PodcastAnalysisSummary[];
      pagination: { page: number; limit: number; total: number; pages: number };
      totals?: {
        totalPodcasts: number;
        totalContent: number;
        totalTranscribed: number;
        totalAnalyzed: number;
        totalReady?: number;
        totalBrandMentions: number;
        totalPaidMentions: number;
        totalUniqueBrands: number;
        totalUniquePaidBrands: number;
        totalAcceptedPaidBrands?: number;
        contentWithPaidMentions: number;
        totalDuration: number;
      };
    }>(`/api/podcasts/analysis-summary?${params}`);
    return response.data;
  },

  async getPodcastAnalysisDetails(podcastId: string, page = 1, limit = 20) {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    const response = await api.get<{
      podcast: Podcast;
      episodes: EpisodeAnalysisSummary[];
      pagination: { page: number; limit: number; total: number; pages: number };
    }>(`/api/podcasts/${podcastId}/analysis-details?${params}`);
    return response.data;
  },

  async getPodcastById(id: string) {
    const response = await api.get<Podcast>(`/api/podcasts/${id}`);
    return response.data;
  },

  /** Brand mentions for a page of content in one call, grouped by contentId. */
  async getBrandMentionsBatch(contentIds: string[]) {
    const response = await api.post<{
      mentionsByContentId: Record<string, BrandMention[]>;
    }>(`/api/podcasts/content/brand-mentions/batch`, { contentIds });
    return response.data;
  },

  async getContentTranscriptCoverage(contentId: string) {
    const response = await api.get<{
      rangeStart: number;
      rangeEnd: number;
      rangeDurationSec: number;
      coveredSec: number;
      missingSec: number;
      coveredPct: number;
      missingPct: number;
    }>(`/api/podcasts/content/${contentId}/transcript-coverage`);
    return response.data;
  },

  async getContentTranscriptSegments(contentId: string) {
    const response = await api.get<{
      segments: Array<{
        start: number;
        end: number;
        text: string;
        words?: Array<{ word: string; start: number; end: number }>;
      }>;
    }>(`/api/podcasts/content/${contentId}/transcript-segments`);
    return response.data;
  },

  async getContentTranscriptExcerpt(
    contentId: string,
    start: number,
    end: number,
    contextPadSec = 20,
  ) {
    const params = new URLSearchParams({
      start: String(start),
      end: String(end),
      contextPadSec: String(contextPadSec),
    });
    const response = await api.get<{
      segments: Array<{
        start: number;
        end: number;
        text: string;
        words?: Array<{ word: string; start: number; end: number }>;
      }>;
    }>(`/api/podcasts/content/${contentId}/transcript-excerpt?${params}`);
    return response.data;
  },

  async downloadContentTranscript(contentId: string) {
    const response = await api.get<Blob>(
      `/api/podcasts/content/${contentId}/transcript-download`,
      { responseType: "blob" },
    );
    const blob = new Blob([response.data], { type: "application/json" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `transcript-${contentId}.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  async deleteBrandMention(mentionId: string, hard = false) {
    const params = hard ? "?hard=true" : "";
    const response = await api.delete<{
      message: string;
      deletedMention: {
        _id: string;
        brandName: string;
        contentId: string;
      };
    }>(`/api/brands/mentions/${mentionId}${params}`);
    return response.data;
  },

  async createBrandMention(data: {
    contentId: string;
    brandId?: string;
    brandName: string;
    startTime: number;
    endTime: number;
    mentionType?: string;
    placementType?: string;
    commercialConfidence?: number;
    visualCue?: string | null;
    context?: string;
  }): Promise<{ mention: BrandMention }> {
    const response = await api.post<{ message: string; mention: BrandMention }>(
      "/api/brands/mentions/create",
      data,
    );
    return { mention: response.data.mention };
  },

  async getBrandReviewMetrics(params?: {
    podcastId?: string;
    from?: string;
    to?: string;
  }): Promise<{
    weeks: Array<{
      week: string;
      detected: number;
      accepted: number;
      corrected: number;
      rejected: number;
      missing: number;
      pending: number;
      precision: number | null;
      recall: number | null;
      editRate: number | null;
      cleanAcceptRate: number | null;
      episodesReviewed: number;
      correctionsByField: {
        brand: number;
        time: number;
        mentionType: number;
        placementType: number;
        commercialConfidence: number;
        visualCue: number;
      };
      correctionCombos?: Array<{ label: string; count: number }>;
      totalFieldChanges?: number;
    }>;
    totals: {
      detected: number;
      accepted: number;
      corrected: number;
      rejected: number;
      missing: number;
      pending: number;
      episodesReviewed: number;
      precision: number | null;
      recall: number | null;
      editRate: number | null;
      cleanAcceptRate: number | null;
      correctionsByField: {
        brand: number;
        time: number;
        mentionType: number;
        placementType: number;
        commercialConfidence: number;
        visualCue: number;
      };
      correctionCombos?: Array<{ label: string; count: number }>;
      totalFieldChanges?: number;
    } | null;
  }> {
    const q = new URLSearchParams();
    if (params?.podcastId) q.set("podcastId", params.podcastId);
    if (params?.from) q.set("from", params.from);
    if (params?.to) q.set("to", params.to);
    const response = await api.get(`/api/brands/review-metrics?${q.toString()}`);
    return response.data;
  },

  async updateMentionFeedback(
    mentionId: string,
    status: "confirmed" | "rejected" | null,
    reason?: string,
    context?: string,
  ): Promise<{ mention: BrandMention }> {
    const response = await api.patch<{ mention: BrandMention }>(
      `/api/brands/mentions/${mentionId}/feedback`,
      { status, reason, context },
    );
    return response.data;
  },

  async getPodcastLearningsPodcasts(): Promise<{ podcasts: PodcastLearningsFilterPodcast[] }> {
    const response = await api.get<{ podcasts: PodcastLearningsFilterPodcast[] }>(
      '/api/podcast-learnings/podcasts',
    );
    return response.data;
  },

  async getPodcastLearningsStats(podcastId?: string): Promise<PodcastLearningsStats> {
    const q = new URLSearchParams();
    if (podcastId) q.set('podcastId', podcastId);
    const response = await api.get<PodcastLearningsStats>(
      `/api/podcast-learnings/stats?${q.toString()}`,
    );
    return response.data;
  },

  async getPodcastLearningExamples(params: {
    podcastId?: string;
    flow?: string;
    failureMode?: string;
    outcome?: string;
    dateFrom?: string;
    dateTo?: string;
    evidence?: PodcastLearningEvidenceFilter;
    hasFrame?: boolean;
    hasBlockFrames?: boolean;
    pgmCue?: PgmCueKind;
    mentionOrigin?: MentionOrigin;
    includeInAlgorithm?: 'true' | 'false';
    limit?: number;
    offset?: number;
  }): Promise<{
    examples: PodcastLearningExampleRow[];
    total: number;
    limit: number;
    offset: number;
  }> {
    const q = new URLSearchParams();
    if (params.podcastId) q.set('podcastId', params.podcastId);
    if (params.flow) q.set('flow', params.flow);
    if (params.failureMode) q.set('failureMode', params.failureMode);
    if (params.outcome) q.set('outcome', params.outcome);
    if (params.dateFrom) q.set('dateFrom', params.dateFrom);
    if (params.dateTo) q.set('dateTo', params.dateTo);
    if (params.evidence) q.set('evidence', params.evidence);
    if (params.hasFrame) q.set('hasFrame', 'true');
    if (params.hasBlockFrames) q.set('hasBlockFrames', 'true');
    if (params.pgmCue) q.set('pgmCue', params.pgmCue);
    if (params.mentionOrigin) q.set('mentionOrigin', params.mentionOrigin);
    if (params.includeInAlgorithm) q.set('includeInAlgorithm', params.includeInAlgorithm);
    if (params.limit != null) q.set('limit', String(params.limit));
    if (params.offset != null) q.set('offset', String(params.offset));
    const response = await api.get(`/api/podcast-learnings/examples?${q.toString()}`);
    return response.data;
  },

  async updatePodcastLearningExample(
    id: string,
    body: {
      includeInAlgorithm?: boolean;
      excludeFromLearning?: boolean;
      reasonPreset?: string | null;
      reasonText?: string | null;
      visualCue?: string | null;
    },
  ): Promise<{ example: PodcastLearningExampleRow }> {
    const response = await api.patch<{ example: PodcastLearningExampleRow }>(
      `/api/podcast-learnings/examples/${id}`,
      body,
    );
    return response.data;
  },

  async updatePodcastLearningExampleInclusion(
    id: string,
    includeInAlgorithm: boolean,
  ): Promise<{ example: PodcastLearningExampleRow }> {
    return this.updatePodcastLearningExample(id, { includeInAlgorithm });
  },

  async updatePodcastLearningExampleExclusion(
    id: string,
    excludeFromLearning: boolean,
  ): Promise<{ example: PodcastLearningExampleRow }> {
    return this.updatePodcastLearningExample(id, { excludeFromLearning });
  },

  async getPodcastLearningExampleDetail(
    id: string,
    params?: { pgmCue?: PgmCueKind },
  ): Promise<{
    example: PodcastLearningExampleRow & { contentUrl?: string | null };
    mention: {
      _id: string;
      feedback?: { status?: string; reason?: string | null };
      mentionText?: string;
      context?: string;
      visualCue?: string;
      placementType?: string;
      evidenceType?: string;
      segmentStart?: number;
      segmentEnd?: number;
      frameS3Key?: string;
      frameUrl?: string;
      blockFrameUrls?: BlockFrameUrl[];
      transcriptExcerpt?: Array<{ start: number; end: number; text: string }>;
    } | null;
    transcriptSegments: Array<{ start: number; end: number; text: string }>;
    evidenceWindow: { start: number; end: number };
    pgmFixtures?: {
      cue: PgmCueKind;
      selections: PgmFixtureSelection[];
      committedSourceKeys: string[];
    };
  }> {
    const q = new URLSearchParams();
    if (params?.pgmCue) q.set('pgmCue', params.pgmCue);
    const suffix = q.toString() ? `?${q.toString()}` : '';
    const response = await api.get(`/api/podcast-learnings/examples/${id}${suffix}`);
    return response.data;
  },

  async getPodcastLearningProfile(podcastId: string): Promise<{
    profile: {
      podcastId: string;
      cacheVersion: number;
      podcastTitle?: string | null;
      categoryRows: PodcastLearningProfileCategory[];
    } | null;
    recentExamples: PodcastLearningExampleRow[];
  }> {
    const response = await api.get(`/api/podcast-learnings/profiles/${podcastId}`);
    return response.data;
  },

  async getPodcastLearningFeedbackGaps(params?: {
    podcastId?: string;
    limit?: number;
  }): Promise<{ gaps: PodcastLearningFeedbackGap[]; total: number }> {
    const q = new URLSearchParams();
    if (params?.podcastId) q.set('podcastId', params.podcastId);
    if (params?.limit != null) q.set('limit', String(params.limit));
    const response = await api.get(`/api/podcast-learnings/feedback-gaps?${q.toString()}`);
    return response.data;
  },

  async getPgmFixtureStatus(cue: PgmCueKind, pool: PgmFixturePool = 'positive'): Promise<PgmFixtureStatus> {
    const q = new URLSearchParams({ cue, pool });
    const response = await api.get<PgmFixtureStatus>(
      `/api/podcast-learnings/pgm-fixtures/status?${q.toString()}`,
    );
    return response.data;
  },

  async getPgmAudits(cue?: PgmCueKind): Promise<{ runs: PgmAuditListItem[]; total: number }> {
    const q = new URLSearchParams();
    if (cue) q.set('cue', cue);
    const response = await api.get<{ runs: PgmAuditListItem[]; total: number }>(
      `/api/podcast-learnings/pgm-audits?${q.toString()}`,
    );
    return response.data;
  },

  async getPgmAuditDetail(contentId: string): Promise<PgmAuditDetail> {
    const response = await api.get<PgmAuditDetail>(`/api/podcast-learnings/pgm-audits/${contentId}`);
    return response.data;
  },

  async commitPgmFixtures(params: {
    cue: PgmCueKind;
    pool?: PgmFixturePool;
    frames: CreatePgmFixtureFrameInput[];
  }): Promise<{
    cue: PgmCueKind;
    pool: PgmFixturePool;
    results: CommitPgmFixtureFrameResult[];
    index?: PgmFixtureStatus;
  }> {
    const response = await api.post<{
      cue: PgmCueKind;
      pool: PgmFixturePool;
      results: CommitPgmFixtureFrameResult[];
      index?: PgmFixtureStatus;
    }>('/api/podcast-learnings/pgm-fixtures', params);
    return response.data;
  },

  /** @deprecated Use commitPgmFixtures */
  async createPgmFixtures(params: {
    cue: PgmCueKind;
    pool?: PgmFixturePool;
    frames: CreatePgmFixtureFrameInput[];
  }): Promise<{
    cue: PgmCueKind;
    pool: PgmFixturePool;
    results: CommitPgmFixtureFrameResult[];
    index?: PgmFixtureStatus;
  }> {
    return this.commitPgmFixtures(params);
  },

  async getPgmFixtureSelections(params?: {
    cue?: PgmCueKind;
    mentionId?: string;
    contentId?: string;
    pool?: PgmFixturePool;
  }): Promise<{ selections: PgmFixtureSelection[]; source: string }> {
    const q = new URLSearchParams();
    if (params?.cue) q.set('cue', params.cue);
    if (params?.mentionId) q.set('mentionId', params.mentionId);
    if (params?.contentId) q.set('contentId', params.contentId);
    if (params?.pool) q.set('pool', params.pool);
    const response = await api.get<{ selections: PgmFixtureSelection[]; source: string }>(
      `/api/podcast-learnings/pgm-fixtures/selections?${q.toString()}`,
    );
    return response.data;
  },

  async savePgmFixtureSelections(params: {
    cue: PgmCueKind;
    pool?: PgmFixturePool;
    auditSource?: 'block_frames' | 'pgm_audit';
    learningExampleId?: string;
    mentionId?: string;
    contentId: string;
    tsecs?: number[];
    frames?: Array<{ tsec: number; pgmPass?: boolean; pgmRejectReason?: string }>;
  }): Promise<{
    cue: PgmCueKind;
    pool: PgmFixturePool;
    mentionId: string;
    savedCount: number;
    selections: PgmFixtureSelection[];
  }> {
    const response = await api.put<{
      cue: PgmCueKind;
      pool: PgmFixturePool;
      mentionId: string;
      savedCount: number;
      selections: PgmFixtureSelection[];
    }>('/api/podcast-learnings/pgm-fixtures/selections', params);
    return response.data;
  },

  async applyPgmFixtureSelections(params?: {
    cue?: PgmCueKind;
    mentionId?: string;
  }): Promise<{ applied: number; results: CommitPgmFixtureFrameResult[]; message?: string }> {
    const response = await api.post<{
      applied: number;
      results: CommitPgmFixtureFrameResult[];
      message?: string;
    }>('/api/podcast-learnings/pgm-fixtures/apply-selections', params ?? {});
    return response.data;
  },

  async updateMentionClassification(
    mention: BrandMention,
    updates: {
      mentionType?: string;
      placementType?: string;
      visualCue?: BrandMention["visualCue"];
    },
  ): Promise<void> {
    const body: Record<string, unknown> = {
      startTime: mention.startTime,
      endTime: mention.endTime,
      mentionType: updates.mentionType ?? mention.classification,
      commercialConfidence: mention.commercialConfidence,
      brandName: mention.brandName,
      brandId: mention.brandId,
    };
    if (updates.placementType !== undefined) {
      body.placementType = updates.placementType;
    }
    if (updates.visualCue !== undefined) {
      body.visualCue = updates.visualCue;
    }
    await api.patch(`/api/brands/mentions/${mention._id}/times`, body);
  },

  async updateMentionTimes(
    mentionId: string,
    startTime: number,
    endTime: number,
    mentionType: string,
    commercialConfidence: number,
    brandName?: string,
    brandId?: string,
    visualCue?: BrandMention["visualCue"],
    placementType?: BrandMention["placementType"],
  ) {
    const response = await api.patch<{
      message: string;
      updatedMention: {
        _id: string;
        brandName: string;
        brandId: string;
        contentId: string;
        startTime: number;
        endTime: number;
        mentionType: string;
        commercialConfidence: number;
        visualCue?: BrandMention["visualCue"];
        evidenceType?: BrandMention["evidenceType"];
      };
    }>(`/api/brands/mentions/${mentionId}/times`, {
      startTime,
      endTime,
      mentionType,
      commercialConfidence,
      brandName,
      brandId,
      ...(visualCue !== undefined ? { visualCue } : {}),
      ...(placementType !== undefined ? { placementType } : {}),
    });
    return response.data;
  },

  async bulkUpdateBrandMentions(
    oldBrandName: string,
    newBrandId: string,
    newBrandName: string,
    podcastId?: string,
  ) {
    const response = await api.patch<{
      message: string;
      updatedCount: number;
      matchedCount: number;
      oldBrandName: string;
      newBrandName: string;
      podcastId: string | null;
    }>("/api/brands/mentions/bulk-update", {
      oldBrandName,
      newBrandId,
      newBrandName,
      podcastId,
    });
    return response.data;
  },

  async deleteAllContentMentions(contentId: string) {
    const response = await api.delete<{
      message: string;
      deletedMentions: number;
      contentId: string;
    }>(`/api/podcasts/content/${contentId}/mentions`);
    return response.data;
  },

  async deleteAllPodcastMonthMentions(podcastId: string, month: string) {
    const response = await api.delete<{
      message: string;
      deletedMentions: number;
      affectedContent: number;
      podcastId: string;
      month: string;
    }>(`/api/podcasts/${podcastId}/mentions/month/${month}`);
    return response.data;
  },

  async getPodcastContentAnalysis(
    podcastId: string,
    page = 1,
    limit = 20,
    month?: string,
    sortBy = "publishedAt",
    sortOrder = "desc",
    week?: string,
    programId?: string,
    reviewStatus?: "pending" | "ready" | "pending-mentions",
    search?: string,
    digital?: "digital" | "non-digital",
    transcribed?: "yes" | "no",
    analysis?: "analyzed" | "not-analyzed",
  ) {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      sortBy,
      sortOrder,
    });

    if (search) {
      params.append("search", search);
    }

    if (digital) {
      params.append("digital", digital);
    }

    if (transcribed) {
      params.append("transcribed", transcribed);
    }

    if (analysis) {
      params.append("analysis", analysis);
    }

    if (month) {
      params.append("month", month);
    }

    if (week) {
      params.append("week", week);
    }

    if (programId) {
      params.append("programId", programId);
    }

    if (reviewStatus) {
      params.append("reviewStatus", reviewStatus);
    }

    const response = await api.get<{
      content: ContentItem[];
      pagination: { page: number; limit: number; total: number; pages: number };
    }>(`/api/podcasts/${podcastId}/content-analysis?${params}`);
    return response.data;
  },

  async getContentDateRange(podcastId: string) {
    const response = await api.get<{
      earliest: string | null;
      latest: string | null;
    }>(`/api/podcasts/${podcastId}/content-date-range`);
    return response.data;
  },

  async getProgramsAnalysis(
    week?: string,
    taxonomy: PodcastTaxonomyFilters = {},
    podcastId?: string,
    sortBy?: string,
    sortOrder?: "asc" | "desc",
    startDate?: string,
    endDate?: string,
    skipPrevWeek = false,
    minContentCount?: number,
    worldCupOnly = false,
    tvDigitalPrograms = false,
    mixDigitalTv = false,
  ) {
    const params = new URLSearchParams();

    if (week) {
      params.append("week", week);
    }

    appendPodcastTaxonomyParams(params, taxonomy);

    if (podcastId) {
      params.append("podcastId", podcastId);
    }

    if (sortBy) {
      params.append("sortBy", sortBy);
    }

    if (sortOrder) {
      params.append("sortOrder", sortOrder);
    }

    if (startDate) {
      params.append("startDate", startDate);
    }

    if (endDate) {
      params.append("endDate", endDate);
    }

    if (skipPrevWeek) {
      params.append("skipPrevWeek", "true");
    }

    if (minContentCount !== undefined && minContentCount > 0) {
      params.append("minContentCount", minContentCount.toString());
    }

    if (worldCupOnly) {
      params.append("worldCupOnly", "true");
    }

    if (tvDigitalPrograms) {
      params.append("tvDigitalPrograms", "true");
    }

    if (mixDigitalTv) {
      params.append("mixDigitalTv", "true");
    }

    const response = await api.get<{
      programs: {
        _id: string;
        name: string;
        imageS3Key?: string;
        imageUpdatedAt?: string | null;
        updatedAt?: string | null;
        podcast: {
          _id: string;
          title: string;
          socialAccounts?: { instagram?: string; tiktok?: string };
          socialMetrics?: {
            youtube?: { subscriberCount?: number };
            instagram?: { followerCount?: number };
            tiktok?: { followerCount?: number };
          };
        };
        /** Episodes with YouTube duration over 5 min in the period. */
        contentCount: number;
        /** Sum of views per episode from latest `dailyMetrics` snapshot (fallback: day7, then insights/views). */
        totalViews: number;
        /** Same view basis as totalViews (latest snapshot) summed for long-form. */
        totalViewsLongForm?: number;
        totalLikes: number;
        totalComments: number;
        totalInteractions: number;
        avgViews: number;
        /** Mean per-episode views at ~7 days after publish (dailyMetrics window + fallbacks). */
        avgViewsDay7: number;
        /** Sum of per-episode views at ~7 days after publish. */
        totalViewsDay7?: number;
        avgViewsAllTime: number;
        avgInteractions: number;
        /** Mean likes+comments at ~7 days after publish per long-form episode. */
        avgInteractionsDay7: number;
        /** Sum of likes+comments at ~7 days after publish across long-form episodes. */
        totalInteractionsDay7?: number;
        engagementRate: number;
        uniquePaidBrandsCount: number;
        /** Paid mentions excluding curator-rejected feedback. */
        paidMentionsCount?: number;
        /** Distinct content with ≥1 paid, non-rejected mention. */
        contentWithMentionsCount?: number;
        dateRange: {
          earliest: string;
          latest: string;
        };
        rankingPosition?: number;
        positionLastWeek?: number | null;
        avgViewsLastWeek?: number | null;
      }[];
      totalUniqueBrands: number;
      /** Paid mentions with linked brandId, excluding curator-rejected feedback. */
      totalPaidMentions?: number;
      /** Distinct content with ≥1 paid, non-rejected mention. */
      totalContentWithMentions?: number;
      /** Distinct programs enrolled in the ranking ecosystem for the period. */
      activeProgramCount?: number;
    }>(`/api/programs/analysis?${params}`);
    return response.data;
  },

  async getLinkedInLivestreamCompare(ids: string[], night?: "last") {
    const params = new URLSearchParams();
    params.set("ids", ids.join(","));
    if (night === "last") params.set("night", "last");
    const response = await api.get<{
      streams: Array<{
        id: string;
        title: string;
        programName: string | null;
        channelName: string;
        channelImageS3Key: string | null;
        channelImageUrl: string | null;
        channelColor: string | null;
        startedAt: string | null;
        avgViewers: number;
        peakViewers: number;
        viewsDay7: number | null;
        viewsAsOf: string | null;
        chatMessages: number | null;
        uniqueChatUsers: number | null;
        minutes: Array<{ minute: number; viewers: number; chat?: number }>;
      }>;
    }>(`/api/programs/linkedin-livestream-compare?${params}`);
    return response.data;
  },

  async getLinkedInLivestreamBrands(id: string) {
    const params = new URLSearchParams();
    params.set("id", id);
    const response = await api.get<{
      stream: {
        id: string;
        title: string;
        programName: string | null;
        channelName: string;
        channelImageS3Key: string | null;
        channelImageUrl: string | null;
        channelColor: string | null;
        startedAt: string | null;
        avgViewers: number;
        peakViewers: number;
        viewsDay7: number | null;
        viewsAsOf: string | null;
        chatMessages: number | null;
        uniqueChatUsers: number | null;
        minutes: Array<{ minute: number; viewers: number; chat?: number }>;
      };
      events: Array<{
        id: string;
        brandId: string | null;
        brandName: string;
        bucket: "content" | "physical" | "banner" | "credits";
        minute: number;
        mentionType: "paid" | "organic" | "neutral" | null;
        s3Key?: string | null;
        logoUrl?: string | null;
      }>;
      brands: Array<{
        brandId: string | null;
        brandName: string;
        firstMinute: number;
        counts: { content: number; physical: number; banner: number; credits: number };
        total: number;
        s3Key?: string | null;
        logoUrl?: string | null;
      }>;
    }>(`/api/programs/linkedin-livestream-brands?${params}`);
    return response.data;
  },

  async getLinkedInBrandRadiography() {
    const response = await api.get<{
      brandId: string;
      brandName: string;
      startDate: string;
      endDate: string;
      viewsAsOf: string;
      viewsAsOfLabel: string;
      days: Array<{ date: string; label: string }>;
      lanes: Array<{
        id: string;
        kind: "channel" | "rest";
        channelName: string;
        programNames: string;
        channelImageS3Key: string | null;
        channelImageUrl: string | null;
        channelColor: string | null;
        highlighted: boolean;
        views: number | null;
        episodeCount: number;
        mentions: { content: number; physical: number; banner: number; credits: number };
        days: Array<{
          content: number;
          physical: number;
          banner: number;
          credits: number;
          snapshotUrl: string | null;
          snapshotMentionId: string | null;
        }>;
      }>;
      debug: {
        mentionsBeforeExclusion: number;
        mentionsAfterExclusion: number;
        excludedIds: string[];
        perChannelCounts: Array<{ channelName: string; mentions: number }>;
      };
    }>(`/api/programs/linkedin-brand-radiography`);
    return response.data;
  },

  async getFifaClassification(params: {
    week?: string;
    year?: string;
    startDate?: string;
    endDate?: string;
    podcastId?: string;
    source?: "content" | "streams" | "both";
    page?: number;
    pageSize?: number;
    includeRejected?: boolean;
    contentSortBy?: string;
    contentSortOrder?: "asc" | "desc";
    streamSortBy?: string;
    streamSortOrder?: "asc" | "desc";
  }) {
    const q = new URLSearchParams();
    if (params.week) q.append("week", params.week);
    if (params.year) q.append("year", params.year);
    if (params.startDate) q.append("startDate", params.startDate);
    if (params.endDate) q.append("endDate", params.endDate);
    if (params.podcastId) q.append("podcastId", params.podcastId);
    if (params.source) q.append("source", params.source);
    if (params.page != null) q.append("page", String(params.page));
    if (params.pageSize != null) q.append("pageSize", String(params.pageSize));
    if (params.includeRejected) q.append("includeRejected", "true");
    if (params.contentSortBy) q.append("contentSortBy", params.contentSortBy);
    if (params.contentSortOrder) q.append("contentSortOrder", params.contentSortOrder);
    if (params.streamSortBy) q.append("streamSortBy", params.streamSortBy);
    if (params.streamSortOrder) q.append("streamSortOrder", params.streamSortOrder);

    const response = await api.get<{
      content?: {
        items: Array<{
          contentId: string;
          title: string;
          description?: string | null;
          publishedAt?: string | null;
          programId?: string | null;
          programName?: string | null;
          podcastId?: string | null;
          podcastTitle?: string | null;
          isFifaWorldCup: boolean;
          llmReason: string;
          updatedAt?: string | null;
          url?: string | null;
          views?: number;
          ranking?: number;
        }>;
        total: number;
        page: number;
        pageSize: number;
      };
      streams?: {
        items: Array<{
          streamId: string;
          title: string;
          description?: string | null;
          actualStartTime?: string | null;
          channelId?: string | null;
          channelName?: string | null;
          programId?: string | null;
          programName?: string | null;
          isFifaWorldCup: boolean;
          llmReason: string;
          updatedAt?: string | null;
          avgViewers?: number | null;
          peakViewers?: number | null;
          url?: string | null;
          ranking?: number;
        }>;
        total: number;
        page: number;
        pageSize: number;
      };
    }>(`/api/programs/fifa-classification?${q}`);
    return response.data;
  },

  async getProgramBrands(
    programId: string,
    dateQuery?: {
      week?: string;
      startDate?: string;
      endDate?: string;
      year?: string;
    },
  ) {
    const params = new URLSearchParams();

    if (dateQuery?.week) {
      params.append("week", dateQuery.week);
    }
    if (dateQuery?.startDate) {
      params.append("startDate", dateQuery.startDate);
    }
    if (dateQuery?.endDate) {
      params.append("endDate", dateQuery.endDate);
    }
    if (dateQuery?.year) {
      params.append("year", dateQuery.year);
    }

    const response = await api.get<{
      brands: Array<{
        _id: string;
        name: string;
        s3Key?: string;
        logoUrl?: string;
        category?: string;
        mentionCount: number;
      }>;
    }>(`/api/programs/${programId}/brands?${params}`);
    return response.data;
  },

  async getChannelsSocialAnalytics(
    week?: string,
    taxonomy: PodcastTaxonomyFilters = {},
    podcastId?: string,
    year?: string,
    startDate?: string,
    endDate?: string,
  ) {
    const params = new URLSearchParams();
    if (week) params.append("week", week);
    if (year) params.append("year", year);
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    appendPodcastTaxonomyParams(params, taxonomy);
    if (podcastId && podcastId !== "all") params.append("podcastId", podcastId);
    const response = await api.get<{
      channels: Array<{
        _id: string;
        name: string;
        imageS3Key?: string;
        imageUrl?: string;
        totalViews?: number;
        contentCount?: number;
      }>;
    }>(`/api/programs/analytics/channels/social?${params}`);
    return response.data;
  },

  async getBrandsTopByViews(
    week?: string,
    taxonomy: PodcastTaxonomyFilters = {},
    podcastId?: string,
    year?: string,
    startDate?: string,
    endDate?: string,
    limit = 10,
  ) {
    const params = new URLSearchParams();
    if (week) params.append("week", week);
    if (year) params.append("year", year);
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    appendPodcastTaxonomyParams(params, taxonomy);
    if (podcastId && podcastId !== "all") params.append("podcastId", podcastId);
    if (limit) params.append("limit", String(limit));
    const response = await api.get<{
      brands: Array<{
        _id: string;
        name: string;
        imageS3Key?: string;
        category?: string;
        uniqueProgramCount?: number;
        totalPaidMentions?: number;
        totalViews?: number;
      }>;
    }>(`/api/programs/analytics/brands/top-by-views?${params}`);
    return response.data;
  },

  async getChannelBrands(podcastId: string, week?: string) {
    const params = new URLSearchParams();
    if (week) {
      params.append("week", week);
    }
    const response = await api.get<{
      brands: Array<{
        _id: string;
        brandName: string;
        brandS3Key?: string;
        brandCategory?: string;
        mentionCount: number;
      }>;
    }>(`/api/programs/analytics/channels/${podcastId}/brands?${params}`);
    return response.data;
  },

  async getTopContentAnalytics(
    week?: string,
    taxonomy: PodcastTaxonomyFilters = {},
    podcastId?: string,
    limit = 100,
    year?: string,
    startDate?: string,
    endDate?: string,
    page = 1,
    search?: string,
  ) {
    const params = new URLSearchParams();

    if (week) {
      params.append("week", week);
    }

    if (year) {
      params.append("year", year);
    }

    if (startDate) {
      params.append("startDate", startDate);
    }

    if (endDate) {
      params.append("endDate", endDate);
    }

    appendPodcastTaxonomyParams(params, taxonomy);

    if (podcastId && podcastId !== "all") {
      params.append("podcastId", podcastId);
    }

    params.append("limit", limit.toString());
    params.append("skip", String((Math.max(1, page) - 1) * limit));

    if (search && search.trim()) {
      params.append("search", search.trim());
    }

    const response = await api.get<{
      content: Array<{
        _id: string;
        title: string;
        publishedAt: string;
        views: number;
        likes: number;
        comments: number;
        interactions: number;
        url?: string;
        source: string;
        mediaType: string;
        duration?: number;
        programId?: string;
        programName?: string;
        programImageS3Key?: string;
        podcastId?: string;
        podcastTitle?: string;
        podcastImageS3Key?: string;
        podcastImageUrl?: string;
        ranking: number;
      }>;
      total: number;
    }>(`/api/programs/analytics/top-content?${params}`);
    return response.data;
  },

  async getContentDailyMetrics(contentId: string): Promise<ContentDailyMetricsResponse> {
    const response = await api.get<ContentDailyMetricsResponse>(
      `/api/programs/analytics/content/${contentId}/daily-metrics`,
    );
    return response.data;
  },

  async getProgramEpisodesDailyMetrics(
    programId: string,
    params: { startDate?: string; endDate?: string },
  ) {
    const q = new URLSearchParams();
    if (params.startDate) q.set("startDate", params.startDate);
    if (params.endDate) q.set("endDate", params.endDate);
    const queryString = q.toString();
    const response = await api.get<{
      program: {
        _id: string;
        name: string;
        imageS3Key?: string;
        podcast: { _id: string; title: string; category?: string; type?: string } | null;
      };
      episodes: Array<{
        _id: string;
        title: string;
        publishedAt?: string;
        url?: string;
        thumbnail?: string;
        duration?: number;
        dailyMetrics: Array<{
          collectedAt: string;
          daysSincePublished?: number;
          views: number;
          likes: number;
          comments: number;
        }>;
      }>;
      filters: { startDate: string | null; endDate: string | null };
    }>(
      `/api/programs/${encodeURIComponent(programId)}/episodes-daily-metrics${queryString ? `?${queryString}` : ""}`,
    );
    return response.data;
  },

  async getTimeSeriesAnalytics(
    week?: string,
    taxonomy: PodcastTaxonomyFilters = {},
    podcastId?: string,
    programId?: string,
    year?: string,
    startDate?: string,
    endDate?: string,
    granularity: "daily" | "monthly" = "daily",
    minContentCount?: number,
    excludeSingleProgramBrands?: boolean,
    consistentProgramsOnly?: boolean,
  ) {
    const params = new URLSearchParams();

    if (week) {
      params.append("week", week);
    }

    if (year) {
      params.append("year", year);
    }

    if (startDate) {
      params.append("startDate", startDate);
    }

    if (endDate) {
      params.append("endDate", endDate);
    }

    appendPodcastTaxonomyParams(params, taxonomy);

    if (podcastId) {
      params.append("podcastId", podcastId);
    }

    if (programId) {
      params.append("programId", programId);
    }

    params.append("granularity", granularity);

    if (minContentCount !== undefined && minContentCount > 0) {
      params.append("minContentCount", minContentCount.toString());
    }

    if (excludeSingleProgramBrands) {
      params.append("excludeSingleProgramBrands", "true");
    }

    if (consistentProgramsOnly) {
      params.append("consistentProgramsOnly", "true");
    }

    const response = await api.get<{
      timeSeries: Array<{
        date: string;
        contentCount: number;
        totalViews: number;
        avgViews: number;
        totalInteractions: number;
        avgInteractions: number;
        paidBrandsCount: number;
        engagementRate: number;
      }>;
      granularity: string;
    }>(`/api/programs/analytics/time-series?${params}`);
    return response.data;
  },

  async getBrandsAnalyticsByPrograms(
    week?: string,
    taxonomy: PodcastTaxonomyFilters = {},
    podcastId?: string,
    year?: string,
    startDate?: string,
    endDate?: string,
    minContentCount?: number,
    limit?: number,
    search?: string,
    source?: string,
  ) {
    const params = new URLSearchParams();

    if (week) {
      params.append("week", week);
    }

    if (year) {
      params.append("year", year);
    }

    if (startDate) {
      params.append("startDate", startDate);
    }

    if (endDate) {
      params.append("endDate", endDate);
    }

    appendPodcastTaxonomyParams(params, taxonomy);

    if (podcastId) {
      params.append("podcastId", podcastId);
    }

    if (minContentCount !== undefined && minContentCount > 0) {
      params.append("minContentCount", minContentCount.toString());
    }

    if (limit !== undefined && limit > 0) {
      params.append("limit", limit.toString());
    }

    if (search) {
      params.append("search", search);
    }

    if (source) {
      params.append("source", source);
    }

    const response = await api.get<{
      brands: Array<{
        _id: string;
        name?: string;
        brandName?: string;
        s3Key?: string;
        brandS3Key?: string;
        logoUrl?: string;
        category?: string;
        brandCategory?: string;
        programCount?: number;
        totalPrograms?: number;
        totalPaidMentions: number;
        totalViews: number;
        totalAccumulatedViews: number;
        programs?: Array<{
          _id: string;
          name: string;
          imageS3Key?: string;
          podcast?: {
            _id: string;
            title: string;
          };
          paidMentionsCount: number;
          totalViews: number;
          accumulatedViews: number;
        }>;
        channels?: Array<{
          podcastId: string;
          podcastTitle: string;
          programCount?: number;
          totalPaidMentions?: number;
          totalViews?: number;
          totalAccumulatedViews?: number;
          programs?: Array<{
            _id: string;
            name: string;
            imageS3Key?: string;
            paidMentionsCount?: number;
            accumulatedViews?: number;
            totalViews?: number;
          }>;
          mentions?: Array<{
            _id: string;
            contentTitle?: string;
            contentPublishedAt?: string;
            contentUrl?: string;
            programName?: string;
            confidence?: number;
            segmentStart?: number;
          }>;
        }>;
      }>;
      totalBrands: number;
      totalPrograms: number;
      totalPaidMentions: number;
    }>(`/api/programs/analytics/brands?${params}`);
    return response.data;
  },

  async getBrandAnalyticsChannelMentions(
    brandId: string,
    week?: string,
    taxonomy: PodcastTaxonomyFilters = {},
    podcastId?: string,
    year?: string,
    startDate?: string,
    endDate?: string,
  ) {
    const params = new URLSearchParams();
    if (week) params.append("week", week);
    if (year) params.append("year", year);
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    appendPodcastTaxonomyParams(params, taxonomy);
    if (podcastId) params.append("podcastId", podcastId);

    const response = await api.get<{
      mentions: Array<{
        _id: string;
        contentId?: string;
        contentTitle?: string;
        contentPublishedAt?: string;
        contentUrl?: string;
        programName?: string;
        confidence?: number;
        segmentStart?: number;
      }>;
    }>(`/api/programs/analytics/brands/${brandId}/mentions?${params}`);
    return response.data;
  },

  async getBrandCategoryTopRankings(
    week?: string,
    taxonomy: PodcastTaxonomyFilters = {},
    podcastId?: string,
    year?: string,
    startDate?: string,
    endDate?: string,
    minContentCount?: number,
    sortBy = "totalPaidMentions",
    order: "asc" | "desc" = "desc",
  ) {
    const params = new URLSearchParams();

    if (week) {
      params.append("week", week);
    }

    if (year) {
      params.append("year", year);
    }

    if (startDate) {
      params.append("startDate", startDate);
    }

    if (endDate) {
      params.append("endDate", endDate);
    }

    appendPodcastTaxonomyParams(params, taxonomy);

    if (podcastId) {
      params.append("podcastId", podcastId);
    }

    if (minContentCount !== undefined && minContentCount > 0) {
      params.append("minContentCount", minContentCount.toString());
    }

    if (sortBy) {
      params.append("sortBy", sortBy);
    }

    if (order) {
      params.append("order", order);
    }

    const response = await api.get<{
      rankings: Array<{
        category: string;
        topBrand: {
          _id: string;
          name: string;
          brandS3Key?: string;
          category?: string;
          programCount: number;
          totalPaidMentions: number;
          totalViews: number;
          totalAccumulatedViews: number;
        };
        categoryTotals: {
          totalPrograms: number;
          totalPaidMentions: number;
          totalViews: number;
          totalAccumulatedViews: number;
          brandCount: number;
        };
        allBrands?: Array<{
          _id: string;
          name: string;
          brandS3Key?: string;
          category?: string;
          programCount: number;
          totalPaidMentions: number;
          totalViews: number;
          totalAccumulatedViews: number;
        }>;
      }>;
      sortBy: string;
      order: string;
      filters: any;
    }>(`/api/programs/analytics/brands/category-rankings?${params}`);
    return response.data;
  },

  async getGuestAnalytics(
    week?: string,
    taxonomy: PodcastTaxonomyFilters = {},
    podcastId?: string,
    year?: string,
    startDate?: string,
    endDate?: string,
    minContentCount?: number,
  ) {
    const params = new URLSearchParams();

    if (week) {
      params.append("week", week);
    }

    if (year) {
      params.append("year", year);
    }

    if (startDate) {
      params.append("startDate", startDate);
    }

    if (endDate) {
      params.append("endDate", endDate);
    }

    appendPodcastTaxonomyParams(params, taxonomy);

    if (podcastId) {
      params.append("podcastId", podcastId);
    }

    if (minContentCount !== undefined && minContentCount > 0) {
      params.append("minContentCount", minContentCount.toString());
    }

    const response = await api.get<{
      guests: Array<{
        _id: string;
        guestName: string;
        imageS3Key?: string;
        socialAccounts?: Array<{
          platform: string;
          handle: string;
        }>;
        appearances: number;
        totalViews: number;
        avgViews: number;
        totalLikes: number;
        totalComments: number;
        engagementRate: number;
        baselineAvgViews: number | null;
        viewsImpact: number | null;
        program: {
          _id: string;
          name: string;
          imageS3Key?: string;
          role?: "guest" | "host";
        } | null;
        podcast: {
          _id: string;
          title: string;
        } | null;
        topContent: Array<{
          _id: string;
          title: string;
          views: number;
          viewsAtDay7?: number;
          publishedAt: string;
        }>;
      }>;
    }>(`/api/guests/analytics?${params}`);
    return response.data;
  },

  async assignGuestToProgram(
    guestId: string,
    programId: string,
    role: "guest" | "host",
  ) {
    const response = await api.post<{ success: boolean; message: string }>(
      `/api/guests/${guestId}/assign-program`,
      { programId, role },
    );
    return response.data;
  },

  async removeGuestFromProgram(guestId: string, programId: string) {
    const response = await api.delete<{ success: boolean }>(
      `/api/guests/${guestId}/programs/${programId}`,
    );
    return response.data;
  },

  // Note: This now returns content items from the content collection, but maintains Episode interface for compatibility
  async getPodcastEpisodes(
    podcastId: string,
    page: number = 1,
    limit: number = 20,
  ) {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    const response = await api.get<PodcastEpisodesResponse>(
      `/api/podcasts/${podcastId}/episodes?${params.toString()}`,
    );
    return response.data;
  },

  async getPodcastSponsors(podcastId: string) {
    const response = await api.get(`/api/podcasts/${podcastId}/sponsors`);
    return response.data;
  },

  async getPodcastAnalyticsQ1() {
    const response = await api.get<
      Record<string, { views: number; episodeCount: number }>
    >("/api/podcasts/analytics/q1");
    return response.data;
  },

  async getMyPodcasts(page = 1, limit = 10, search?: string, sortBy?: string) {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (search) params.append("search", search);
    if (sortBy) params.append("sortBy", sortBy);

    const response = await api.get<PaginatedResponse<Podcast>>(
      `/api/podcasts/my-podcasts?${params}`,
    );
    return response.data;
  },

  // Episodes
  async getEpisodes(page = 1, limit = 10, search?: string, podcastId?: string) {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (search) params.append("search", search);
    if (podcastId) params.append("podcastId", podcastId);

    const response = await api.get<PaginatedResponse<Episode>>(
      `/api/episodes?${params}`,
    );
    return response.data;
  },

  async getEpisode(id: string) {
    const response = await api.get<Episode>(`/api/episodes/${id}`);
    return response.data;
  },

  async getEpisodeSponsorMentions(
    episodeId: string,
  ): Promise<SponsorMention[]> {
    const response = await api.get<PaginatedResponse<SponsorMention>>(
      `/api/episodes/${episodeId}/sponsor-mentions?limit=100`,
    );
    return response.data.sponsors || [];
  },

  async getMentionFrames(mentionId: string): Promise<SponsorFrame> {
    const response = await api.get<SponsorFrame>(
      `/api/sponsor-mentions/${mentionId}/frames`,
    );
    return response.data;
  },

  async getSponsorDetails(sponsorId: string): Promise<SponsorDetails> {
    const response = await api.get<SponsorDetails>(
      `/api/podcasts/sponsors/${sponsorId}`,
    );
    return response.data;
  },

  // Sponsors
  async getSponsors(page = 1, limit = 10, search?: string, category?: string) {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (search) params.append("search", search);
    if (category) params.append("category", category);

    const response = await api.get<PaginatedResponse<Sponsor>>(
      `/api/sponsors?${params}`,
    );
    return response.data;
  },

  async getSponsor(id: string) {
    const response = await api.get<Sponsor>(`/api/sponsors/${id}`);
    return response.data;
  },

  async getAllSponsors() {
    const response = await api.get<Sponsor[]>("/api/sponsors/all");
    return response.data;
  },

  async getSponsorsWithQ1Mentions() {
    const response = await api.get<Sponsor[]>("/api/sponsors/q1-mentions");
    return response.data;
  },

  // Dashboard
  async getDashboardStats() {
    const response = await api.get<DashboardStats>("/api/analytics/dashboard");
    return response.data;
  },

  // Admin
  async getAdminPodcasts(page = 1, limit = 10) {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    const response = await api.get<PaginatedResponse<Podcast>>(
      `/api/admin/podcasts?${params}`,
    );
    return response.data;
  },

  async getAdminPrograms(options?: {
    page?: number;
    limit?: number;
    search?: string;
    hasImage?: string;
    hidden?: string;
    isActive?: string;
    sort?: string;
    kind?: "all" | AdminProgramKind;
  }): Promise<{
    programs: AdminProgram[];
    pagination: { page: number; limit: number; total: number; pages: number };
  }> {
    const params = new URLSearchParams();
    params.set("page", String(options?.page ?? 1));
    params.set("limit", String(options?.limit ?? 50));
    if (options?.search?.trim()) params.set("search", options.search.trim());
    if (options?.hasImage && options.hasImage !== "all") {
      params.set("hasImage", options.hasImage);
    }
    if (options?.hidden && options.hidden !== "all") {
      params.set("hidden", options.hidden);
    }
    if (options?.isActive && options.isActive !== "all") {
      params.set("isActive", options.isActive);
    }
    if (options?.sort) params.set("sort", options.sort);
    if (options?.kind && options.kind !== "all") params.set("kind", options.kind);
    const response = await api.get<{
      programs: AdminProgram[];
      pagination: { page: number; limit: number; total: number; pages: number };
    }>(`/api/admin/programs?${params}`);
    return response.data;
  },

  async getAdminProgram(programId: string): Promise<{ program: AdminProgram }> {
    const response = await api.get<{ program: AdminProgram }>(
      `/api/admin/programs/${programId}`,
    );
    return response.data;
  },

  async getAdminProgramAssignments(programId: string): Promise<{
    kind: AdminProgramKind;
    total: number;
    limit: number;
    items: AdminProgramAssignment[];
  }> {
    const response = await api.get<{
      kind: AdminProgramKind;
      total: number;
      limit: number;
      items: AdminProgramAssignment[];
    }>(`/api/admin/programs/${programId}/assignments`);
    return response.data;
  },

  async patchAdminProgram(
    programId: string,
    payload: AdminProgramPatch,
  ): Promise<{ program: AdminProgram }> {
    const response = await api.patch<{ program: AdminProgram }>(
      `/api/admin/programs/${programId}`,
      payload,
    );
    return response.data;
  },

  async uploadAdminProgramImage(
    programId: string,
    payload:
      | { imageBase64: string; contentType?: string; fileName?: string }
      | { imageUrl: string },
  ): Promise<{ program: AdminProgram }> {
    const response = await api.post<{ program: AdminProgram }>(
      `/api/admin/programs/${programId}/image`,
      payload,
    );
    return response.data;
  },

  async getAdminChannels(options?: {
    page?: number;
    limit?: number;
    search?: string;
    category?: string;
    type?: string;
  }) {
    const params = new URLSearchParams();
    params.set("page", String(options?.page ?? 1));
    params.set("limit", String(options?.limit ?? 50));
    if (options?.search?.trim()) params.set("search", options.search.trim());
    if (options?.category && options.category !== "all") {
      params.set("category", options.category);
    }
    if (options?.type && options.type !== "all") {
      params.set("type", options.type);
    }

    const response = await api.get<{
      channels: AdminChannelRow[];
      pagination: { page: number; limit: number; total: number; pages: number };
    }>(`/api/admin/channels?${params}`);
    return response.data;
  },

  async createAdminChannel(payload: AdminChannelCreatePayload) {
    const response = await api.post<{ channel: AdminChannelRow }>(
      `/api/admin/channels`,
      payload,
    );
    return response.data;
  },

  async patchAdminChannelTaxonomy(
    id: string,
    payload: Pick<AdminChannelPatchPayload, "category" | "type">,
  ) {
    const response = await api.patch<{ channel: AdminChannelRow }>(
      `/api/admin/channels/${id}`,
      payload,
    );
    return response.data;
  },

  async getAdminMediaChannels(options?: {
    page?: number;
    limit?: number;
    search?: string;
    category?: string;
    type?: string;
    source?: string;
    hasLivestream?: string;
    platform?: string;
    hasPlatforms?: string[];
    missingPlatforms?: string[];
    accountCheck?: string;
  }) {
    const params = new URLSearchParams();
    params.set("page", String(options?.page ?? 1));
    params.set("limit", String(options?.limit ?? 200));
    if (options?.search?.trim()) params.set("search", options.search.trim());
    if (options?.category && options.category !== "all") {
      params.set("category", options.category);
    }
    if (options?.type && options.type !== "all") {
      params.set("type", options.type);
    }
    if (options?.source && options.source !== "all") {
      params.set("source", options.source);
    }
    if (options?.hasLivestream && options.hasLivestream !== "all") {
      params.set("hasLivestream", options.hasLivestream);
    }
    if (options?.platform && options.platform !== "all") {
      params.set("platform", options.platform);
    }
    if (options?.hasPlatforms?.length) {
      params.set("hasPlatforms", options.hasPlatforms.join(","));
    }
    if (options?.missingPlatforms?.length) {
      params.set("missingPlatforms", options.missingPlatforms.join(","));
    }
    if (options?.accountCheck && options.accountCheck !== "all") {
      params.set("accountCheck", options.accountCheck);
    }

    const response = await api.get<{
      channels: MediaChannelRow[];
      pagination: { page: number; limit: number; total: number; pages: number };
    }>(`/api/admin/media-channels?${params}`);
    return response.data;
  },

  async createAdminMediaChannel(youtubeChannelId: string) {
    const response = await api.post<{
      channelId: string;
      podcastId: string;
      livestreamChannelId: string;
      podcastCreated: boolean;
      livestreamCreated: boolean;
    }>(`/api/admin/media-channels`, { youtubeChannelId }, { timeout: 60000 });
    return response.data;
  },

  async patchAdminMediaChannel(
    id: string,
    payload: {
      name: string;
      slug: string;
      description: string;
      type: "tv" | "noticias" | "radio" | "digital";
      category: "politics" | "other";
      trackLivestream: boolean;
      youtubeChannelId: string;
      youtubeUsername: string;
      instagramUsername: string;
      facebookUsername: string;
      tiktokUsername: string;
      spotifyId: string;
    },
  ) {
    const response = await api.patch<{
      channel: MediaChannelDetail;
      summary: MediaChannelRow;
    }>(`/api/admin/media-channels/${id}`, payload);
    return response.data;
  },

  async addAdminMediaChannelPodcast(id: string) {
    const response = await api.post<{ podcastId: string; channelId: string }>(
      `/api/admin/media-channels/${id}/podcast`,
      {},
      { timeout: 60000 },
    );
    return response.data;
  },

  async syncAdminMediaChannelImages(id: string) {
    const response = await api.post<{
      results: MediaChannelImageSyncResult[];
      channelImage: MediaChannelMainImageSyncResult;
      channel: MediaChannelDetail;
      summary: MediaChannelRow;
    }>(`/api/admin/media-channels/${id}/sync-images`, {}, { timeout: 300000 });
    return response.data;
  },

  async deleteAdminMediaChannel(id: string) {
    const response = await api.delete<{ deleted: boolean }>(`/api/admin/media-channels/${id}`);
    return response.data;
  },

  async removeAdminMediaChannelPlatform(
    id: string,
    platform: "instagram" | "facebook" | "tiktok" | "spotify",
  ) {
    const response = await api.delete<{ channel: MediaChannelDetail; summary: MediaChannelRow }>(
      `/api/admin/media-channels/${id}/platforms/${platform}`,
    );
    return response.data;
  },

  async searchAdminMediaChannelPlatform(
    id: string,
    platform: "instagram" | "facebook" | "tiktok" | "spotify",
  ) {
    const response = await api.post<MediaChannelAccountSearchResult>(
      `/api/admin/media-channels/${id}/platforms/${platform}/search`,
      {},
      { timeout: 60000 },
    );
    return response.data;
  },

  async discoverAdminMediaChannelAccounts(id: string) {
    const response = await api.post<{
      links: string[];
      results: MediaChannelAccountDiscoveryResult[];
      channel: MediaChannelDetail;
      summary: MediaChannelRow;
    }>(`/api/admin/media-channels/${id}/discover-accounts`, {}, { timeout: 60000 });
    return response.data;
  },

  async getAdminMediaChannelAccountDiscovery() {
    const response = await api.get<{
      stats: MediaChannelAccountDiscoveryStats;
      job: MediaChannelAccountDiscoveryJob;
    }>("/api/admin/media-channels/account-discovery");
    return response.data;
  },

  async startAdminMediaChannelAccountDiscovery(scope: "unchecked" | "all") {
    const response = await api.post<{ job: MediaChannelAccountDiscoveryJob }>(
      "/api/admin/media-channels/account-discovery/run",
      { scope },
    );
    return response.data;
  },

  async stopAdminMediaChannelAccountDiscovery() {
    const response = await api.post<{ job: MediaChannelAccountDiscoveryJob }>(
      "/api/admin/media-channels/account-discovery/stop",
      {},
    );
    return response.data;
  },

  async getAdminMediaChannelDetail(id: string) {
    const response = await api.get<{
      channel: MediaChannelDetail;
      summary: MediaChannelRow;
      programs: MediaChannelProgram[];
      podcastPrograms: MediaChannelPodcastProgram[];
      livestreamPrograms: MediaChannelLivestreamProgram[];
    }>(`/api/admin/media-channels/${id}`);
    return response.data;
  },

  async getAdminMediaChannelPrograms(id: string) {
    const response = await api.get<{
      programs: MediaChannelProgramRow[];
      podcastPrograms: MediaChannelLinkablePodcastProgram[];
      livestreamPrograms: MediaChannelLinkableLivestreamProgram[];
      availableKinds: MediaChannelProgramKind[];
      legacyPodcastId: string | null;
      legacyLivestreamChannelId: string | null;
    }>(`/api/admin/media-channels/${id}/programs`);
    return response.data;
  },

  async getAdminMediaChannelProgramSuggestions(
    id: string,
    links: { podcastProgramId?: string; livestreamProgramId?: string },
  ) {
    const response = await api.get<{
      aliases: MediaChannelProgramSuggestion[];
      hashtags: MediaChannelProgramSuggestion[];
    }>(`/api/admin/media-channels/${id}/programs/suggestions`, { params: links });
    return response.data;
  },

  async getAdminMediaChannelProgramContent(
    id: string,
    program:
      | { channelProgramId: string }
      | { podcastProgramId: string }
      | { livestreamProgramId: string },
    /** Searches title / description across all of the channel's videos and streams. */
    q?: string,
  ) {
    const response = await api.get<{
      content: Array<{
        _id: string;
        title: string;
        url: string | null;
        source: string | null;
        publishedAt: string | null;
        durationSeconds: number | null;
        channelProgramId: string | null;
        channelProgramManual: boolean;
      }>;
      contentTotal: number;
      streams: Array<{
        _id: string;
        title: string;
        url: string | null;
        status: string | null;
        startedAt: string | null;
        durationSeconds: number | null;
        channelProgramId: string | null;
        channelProgramManual: boolean;
      }>;
      streamTotal: number;
      limit: number;
    }>(`/api/admin/media-channels/${id}/program-content`, {
      params: { ...program, ...(q ? { q } : {}) },
    });
    return response.data;
  },

  async assignAdminMediaChannelProgramContent(
    id: string,
    payload: { contentIds: string[]; streamIds: string[]; channelProgramId: string | null },
  ) {
    const response = await api.post<{ contentModified: number; streamsModified: number }>(
      `/api/admin/media-channels/${id}/program-content/assign`,
      payload,
    );
    return response.data;
  },

  async moveAdminMediaChannelProgramContent(
    id: string,
    payload: { kind: MediaChannelProgramKind; ids: string[]; targetProgramId: string },
  ) {
    const response = await api.post<{ movedCount: number; target: { _id: string; name: string } }>(
      `/api/admin/media-channels/${id}/program-content/move`,
      payload,
    );
    return response.data;
  },

  async createAdminMediaChannelProgram(id: string, payload: MediaChannelProgramInput) {
    const response = await api.post<{ program: MediaChannelProgramRow }>(
      `/api/admin/media-channels/${id}/programs`,
      payload,
    );
    return response.data;
  },

  async patchAdminMediaChannelProgram(
    id: string,
    programId: string,
    payload: Partial<MediaChannelProgramInput>,
  ) {
    const response = await api.patch<{ program: MediaChannelProgramRow }>(
      `/api/admin/media-channels/${id}/programs/${programId}`,
      payload,
    );
    return response.data;
  },

  async deleteAdminMediaChannelProgram(id: string, programId: string) {
    const response = await api.delete<{ programId: string }>(
      `/api/admin/media-channels/${id}/programs/${programId}`,
    );
    return response.data;
  },

  async mergeAdminMediaChannelLegacyProgram(
    id: string,
    payload: { kind: MediaChannelProgramKind; sourceId: string; targetId: string },
  ) {
    const response = await api.post<{
      movedCount: number;
      target: { _id: string; name: string };
    }>(`/api/admin/media-channels/${id}/legacy-programs/merge`, payload);
    return response.data;
  },

  async deleteAdminMediaChannelLegacyPrograms(
    id: string,
    payload: { podcastProgramIds: string[]; livestreamProgramIds: string[] },
  ) {
    const response = await api.post<{
      deletedCount: number;
      skipped: Array<{ _id: string; name: string; reason: string }>;
    }>(`/api/admin/media-channels/${id}/legacy-programs/delete`, payload);
    return response.data;
  },

  async mergeAdminMediaChannelProgram(id: string, programId: string, sourceProgramId: string) {
    const response = await api.post<{ program: MediaChannelProgramRow }>(
      `/api/admin/media-channels/${id}/programs/${programId}/merge`,
      { sourceProgramId },
    );
    return response.data;
  },

  async getAdminChannelDetail(id: string) {
    const response = await api.get<{ channel: AdminChannelRow }>(
      `/api/admin/channels/${id}`,
    );
    return response.data;
  },

  async getAdminChannelPrograms(
    channelId: string,
    options?: { allContent?: boolean; includeLivestream?: boolean },
  ) {
    const params = new URLSearchParams();
    if (options?.allContent) params.set("allContent", "1");
    if (options?.includeLivestream) params.set("includeLivestream", "1");
    const qs = params.toString();
    const response = await api.get<{
      contentYear: number | null;
      programs: AdminChannelProgramRow[];
      catalogPrograms: AdminChannelProgramCatalogRow[];
    }>(`/api/admin/channels/${channelId}/programs${qs ? `?${qs}` : ""}`);
    return response.data;
  },

  async getAdminChannelPlaylists(channelId: string) {
    const response = await api.get<{
      contentYear: number;
      playlists: AdminChannelPlaylistRow[];
    }>(`/api/admin/channels/${channelId}/playlists`);
    return response.data;
  },

  async assignAdminChannelPlaylistContent(
    channelId: string,
    payload: AdminChannelPlaylistAssignPayload,
  ) {
    const response = await api.post<{
      modifiedCount: number;
      playlistId: string;
      programId: string;
      programName: string;
      createdProgram: boolean;
    }>(`/api/admin/channels/${channelId}/playlists/assign`, payload);
    return response.data;
  },

  async getAdminChannelProgramContent(
    channelId: string,
    programId: string,
    options?: { allContent?: boolean },
  ) {
    const params = new URLSearchParams();
    if (options?.allContent) params.set("allContent", "1");
    const qs = params.toString();
    const response = await api.get<{
      contentYear: number | null;
      content: AdminChannelProgramContentRow[];
      total: number;
    }>(
      `/api/admin/channels/${channelId}/programs/${programId}/content${qs ? `?${qs}` : ""}`,
    );
    return response.data;
  },

  async patchAdminChannelContentProgram(
    channelId: string,
    contentId: string,
    programId: string | null,
  ) {
    const response = await api.patch<{
      success: boolean;
      contentId: string;
      programId: string | null;
    }>(`/api/admin/channels/${channelId}/content/${contentId}/program`, { programId });
    return response.data;
  },

  async patchAdminChannelSettings(id: string, payload: AdminChannelPatchPayload) {
    const response = await api.patch<{ channel: AdminChannelRow }>(
      `/api/admin/channels/${id}`,
      payload,
    );
    return response.data;
  },

  async uploadAdminChannelImage(
    channelId: string,
    payload:
      | { imageBase64: string; contentType?: string; fileName?: string }
      | { imageUrl: string },
  ): Promise<{ channel: AdminChannelRow }> {
    const response = await api.post<{ channel: AdminChannelRow }>(
      `/api/admin/channels/${channelId}/image`,
      payload,
    );
    return response.data;
  },

  async reassignAdminChannelProgramContent(
    channelId: string,
    payload: { fromProgramId: string; toProgramId: string },
  ) {
    const response = await api.post<{
      modifiedCount: number;
      fromProgramId: string;
      toProgramId: string;
    }>(`/api/admin/channels/${channelId}/programs/reassign`, payload);
    return response.data;
  },

  async createAdminChannelProgram(
    channelId: string,
    payload: AdminChannelProgramCreatePayload,
  ) {
    const response = await api.post<{ program: AdminChannelProgramRow }>(
      `/api/admin/channels/${channelId}/programs`,
      payload,
    );
    return response.data;
  },

  async patchAdminChannelProgram(
    channelId: string,
    programId: string,
    payload: AdminChannelProgramPatchPayload,
  ) {
    const response = await api.patch<{ program: AdminChannelProgramRow }>(
      `/api/admin/channels/${channelId}/programs/${programId}`,
      payload,
    );
    return response.data;
  },

  async deleteAdminChannelProgram(
    channelId: string,
    programId: string,
    payload?: { migrateToProgramId?: string; unlinkContent?: boolean },
  ) {
    const response = await api.delete<{
      programId: string;
      migratedContentCount: number;
      unlinkedContentCount: number;
    }>(`/api/admin/channels/${channelId}/programs/${programId}`, {
      data: payload,
    });
    return response.data;
  },

  // Companies
  async getMyCompany(): Promise<Company> {
    const response = await api.get<Company>("/api/companies/me");
    return response.data;
  },

  async getCompanyProfileMetrics(
    startDate: string,
    endDate: string,
    podcastId?: string,
  ): Promise<{
    hero: {
      totalViews: number;
      totalInteractions: number;
      contentCount: number;
      livestreamCount: number;
      uniqueBrandsCount: number;
    };
    channelPills: Array<{
      platform: "youtube" | "instagram" | "facebook" | "tiktok";
      label: string;
      handle?: string;
      profileUrl: string;
    }>;
    platforms: {
      youtube: null | {
        channelCount: number;
        views: number;
        followers: number;
        avgViews: number;
        engagementPercent: number;
        likes: number;
        comments: number;
        contentCount: number;
        channels: Array<{
          accountId: string;
          name: string;
          handle: string;
          views: number;
          likes: number;
          comments: number;
          followers: number;
        }>;
        detailRows: Array<{ label: string; value: string }>;
      };
      youtube_shorts: null | {
        channelCount: number;
        views: number;
        followers: number;
        avgViews: number;
        engagementPercent: number;
        likes: number;
        comments: number;
        contentCount: number;
        channels: Array<{
          accountId: string;
          name: string;
          handle: string;
          views: number;
          likes: number;
          comments: number;
          followers: number;
        }>;
        detailRows: Array<{ label: string; value: string }>;
      };
      instagram: null | {
        channelCount: number;
        views: number;
        followers: number;
        avgViews: number;
        engagementPercent: number;
        likes: number;
        comments: number;
        contentCount: number;
        channels: Array<{
          accountId: string;
          name: string;
          handle: string;
          views: number;
          likes: number;
          comments: number;
          followers: number;
        }>;
        detailRows: Array<{ label: string; value: string }>;
      };
      facebook: null | {
        channelCount: number;
        views: number;
        followers: number;
        avgViews: number;
        engagementPercent: number;
        likes: number;
        comments: number;
        contentCount: number;
        channels: Array<{
          accountId: string;
          name: string;
          handle: string;
          views: number;
          likes: number;
          comments: number;
          followers: number;
        }>;
        detailRows: Array<{ label: string; value: string }>;
      };
      tiktok: null | {
        channelCount: number;
        views: number;
        followers: number;
        avgViews: number;
        engagementPercent: number;
        likes: number;
        comments: number;
        contentCount: number;
        channels: Array<{
          accountId: string;
          name: string;
          handle: string;
          views: number;
          likes: number;
          comments: number;
          followers: number;
        }>;
        detailRows: Array<{ label: string; value: string }>;
      };
    };
    podscope: null | {
      streamCount: number;
      avgViewers: number;
      peakViewers: number;
      avgChatMessages: number;
      avgChatUniqueUsers: number;
    };
  }> {
    const params = new URLSearchParams();
    params.append("startDate", startDate);
    params.append("endDate", endDate);
    if (podcastId) params.append("podcastId", podcastId);
    const response = await api.get(
      `/api/companies/me/profile-metrics?${params.toString()}`,
    );
    return response.data;
  },

  async getCompanyProfilePrograms(
    startDate: string,
    endDate: string,
    podcastId?: string,
    options?: { recentEpisodesLimit?: number | "all" },
  ): Promise<{
    programs: Array<{
      programId: string;
      name: string;
      /** Podcast / channel title (YouTube channel name in product). */
      channelName: string;
      imageS3Key: string | null;
      updatedAt: string | null;
      platform_counts: {
        youtube: number;
        youtube_shorts: number;
        instagram: number;
        facebook: number;
        tiktok: number;
      };
      recentEpisodes: Array<{
        contentId: string;
        title: string;
        views: number;
        publishedAt: string | null;
        episodeNumber: number | null;
        platform: "youtube" | "youtube_shorts" | "instagram" | "facebook" | "tiktok" | null;
      }>;
    }>;
  }> {
    const params = new URLSearchParams();
    params.append("startDate", startDate);
    params.append("endDate", endDate);
    if (podcastId) params.append("podcastId", podcastId);
    if (options?.recentEpisodesLimit != null) {
      params.append(
        "recentEpisodesLimit",
        options.recentEpisodesLimit === "all"
          ? "all"
          : String(options.recentEpisodesLimit),
      );
    }
    const response = await api.get(
      `/api/companies/me/profile-programs?${params.toString()}`,
    );
    return response.data;
  },

  async getCompanyProfileBrands(
    startDate: string,
    endDate: string,
    podcastId?: string,
    options?: { recentAppearancesLimit?: number | "all"; brandKey?: string },
  ): Promise<CompanyProfileBrandsResponse> {
    const params = new URLSearchParams();
    params.append("startDate", startDate);
    params.append("endDate", endDate);
    if (podcastId) params.append("podcastId", podcastId);
    if (options?.brandKey) params.append("brandKey", options.brandKey);
    if (options?.recentAppearancesLimit != null) {
      params.append(
        "recentAppearancesLimit",
        options.recentAppearancesLimit === "all"
          ? "all"
          : String(options.recentAppearancesLimit),
      );
    }
    const response = await api.get(
      `/api/companies/me/profile-brands?${params.toString()}`,
    );
    return response.data;
  },

  /** Mi contenido home: metrics + programs + brands + social accounts (+ optional embedded ranking stats). */
  async getMiContenidoDashboard(
    startDate: string,
    endDate: string,
    podcastId?: string,
    options?: {
      recentEpisodesLimit?: number | "all";
      recentAppearancesLimit?: number | "all";
      /** Default false — ranking loads via batch endpoint when section is visible. */
      includeRankingStats?: boolean;
    },
  ) {
    const params = new URLSearchParams();
    params.append("startDate", startDate);
    params.append("endDate", endDate);
    if (podcastId) params.append("podcastId", podcastId);
    if (options?.includeRankingStats) {
      params.append("includeRankingStats", "true");
    }
    if (options?.recentEpisodesLimit != null) {
      params.append(
        "recentEpisodesLimit",
        options.recentEpisodesLimit === "all"
          ? "all"
          : String(options.recentEpisodesLimit),
      );
    }
    if (options?.recentAppearancesLimit != null) {
      params.append(
        "recentAppearancesLimit",
        options.recentAppearancesLimit === "all"
          ? "all"
          : String(options.recentAppearancesLimit),
      );
    }
    const response = await api.get(
      `/api/companies/me/mi-contenido-dashboard?${params.toString()}`,
    );
    return response.data;
  },

  /** Batch ranking stats for Mi contenido home program table (deferred load). */
  async getMiContenidoRankingStats(
    programIds: string[],
    startDate: string,
    endDate: string,
    youtubeLongFormMinDurationSec?: number,
  ) {
    const response = await api.post(
      "/api/companies/me/mi-contenido-ranking-stats",
      {
        programIds,
        startDate,
        endDate,
        ...(youtubeLongFormMinDurationSec != null
          ? { youtubeLongFormMinDurationSec }
          : {}),
      },
    );
    return response.data as {
      programStats: Record<string, { analytics: Record<string, unknown> }>;
    };
  },

  async getCompanyPlatformContent(
    platform: "youtube" | "youtube_shorts" | "instagram" | "facebook" | "tiktok" | "all",
    startDate: string,
    endDate: string,
    podcastId?: string,
    page?: number,
    limit?: number,
    options?: {
      programId?: string;
      brandKey?: string;
      socialAccountId?: string;
      /** When true, only `private_content` rows (no merge with public `content`). */
      privateOnly?: boolean;
      /** Text search on title/description (hashtags, keywords, captions). */
      q?: string;
      sortBy?: "recent" | "views" | "interactions" | "peakViewers" | "avgViewers";
    },
  ): Promise<{
    items: Array<{
      _id: string;
      title: string | null;
      url: string | null;
      thumbnail: string | null;
      imageS3Key: string | null;
      publishedAt: string | null;
      views: number;
      likes: number;
      comments: number;
      /** When the views/likes/comments snapshot was collected. */
      metricsUpdatedAt?: string | null;
      mediaType: string | null;
      podcastId: string;
      programId: string | null;
      programName: string | null;
      programImageS3Key: string | null;
      programImageUpdatedAt: string | null;
      podcastImageS3Key?: string | null;
      podcastImageUrl?: string | null;
      platformKey?: "youtube" | "youtube_shorts" | "instagram" | "facebook" | "tiktok";
      brands: Array<{ brandKey: string; name: string; logo: string | null }>;
      /** YouTube + matching `livestream_streams` row only; else omitted or null */
      livestreamAvgViewers?: number | null;
      livestreamPeakViewers?: number | null;
    }>;
    total: number;
    page: number;
    limit: number;
    /** True when scoped YouTube content has at least one livestream_streams match */
    hasLivestreamData?: boolean;
  }> {
    const params = new URLSearchParams();
    params.append("platform", platform);
    params.append("startDate", startDate);
    params.append("endDate", endDate);
    if (podcastId) params.append("podcastId", podcastId);
    if (options?.programId) params.append("programId", options.programId);
    if (options?.socialAccountId) {
      params.append("socialAccountId", options.socialAccountId);
    }
    if (options?.brandKey != null && String(options.brandKey).length > 0) {
      params.append("brandKey", String(options.brandKey));
    }
    if (page) params.append("page", String(page));
    if (limit) params.append("limit", String(limit));
    if (options?.privateOnly) params.append("privateOnly", "true");
    if (options?.q) params.append("q", options.q);
    if (options?.sortBy) params.append("sortBy", options.sortBy);
    const response = await api.get(
      `/api/companies/me/platform-content?${params.toString()}`,
    );
    return response.data;
  },

  async getMiContenidoPlatformContentDailyTrends(
    platform: "youtube" | "youtube_shorts" | "instagram" | "facebook" | "tiktok" | "all",
    startDate: string,
    endDate: string,
    podcastId?: string,
    options?: {
      programId?: string;
      socialAccountId?: string;
      q?: string;
    },
  ): Promise<{
    mode: "views_gained";
    granularity: "day";
    trends: Array<{
      date: string;
      youtubeLongViews: number;
      youtubeShortsViews: number;
      instagramViews: number;
      facebookViews: number;
      tiktokViews: number;
      youtubeLongInteractions: number;
      youtubeShortsInteractions: number;
      instagramInteractions: number;
      facebookInteractions: number;
      tiktokInteractions: number;
      podscopeAvgViewers: number;
      podscopePeakViewers: number;
    }>;
    dailyMetricsSince?: Partial<
      Record<
        "youtube" | "youtube_shorts" | "instagram" | "facebook" | "tiktok",
        string
      >
    >;
    contentWithMetricsCount: number;
    contentTotalCount: number;
  }> {
    const params = new URLSearchParams();
    params.append("platform", platform);
    params.append("startDate", startDate);
    params.append("endDate", endDate);
    if (podcastId) params.append("podcastId", podcastId);
    if (options?.programId) params.append("programId", options.programId);
    if (options?.socialAccountId) {
      params.append("socialAccountId", options.socialAccountId);
    }
    if (options?.q) params.append("q", options.q);
    const response = await api.get(
      `/api/companies/me/platform-content/daily-trends?${params.toString()}`,
    );
    return response.data;
  },

  async getUsageHistory(): Promise<{
    history: Array<{
      id: string;
      year: number;
      month: number;
      name?: string;
      type: "monthly" | "pack";
      limit: number;
      used: number;
      contentIds: string[];
      status: "active" | "invoiced" | "paid";
      createdAt: string;
    }>;
  }> {
    const response = await api.get("/api/companies/usage-history");
    return response.data;
  },
  async updateCompanyProducts(
    companyId: string,
    products: string[],
  ): Promise<{
    success: boolean;
    message: string;
    products: string[];
  }> {
    const response = await api.put(`/api/companies/${companyId}/products`, {
      products,
    });
    return response.data;
  },
  async updateCompanyPerUserPermissions(
    companyId: string,
    enablePerUserProductPermissions: boolean,
  ): Promise<{
    success: boolean;
    message: string;
    enablePerUserProductPermissions: boolean;
  }> {
    const response = await api.put(
      `/api/companies/${companyId}/per-user-permissions`,
      { enablePerUserProductPermissions },
    );
    return response.data;
  },
  async updateCompanyDateFilter(
    companyId: string,
    dateFilterMinStartDate: string | null,
    dateFilterMaxEndDate: string | null,
  ): Promise<{
    success: boolean;
    message: string;
    dateFilterMinStartDate: string | null;
    dateFilterMaxEndDate: string | null;
  }> {
    const response = await api.put(`/api/companies/${companyId}/date-filter`, {
      dateFilterMinStartDate,
      dateFilterMaxEndDate,
    });
    return response.data;
  },
  async updateCompanyMiContenidoDateFilter(
    companyId: string,
    miContenidoDateFilterMinStartDate: string | null,
    miContenidoDateFilterMaxEndDate: string | null,
  ): Promise<{
    success: boolean;
    message: string;
    miContenidoDateFilterMinStartDate: string | null;
    miContenidoDateFilterMaxEndDate: string | null;
  }> {
    const response = await api.put(
      `/api/companies/${companyId}/mi-contenido-date-filter`,
      {
        miContenidoDateFilterMinStartDate,
        miContenidoDateFilterMaxEndDate,
      },
    );
    return response.data;
  },
  async updateCompanyTrackerBrand(
    companyId: string,
    trackerBrandId: string | null,
  ): Promise<{
    success: boolean;
    message: string;
    trackerBrandId: string | null;
  }> {
    const response = await api.put(
      `/api/companies/${companyId}/tracker-brand`,
      { trackerBrandId },
    );
    return response.data;
  },
  async updateCompanyMiContenidoBrands(
    companyId: string,
    brandIds: string[],
  ): Promise<{
    success: boolean;
    message: string;
    miContenidoBrandIds: string[];
  }> {
    const response = await api.put(
      `/api/companies/${companyId}/mi-contenido-brands`,
      { brandIds },
    );
    return response.data;
  },
  async updateCompanyExplorerSponsorCategory(
    companyId: string,
    explorerSponsorCategoryFilter: string | null,
  ): Promise<{
    success: boolean;
    message: string;
    explorerSponsorCategoryFilter: string | null;
  }> {
    const response = await api.put(
      `/api/companies/${companyId}/explorer-sponsor-category`,
      { explorerSponsorCategoryFilter },
    );
    return response.data;
  },
  async updateCompanyExplorerBrandInvestment(
    companyId: string,
    explorerBrandInvestment: ExplorerBrandInvestment | null,
  ): Promise<{
    success: boolean;
    message: string;
    explorerBrandInvestment: ExplorerBrandInvestment | null;
  }> {
    const response = await api.put(
      `/api/companies/${companyId}/explorer-brand-investment`,
      { explorerBrandInvestment },
    );
    return response.data;
  },
  async updateCompanyComparadorPodcasts(
    companyId: string,
    podcastIds: string[],
  ): Promise<{
    success: boolean;
    message: string;
    comparadorPodcastIds: string[];
  }> {
    const response = await api.patch(
      `/api/companies/${companyId}/comparador-podcasts`,
      { podcastIds },
    );
    return response.data;
  },
  async updateCompanySidebarGuides(
    companyId: string,
    sidebarGuidesUrl: string | null,
    sidebarGuidesLabel: string | null,
  ): Promise<{
    success: boolean;
    message: string;
    sidebarGuidesUrl: string | null;
    sidebarGuidesLabel: string | null;
  }> {
    const response = await api.put(
      `/api/companies/${companyId}/sidebar-guides`,
      { sidebarGuidesUrl, sidebarGuidesLabel },
    );
    return response.data;
  },

  async uploadCompanyImage(
    companyId: string,
    payload: { imageBase64: string } | { imageUrl: string },
  ): Promise<{ success: boolean; imageS3Key: string; imageUrl: null }> {
    const response = await api.post(
      `/api/companies/${companyId}/image`,
      payload,
    );
    return response.data;
  },

  async deleteCompanyImage(
    companyId: string,
  ): Promise<{ success: boolean; imageS3Key: null; imageUrl: null }> {
    const response = await api.delete(`/api/companies/${companyId}/image`);
    return response.data;
  },

  async getCompanyChatProfile(companyId: string): Promise<CompanyChatProfile> {
    const response = await api.get(`/api/companies/${companyId}/chat-profile`);
    return response.data;
  },

  async updateCompanyChatProfile(
    companyId: string,
    profile: CompanyChatProfile,
  ): Promise<CompanyChatProfile> {
    const response = await api.put(`/api/companies/${companyId}/chat-profile`, profile);
    return response.data;
  },

  async getComparadorPodcastStats(
    podcastId: string,
    startDate: string,
    endDate: string,
  ): Promise<{
    platforms: {
      youtube: {
        followers: number | null;
        views: number;
        contentCount: number;
        shortsViews: number;
        shortsCount: number;
      } | null;
      instagram: {
        followers: number | null;
        views: number;
        contentCount: number;
      } | null;
      facebook: {
        followers: number | null;
        views: number;
        contentCount: number;
      } | null;
      tiktok: {
        followers: number | null;
        views: number;
        contentCount: number;
      } | null;
    };
    podscope: {
      streamCount: number;
      avgViewers: number;
      peakViewers: number;
    } | null;
    brands: Array<{
      name: string;
      logo: string | null;
      mention_count: number;
      presence_count: number;
      platform_counts?: {
        youtube: number;
        youtube_shorts: number;
        instagram: number;
        facebook: number;
        tiktok: number;
      };
    }>;
  }> {
    const response = await api.get(
      "/api/companies/me/comparador-podcast-stats",
      {
        params: { podcastId, startDate, endDate },
      },
    );
    return response.data;
  },
  async updateUserProducts(
    companyId: string,
    userId: string,
    products: string[],
  ): Promise<{
    success: boolean;
    message: string;
    products: string[];
  }> {
    const response = await api.put(
      `/api/companies/${companyId}/users/${userId}/products`,
      { products },
    );
    return response.data;
  },
  async updateUserExplorerCsvExport(
    companyId: string,
    userId: string,
    explorerCsvExportEnabled: boolean,
  ): Promise<{
    success: boolean;
    message: string;
    explorerCsvExportEnabled: boolean;
  }> {
    const response = await api.patch(
      `/api/companies/${companyId}/users/${userId}/explorer-csv-export`,
      { explorerCsvExportEnabled },
    );
    return response.data;
  },
  async getCompanyPermissions(companyId: string): Promise<CompanyPermissionsOverview> {
    const response = await api.get(`/api/companies/${companyId}/permissions`);
    return response.data;
  },
  async updateCompanyPermissions(
    companyId: string,
    permissions: string[],
  ): Promise<{ success: boolean; permissions: string[] }> {
    const response = await api.put(`/api/companies/${companyId}/permissions`, { permissions });
    return response.data;
  },
  /** Empty list = the user inherits the company permissions. */
  async updateUserPermissions(
    companyId: string,
    userId: string,
    permissions: string[],
  ): Promise<{ success: boolean; permissions: string[] }> {
    const response = await api.put(`/api/companies/${companyId}/users/${userId}/permissions`, {
      permissions,
    });
    return response.data;
  },
  async resyncCompanyPermissions(
    companyId: string,
    options: { grantNew?: string[]; dryRun?: boolean } = {},
  ): Promise<{
    success: boolean;
    dryRun: boolean;
    companyPermissions: string[];
    companyChanged: boolean;
    usersChanged: number;
    mismatches: Array<{ userId: string; email: string; parity: PermissionParityMismatch }>;
  }> {
    const response = await api.post(`/api/companies/${companyId}/permissions/resync`, options);
    return response.data;
  },
  async updateUserFilterOverrides(
    companyId: string,
    userId: string,
    filters: {
      dateFilterMinStartDate: string | null;
      dateFilterMaxEndDate: string | null;
      miContenidoDateFilterMinStartDate: string | null;
      miContenidoDateFilterMaxEndDate: string | null;
      explorerSponsorCategoryFilter: string | null;
    },
  ): Promise<{
    success: boolean;
    message: string;
    filters: {
      dateFilterMinStartDate: string | null;
      dateFilterMaxEndDate: string | null;
      miContenidoDateFilterMinStartDate: string | null;
      miContenidoDateFilterMaxEndDate: string | null;
      explorerSponsorCategoryFilter: string | null;
    };
  }> {
    const response = await api.patch(
      `/api/companies/${companyId}/users/${userId}/filters`,
      filters,
    );
    return response.data;
  },
  async getCompanies(
    page = 1,
    limit = 20,
    search?: string,
  ): Promise<{
    companies: Company[];
    pagination: { page: number; limit: number; total: number; pages: number };
  }> {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    if (search) params.append("search", search);
    const response = await api.get(`/api/companies?${params.toString()}`);
    return response.data;
  },
  async getCompanyById(companyId: string): Promise<Company> {
    const response = await api.get(`/api/companies/${companyId}`);
    return response.data;
  },
  async createCompany(name: string, ownerUserId?: string): Promise<Company> {
    const response = await api.post<Company>("/api/companies", {
      name,
      ownerUserId,
    });
    return response.data;
  },
  async createUser(
    name: string,
    email: string,
    password: string,
  ): Promise<{ message: string; user: User; token: string }> {
    const response = await api.post("/api/auth/signup", {
      name,
      email,
      password,
    });
    return response.data;
  },
  async getCompanyPodcasts(
    companyId: string,
    opts?: { includePrograms?: boolean },
  ): Promise<{ podcasts: Podcast[] }> {
    const params = new URLSearchParams();
    if (opts?.includePrograms) params.set("includePrograms", "true");
    const q = params.toString();
    const response = await api.get(
      `/api/companies/${companyId}/podcasts${q ? `?${q}` : ""}`,
    );
    return response.data;
  },
  /** Program row for Mi contenido when profile aggregate omits this program (no rows in date range). */
  /** Mi contenido program page: stats for date range (not public media kit URL). */
  async getProgramMiContenidoStats(
    programId: string,
    startDate: string,
    endDate: string,
    opts?: {
      youtubeLongFormMinDurationSec?: number;
      contentBrandFilter?: string;
    },
  ) {
    const params = new URLSearchParams({ startDate, endDate });
    if (
      opts?.youtubeLongFormMinDurationSec != null &&
      Number.isFinite(opts.youtubeLongFormMinDurationSec)
    ) {
      params.set(
        "youtubeLongFormMinDurationSec",
        String(opts.youtubeLongFormMinDurationSec),
      );
    }
    if (opts?.contentBrandFilter && opts.contentBrandFilter !== "all") {
      params.set("contentBrandFilter", opts.contentBrandFilter);
    }
    const response = await api.get(
      `/api/companies/me/programs/${encodeURIComponent(programId)}/mi-contenido-stats?${params.toString()}`,
    );
    return response.data;
  },

  /** Mi contenido program: concurrent digital competitor data for Podscope rating. */
  async getProgramPodscopeCompetition(
    programId: string,
    startDate: string,
    endDate: string,
  ): Promise<{
    programChannelName: string | null;
    programAvgViewers: number;
    competingChannels: Array<{
      channelName: string;
      avgViewers: number;
      streamCount: number;
    }>;
    totalConcurrentDigitalViewers: number;
  }> {
    const params = new URLSearchParams({ startDate, endDate });
    const response = await api.get(
      `/api/companies/me/programs/${encodeURIComponent(programId)}/podscope-competition?${params.toString()}`,
    );
    return response.data;
  },

  /** Mi contenido program page: trends by mode + granularity. */
  async getProgramMiContenidoWeeklyTrends(
    programId: string,
    startDate: string,
    endDate: string,
    options?: {
      mode?: "published_at" | "views_gained";
      granularity?: "day" | "week" | "month";
    },
  ): Promise<{
    trends: Array<{
      week: string;
      weekStart: string;
      youtubeLongViews: number;
      youtubeShortsViews: number;
      instagramViews: number;
      facebookViews: number;
      tiktokViews: number;
      podscopeAvgViewers: number;
      podscopePeakViewers: number;
    }>;
    mode: "published_at" | "views_gained";
    granularity: "day" | "week" | "month";
    dailyMetricsSince?: Partial<
      Record<
        "youtube" | "youtube_shorts" | "instagram" | "facebook" | "tiktok",
        string
      >
    >;
  }> {
    const params = new URLSearchParams({ startDate, endDate });
    if (options?.mode) params.append("mode", options.mode);
    if (options?.granularity) params.append("granularity", options.granularity);
    const response = await api.get(
      `/api/companies/me/programs/${encodeURIComponent(programId)}/mi-contenido-weekly-trends?${params.toString()}`,
    );
    return response.data;
  },

  async getChannelMiContenidoStats(
    podcastId: string,
    socialAccountId: string,
    startDate: string,
    endDate: string,
    opts?: { contentBrandFilter?: string },
  ) {
    const params = new URLSearchParams({ startDate, endDate });
    if (opts?.contentBrandFilter && opts.contentBrandFilter !== "all") {
      params.set("contentBrandFilter", opts.contentBrandFilter);
    }
    const response = await api.get(
      `/api/companies/me/podcasts/${encodeURIComponent(podcastId)}/social-accounts/${encodeURIComponent(socialAccountId)}/mi-contenido-stats?${params.toString()}`,
    );
    return response.data;
  },

  async getPodcastMiContenidoStats(
    podcastId: string,
    startDate: string,
    endDate: string,
    opts?: { contentBrandFilter?: string },
  ) {
    const params = new URLSearchParams({ startDate, endDate });
    if (opts?.contentBrandFilter && opts.contentBrandFilter !== "all") {
      params.set("contentBrandFilter", opts.contentBrandFilter);
    }
    const response = await api.get(
      `/api/companies/me/podcasts/${encodeURIComponent(podcastId)}/mi-contenido-stats?${params.toString()}`,
    );
    return response.data;
  },

  async exportProgramMiContenidoMetrics(
    programId: string,
    startDate: string,
    endDate: string,
    _opts: { podcastTitle: string; programTitle: string },
  ): Promise<void> {
    const params = new URLSearchParams({ startDate, endDate });
    const response = await api.get<Blob>(
      `/api/companies/me/programs/${encodeURIComponent(programId)}/mi-contenido-metrics-pdf?${params.toString()}`,
      { responseType: "blob" },
    );
    downloadMiContenidoMetricsPdfBlob(
      response.data,
      response.headers["content-disposition"],
    );
  },

  async getProgramMiContenidoMetricsExportData(
    programId: string,
    startDate: string,
    endDate: string,
    options?: {
      rankingPeriod?: { startDate: string; endDate: string };
      /** Ask for the per-episode breakdown behind each program's totals. */
      includeEpisodes?: boolean;
    },
  ): Promise<import("../miContenido/miContenidoExportData").MiContenidoMetricsExportData> {
    const params = new URLSearchParams({ startDate, endDate });
    if (options?.rankingPeriod) {
      params.set("rankingStartDate", options.rankingPeriod.startDate);
      params.set("rankingEndDate", options.rankingPeriod.endDate);
    }
    if (options?.includeEpisodes) params.set("includeEpisodes", "1");
    const response = await api.get(
      `/api/companies/me/programs/${encodeURIComponent(programId)}/mi-contenido-metrics-export-data?${params.toString()}`,
    );
    return response.data;
  },

  async getPodcastMiContenidoMetricsExportData(
    podcastId: string,
    startDate: string,
    endDate: string,
    options?: {
      rankingPeriod?: { startDate: string; endDate: string };
      /** Ask for the per-episode breakdown behind each program's totals. */
      includeEpisodes?: boolean;
    },
  ): Promise<import("../miContenido/miContenidoExportData").MiContenidoMetricsExportData> {
    const params = new URLSearchParams({ startDate, endDate });
    if (options?.rankingPeriod) {
      params.set("rankingStartDate", options.rankingPeriod.startDate);
      params.set("rankingEndDate", options.rankingPeriod.endDate);
    }
    if (options?.includeEpisodes) params.set("includeEpisodes", "1");
    const response = await api.get(
      `/api/companies/me/podcasts/${encodeURIComponent(podcastId)}/mi-contenido-metrics-export-data?${params.toString()}`,
    );
    return response.data;
  },

  async getCompanyMiContenidoMetricsExportData(
    startDate: string,
    endDate: string,
    options?: {
      rankingPeriod?: { startDate: string; endDate: string };
      /** Ask for the per-episode breakdown behind each program's totals. */
      includeEpisodes?: boolean;
    },
  ): Promise<import("../miContenido/miContenidoExportData").MiContenidoMetricsExportData> {
    const params = new URLSearchParams({ startDate, endDate });
    if (options?.rankingPeriod) {
      params.set("rankingStartDate", options.rankingPeriod.startDate);
      params.set("rankingEndDate", options.rankingPeriod.endDate);
    }
    if (options?.includeEpisodes) params.set("includeEpisodes", "1");
    const response = await api.get(
      `/api/companies/me/mi-contenido-metrics-export-data?${params.toString()}`,
    );
    return response.data;
  },

  async exportPodcastMiContenidoMetrics(
    podcastId: string,
    startDate: string,
    endDate: string,
    _opts: { podcastTitle: string },
  ): Promise<void> {
    const params = new URLSearchParams({ startDate, endDate });
    const response = await api.get<Blob>(
      `/api/companies/me/podcasts/${encodeURIComponent(podcastId)}/mi-contenido-metrics-pdf?${params.toString()}`,
      { responseType: "blob" },
    );
    downloadMiContenidoMetricsPdfBlob(
      response.data,
      response.headers["content-disposition"],
    );
  },

  async getPodcastMetricsReportSchedule(
    podcastId: string,
  ): Promise<{ schedule: import("../miContenido/metricsReportSchedule").MetricsReportScheduleDto | null }> {
    const response = await api.get(
      `/api/companies/me/podcasts/${encodeURIComponent(podcastId)}/metrics-report-schedule`,
    );
    return response.data;
  },

  async putPodcastMetricsReportSchedule(
    podcastId: string,
    body: import("../miContenido/metricsReportSchedule").MetricsReportScheduleUpsertPayload,
  ): Promise<{ schedule: import("../miContenido/metricsReportSchedule").MetricsReportScheduleDto }> {
    const response = await api.put(
      `/api/companies/me/podcasts/${encodeURIComponent(podcastId)}/metrics-report-schedule`,
      body,
    );
    return response.data;
  },

  async getProgramMetricsReportSchedule(
    programId: string,
  ): Promise<{ schedule: import("../miContenido/metricsReportSchedule").MetricsReportScheduleDto | null }> {
    const response = await api.get(
      `/api/companies/me/programs/${encodeURIComponent(programId)}/metrics-report-schedule`,
    );
    return response.data;
  },

  async putProgramMetricsReportSchedule(
    programId: string,
    body: import("../miContenido/metricsReportSchedule").MetricsReportScheduleUpsertPayload,
  ): Promise<{ schedule: import("../miContenido/metricsReportSchedule").MetricsReportScheduleDto }> {
    const response = await api.put(
      `/api/companies/me/programs/${encodeURIComponent(programId)}/metrics-report-schedule`,
      body,
    );
    return response.data;
  },

  async listCompanyMetricsReportSchedules(): Promise<{
    schedules: import("../miContenido/metricsReportSchedule").MetricsReportScheduleListItemDto[];
  }> {
    const response = await api.get("/api/companies/me/metrics-report-schedules");
    return response.data;
  },

  async getCompanyProgramMeta(
    companyId: string,
    programId: string,
    options?: { startDate?: string; endDate?: string },
  ): Promise<{
    _id: string;
    name: string;
    slug: string;
    imageS3Key: string | null;
    updatedAt: string | null;
    podcastId: string;
    podcastSlug?: string;
    podcastTitle?: string;
    podcastImageS3Key?: string | null;
    podcastImageUrl?: string | null;
    isActive: boolean;
    hidden: boolean;
    hashtags?: string[];
    keywords?: string[];
    platform_counts?: {
      youtube: number;
      youtube_shorts: number;
      instagram: number;
      facebook: number;
      tiktok: number;
    };
  }> {
    const params = new URLSearchParams();
    if (options?.startDate) params.set("startDate", options.startDate);
    if (options?.endDate) params.set("endDate", options.endDate);
    const qs = params.toString();
    const response = await api.get(
      `/api/companies/${companyId}/programs/${programId}${qs ? `?${qs}` : ""}`,
    );
    return response.data;
  },

  async getMiContenidoPlatformCounts(
    startDate: string,
    endDate: string,
    scope: {
      podcastId?: string;
      programId?: string;
      socialAccountId?: string;
    },
  ): Promise<{
    youtube: number;
    youtube_shorts: number;
    instagram: number;
    facebook: number;
    tiktok: number;
  }> {
    const params = new URLSearchParams({ startDate, endDate });
    if (scope.podcastId) params.set("podcastId", scope.podcastId);
    if (scope.programId) params.set("programId", scope.programId);
    if (scope.socialAccountId) {
      params.set("socialAccountId", scope.socialAccountId);
    }
    const response = await api.get(
      `/api/companies/me/mi-contenido/platform-counts?${params.toString()}`,
    );
    return response.data;
  },
  async updateCompanyProgramSettings(
    companyId: string,
    programId: string,
    body: {
      enabled?: boolean;
      livestreamDataEnabled?: boolean;
      demographicDataEnabled?: boolean;
    },
  ): Promise<{
    programId: string;
    enabled: boolean;
    livestreamDataEnabled: boolean;
    demographicDataEnabled: boolean;
  }> {
    const response = await api.patch(
      `/api/companies/${companyId}/programs/${programId}/settings`,
      body,
    );
    return response.data;
  },
  async addPodcastToCompany(
    companyId: string,
    podcastId: string,
  ): Promise<{ podcast: Podcast }> {
    const response = await api.post(`/api/companies/${companyId}/podcasts`, {
      podcastId,
    });
    return response.data;
  },
  async removePodcastFromCompany(
    companyId: string,
    podcastId: string,
  ): Promise<void> {
    await api.delete(`/api/companies/${companyId}/podcasts/${podcastId}`);
  },
  async getCompanyPodcastSettings(
    companyId: string,
    podcastId: string,
  ): Promise<{ livestreamDataEnabled: boolean }> {
    const response = await api.get(
      `/api/companies/${companyId}/podcasts/${podcastId}/settings`,
    );
    return response.data;
  },
  async updateCompanyPodcastLivestreamSettings(
    companyId: string,
    podcastId: string,
    livestreamDataEnabled: boolean,
  ): Promise<void> {
    await api.patch(
      `/api/companies/${companyId}/podcasts/${podcastId}/settings`,
      { livestreamDataEnabled },
    );
  },

  async updatePodcastSocialAccounts(
    podcastId: string,
    socialAccounts: { instagram?: string | null; tiktok?: string | null; facebook?: string | null },
  ): Promise<{ socialAccounts: { instagram?: string; tiktok?: string; facebook?: string } }> {
    const response = await api.patch(
      `/api/podcasts/${podcastId}/social-accounts`,
      socialAccounts,
    );
    return response.data;
  },

  // Invitations (Meta)
  async listPodcastInvitations(
    companyId: string,
    podcastId: string,
  ): Promise<{
    invitations: Array<{
      _id: string;
      token: string;
      status: string;
      createdAt: string;
      expiresAt?: string;
      platform: "meta" | "youtube" | "tiktok";
      metaInviteMultiPodcastEnabled?: boolean;
      socialAccountId?: string;
      connectedAccount?: {
        username?: string;
        accountId?: string;
        platform?: "meta" | "youtube" | "tiktok";
      };
    }>;
  }> {
    const response = await api.get(
      `/api/companies/${companyId}/podcasts/${podcastId}/invitations`,
    );
    return response.data;
  },
  async createPodcastInvitation(
    companyId: string,
    podcastId: string,
    expiresInDays = 7,
    platform: "meta" | "youtube" | "tiktok" | "spotify" = "meta",
  ): Promise<{
    _id: string;
    token: string;
    status: string;
    createdAt: string;
    expiresAt?: string;
    platform: "meta" | "youtube" | "tiktok" | "spotify";
  }> {
    const response = await api.post(
      `/api/companies/${companyId}/podcasts/${podcastId}/invitations`,
      { expiresInDays, platform },
    );
    return response.data;
  },
  async deletePodcastInvitation(
    companyId: string,
    podcastId: string,
    invitationId: string,
  ): Promise<void> {
    await api.delete(
      `/api/companies/${companyId}/podcasts/${podcastId}/invitations/${invitationId}`,
    );
  },
  async updatePodcastInvitationMetaMultiPodcast(
    companyId: string,
    podcastId: string,
    invitationId: string,
    metaInviteMultiPodcastEnabled: boolean,
  ): Promise<{ _id: string; metaInviteMultiPodcastEnabled: boolean }> {
    const response = await api.patch(
      `/api/companies/${companyId}/podcasts/${podcastId}/invitations/${invitationId}`,
      { metaInviteMultiPodcastEnabled },
    );
    return response.data;
  },
  async getCompanyUsers(companyId: string): Promise<{ users: User[] }> {
    const response = await api.get(`/api/companies/${companyId}/users`);
    return response.data;
  },
  async inviteCompanyUserByEmail(
    companyId: string,
    email: string,
    companyRole: "admin" | "member",
  ): Promise<{ message: string; email: string; expiresAt: string }> {
    const response = await api.post(
      `/api/companies/${companyId}/user-invitations`,
      {
        email,
        companyRole,
        inviteBaseUrl: window.location.origin,
      },
    );
    return response.data;
  },
  async createCompanyInviteLink(
    companyId: string,
    companyRole: "admin" | "member",
  ): Promise<{
    id: string;
    inviteUrl: string;
    expiresAt: string;
    companyRole: "admin" | "member";
  }> {
    const response = await api.post(
      `/api/companies/${companyId}/user-invitations/link`,
      {
        companyRole,
        inviteBaseUrl: window.location.origin,
      },
    );
    return response.data;
  },
  async createCompanyDemoUser(
    companyId: string,
    body: {
      name: string;
      email: string;
      companyRole: "admin" | "member";
    },
  ): Promise<{
    userId: string;
    email: string;
    password: string;
    created: boolean;
  }> {
    const response = await api.post(
      `/api/companies/${companyId}/demo-users`,
      body,
    );
    return response.data;
  },
  async deleteCompanyDemoUser(companyId: string, userId: string): Promise<void> {
    await api.delete(`/api/companies/${companyId}/demo-users/${userId}`);
  },
  async revokeCompanyDemoUserInvite(
    companyId: string,
    userId: string,
  ): Promise<void> {
    await api.delete(`/api/companies/${companyId}/demo-users/${userId}/invite`);
  },
  async sendCompanyDemoUserInvite(
    companyId: string,
    userId: string,
  ): Promise<{ message: string; email: string }> {
    const response = await api.post(
      `/api/companies/${companyId}/demo-users/${userId}/send-email`,
      { inviteBaseUrl: window.location.origin },
    );
    return response.data;
  },
  async getCompanyDemoUserLink(
    companyId: string,
    userId: string,
  ): Promise<{ userId: string; loginUrl: string }> {
    const response = await api.post(
      `/api/companies/${companyId}/demo-users/${userId}/link`,
      { inviteBaseUrl: window.location.origin },
    );
    return response.data;
  },
  async getCompanyDemoUserPassword(
    companyId: string,
    userId: string,
  ): Promise<{ userId: string; password: string }> {
    const response = await api.get(
      `/api/companies/${companyId}/demo-users/${userId}/password`,
    );
    return response.data;
  },
  async getCompanyUserInvitations(companyId: string): Promise<{
    invitations: Array<{
      id: string;
      kind: "email" | "link";
      email: string | null;
      companyRole: "admin" | "member";
      status: "pending" | "accepted" | "revoked" | "expired";
      createdAt: string;
      expiresAt: string;
      acceptedByUserId: string | null;
    }>;
  }> {
    const response = await api.get(
      `/api/companies/${companyId}/user-invitations`,
    );
    return response.data;
  },
  async refreshCompanyInviteLink(
    companyId: string,
    invitationId: string,
  ): Promise<{ id: string; inviteUrl: string; expiresAt: string }> {
    const response = await api.post(
      `/api/companies/${companyId}/user-invitations/${invitationId}/link`,
      { inviteBaseUrl: window.location.origin },
    );
    return response.data;
  },
  async deleteCompanyUserInvitation(
    companyId: string,
    invitationId: string,
  ): Promise<void> {
    await api.delete(
      `/api/companies/${companyId}/user-invitations/${invitationId}`,
    );
  },
  async addCompanyUser(
    companyId: string,
    userId: string,
    companyRole?: "admin" | "member",
  ): Promise<{ user: User }> {
    const response = await api.post(`/api/companies/${companyId}/users`, {
      userId,
      companyRole,
    });
    return response.data;
  },
  async updateCompanyUserRole(
    companyId: string,
    userId: string,
    companyRole: "admin" | "member",
  ): Promise<{ user: User }> {
    const response = await api.patch(
      `/api/companies/${companyId}/users/${userId}`,
      { companyRole },
    );
    return response.data;
  },
  /** Platform admin: unlinks the user from the company (does not delete the account). */
  async removeCompanyUser(companyId: string, userId: string): Promise<void> {
    await api.delete(`/api/companies/${companyId}/users/${userId}`);
  },
  async deleteCompany(companyId: string): Promise<void> {
    await api.delete(`/api/companies/${companyId}`);
  },

  // Social Accounts
  async getSocialAccountsByPodcast(
    podcastId: string,
    options?: { companyId?: string },
  ): Promise<SocialAccount[]> {
    const params = new URLSearchParams();
    if (options?.companyId) params.set("companyId", options.companyId);
    const qs = params.toString();
    const response = await api.get<SocialAccount[]>(
      `/api/social-accounts/podcasts/${podcastId}${qs ? `?${qs}` : ""}`,
    );
    return response.data;
  },
  async linkSocialAccount(data: {
    podcastId: string;
    platform: SocialPlatform;
    accountId: string;
    username?: string;
    accessToken?: string;
    refreshToken?: string;
    expiresAt?: string;
    grantedPermissions?: string[];
  }): Promise<SocialAccount> {
    const response = await api.post<SocialAccount>(
      "/api/social-accounts/link",
      data,
    );
    return response.data;
  },
  async unlinkSocialAccount(id: string): Promise<void> {
    await api.delete(`/api/social-accounts/${id}`);
  },

  async getAdminSocialAccounts(): Promise<{ accounts: AdminSocialAccount[] }> {
    const response = await api.get<{ accounts: AdminSocialAccount[] }>(
      "/api/admin/social-accounts",
    );
    return response.data;
  },

  async getReviewAuditSummary(params: {
    dateFrom: string;
    dateTo: string;
    effortDateTo?: string;
    podcastId?: string;
  }): Promise<ReviewAuditSummary> {
    const q = new URLSearchParams({
      dateFrom: params.dateFrom,
      dateTo: params.dateTo,
    });
    if (params.effortDateTo) q.set("effortDateTo", params.effortDateTo);
    if (params.podcastId) q.set("podcastId", params.podcastId);
    const response = await api.get<ReviewAuditSummary>(
      `/api/admin/review-audit/summary?${q.toString()}`,
    );
    return response.data;
  },

  async getReviewAuditCases(params: {
    dateFrom: string;
    dateTo: string;
    podcastId?: string;
  }): Promise<ReviewAuditCasesSummary> {
    const q = new URLSearchParams({
      dateFrom: params.dateFrom,
      dateTo: params.dateTo,
    });
    if (params.podcastId) q.set("podcastId", params.podcastId);
    const response = await api.get<ReviewAuditCasesSummary>(
      `/api/admin/review-audit/cases?${q.toString()}`,
    );
    return response.data;
  },

  async getReviewAuditCase(mentionId: string): Promise<ReviewAuditCaseDetail> {
    const response = await api.get<ReviewAuditCaseDetail>(
      `/api/admin/review-audit/cases/${encodeURIComponent(mentionId)}`,
    );
    return response.data;
  },

  /** Mention clip (start - pad to end + pad) as an mp4 blob plus the server's file name. */
  async extractReviewAuditCaseClip(
    mentionId: string,
    pad = 10,
  ): Promise<ReviewAuditCaseClip> {
    try {
      const response = await api.post<ReviewAuditCaseClip>(
        `/api/admin/review-audit/cases/${encodeURIComponent(mentionId)}/clip`,
        {},
        { params: { pad }, timeout: 360000 },
      );
      return response.data;
    } catch (error) {
      const message = (error as { response?: { data?: { message?: string } } }).response?.data
        ?.message;
      throw message ? new Error(message) : error;
    }
  },

  async rememberReviewAuditCaseWording(
    mentionId: string,
  ): Promise<{ brandId: string; fingerprints: BrandTranscriptFingerprint[] }> {
    try {
      const response = await api.post<{
        brandId: string;
        fingerprints: BrandTranscriptFingerprint[];
      }>(`/api/admin/review-audit/cases/${encodeURIComponent(mentionId)}/fingerprint`, {});
      return response.data;
    } catch (error) {
      const message = (error as { response?: { data?: { message?: string } } }).response?.data
        ?.message;
      throw message ? new Error(message) : error;
    }
  },

  async addBrandTranscriptFingerprint(
    brandId: string,
    text: string,
  ): Promise<BrandTranscriptFingerprint[]> {
    try {
      const response = await api.post<{ fingerprints: BrandTranscriptFingerprint[] }>(
        `/api/admin/review-audit/brands/${encodeURIComponent(brandId)}/fingerprints`,
        { text },
      );
      return response.data.fingerprints;
    } catch (error) {
      const message = (error as { response?: { data?: { message?: string } } }).response?.data
        ?.message;
      throw message ? new Error(message) : error;
    }
  },

  async getBrandTranscriptFingerprints(brandId: string): Promise<BrandTranscriptFingerprint[]> {
    const response = await api.get<{ fingerprints: BrandTranscriptFingerprint[] }>(
      `/api/admin/review-audit/brands/${encodeURIComponent(brandId)}/fingerprints`,
    );
    return response.data.fingerprints;
  },

  async updateBrandTranscriptFingerprint(
    brandId: string,
    mentionId: string,
    text: string,
  ): Promise<BrandTranscriptFingerprint[]> {
    try {
      const response = await api.put<{ fingerprints: BrandTranscriptFingerprint[] }>(
        `/api/admin/review-audit/brands/${encodeURIComponent(brandId)}/fingerprints/${encodeURIComponent(mentionId)}`,
        { text },
      );
      return response.data.fingerprints;
    } catch (error) {
      const message = (error as { response?: { data?: { message?: string } } }).response?.data
        ?.message;
      throw message ? new Error(message) : error;
    }
  },

  async removeBrandTranscriptFingerprint(
    brandId: string,
    mentionId: string,
  ): Promise<BrandTranscriptFingerprint[]> {
    const response = await api.delete<{ fingerprints: BrandTranscriptFingerprint[] }>(
      `/api/admin/review-audit/brands/${encodeURIComponent(brandId)}/fingerprints/${encodeURIComponent(mentionId)}`,
    );
    return response.data.fingerprints;
  },

  async getReviewAuditDatasetFrames(
    mentionId: string,
    from: number,
    to: number,
  ): Promise<ReviewAuditDatasetFrame[]> {
    const response = await api.get<{ frames: ReviewAuditDatasetFrame[] }>(
      `/api/admin/review-audit/cases/${encodeURIComponent(mentionId)}/dataset-frames`,
      { params: { from, to } },
    );
    return response.data.frames;
  },

  async saveReviewAuditDatasetFrame(
    mentionId: string,
    input: { tsec: number; cue: string; brands: string[]; note?: string },
  ): Promise<ReviewAuditDatasetFrame> {
    try {
      const response = await api.post<{ frame: ReviewAuditDatasetFrame }>(
        `/api/admin/review-audit/cases/${encodeURIComponent(mentionId)}/dataset-frames`,
        input,
        { timeout: 200000 },
      );
      return response.data.frame;
    } catch (error) {
      const message = (error as { response?: { data?: { message?: string } } }).response?.data
        ?.message;
      throw message ? new Error(message) : error;
    }
  },

  async getReviewAuditCaseClip(mentionId: string): Promise<ReviewAuditCaseClip | null> {
    const response = await api.get<{ clip: ReviewAuditCaseClip | null }>(
      `/api/admin/review-audit/cases/${encodeURIComponent(mentionId)}/clip`,
    );
    return response.data.clip;
  },

  async getReviewAuditCaseClipFile(mentionId: string): Promise<Blob> {
    const response = await api.get<Blob>(
      `/api/admin/review-audit/cases/${encodeURIComponent(mentionId)}/clip/file`,
      { responseType: "blob", timeout: 120000 },
    );
    return response.data;
  },

  async runReviewAuditCaseProbe(mentionId: string): Promise<ReviewAuditCaseProbe> {
    const response = await api.post<ReviewAuditCaseProbe>(
      `/api/admin/review-audit/cases/${encodeURIComponent(mentionId)}/probe`,
      {},
      { timeout: 300000 },
    );
    return response.data;
  },

  async getChatUsageSummary(params: ChatUsageFilters): Promise<ChatUsageSummary> {
    const response = await api.get<ChatUsageSummary>(
      `/api/admin/chat-usage/summary?${chatUsageQuery(params).toString()}`,
    );
    return response.data;
  },

  async getChatUsageConversations(
    params: ChatUsageFilters & { intent?: string; page?: number; pageSize?: number },
  ): Promise<ChatUsageConversationList> {
    const q = chatUsageQuery(params);
    if (params.intent) q.set("intent", params.intent);
    if (params.page != null) q.set("page", String(params.page));
    if (params.pageSize != null) q.set("pageSize", String(params.pageSize));
    const response = await api.get<ChatUsageConversationList>(
      `/api/admin/chat-usage/conversations?${q.toString()}`,
    );
    return response.data;
  },

  async getChatUsageConversation(id: string): Promise<ChatUsageConversationDetail> {
    const response = await api.get<ChatUsageConversationDetail>(
      `/api/admin/chat-usage/conversations/${encodeURIComponent(id)}`,
    );
    return response.data;
  },

  async getReviewTimeWeek(week?: string): Promise<{ weekKey: string; totalMs: number }> {
    const q = week ? `?week=${encodeURIComponent(week)}` : "";
    const response = await api.get<{ weekKey: string; totalMs: number }>(
      `/api/admin/review-time/week${q}`,
    );
    return response.data;
  },

  async heartbeatReviewTime(params: {
    deltaMs: number;
    podcastId?: string;
  }): Promise<{ weekKey: string; totalMs: number }> {
    const response = await api.post<{ weekKey: string; totalMs: number }>(
      "/api/admin/review-time/heartbeat",
      { deltaMs: params.deltaMs, podcastId: params.podcastId },
    );
    return response.data;
  },

  async getReviewTimeByPodcast(week?: string): Promise<ReviewTimeByPodcastResponse> {
    const q = week ? `?week=${encodeURIComponent(week)}` : "";
    const response = await api.get<ReviewTimeByPodcastResponse>(
      `/api/admin/review-time/by-podcast${q}`,
    );
    return response.data;
  },

  async getAdminUsers(page = 1, limit = 10) {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    const response = await api.get<{
      users: User[];
      pagination: { page: number; limit: number; total: number; pages: number };
    }>(`/api/admin/users?${params}`);
    return response.data;
  },

  async deleteUser(userId: string): Promise<void> {
    await api.delete(`/api/admin/users/${userId}`);
  },

  async getPodcastsForAssignment(page = 1, limit = 20, search?: string) {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (search) params.append("search", search);

    const response = await api.get<PaginatedResponse<Podcast>>(
      `/api/admin/podcasts-for-assignment?${params}`,
    );
    return response.data;
  },

  async assignPodcastsToUser(userId: string, podcastIds: string[]) {
    const response = await api.post(
      `/api/admin/users/${userId}/assign-podcasts`,
      {
        podcastIds,
      },
    );
    return response.data;
  },

  async triggerFetchEpisodes(podcastId: string) {
    const response = await api.post(`/api/admin/fetch-episodes/${podcastId}`);
    return response.data;
  },

  async getGroupedContent(podcastId: string): Promise<GroupedContent> {
    const response = await api.get(`/api/studio/content/${podcastId}/grouped`);
    return response.data;
  },

  // NEW: Get private grouped content (from PrivateContent model)
  async getPrivateCollectionsContent(
    podcastId: string,
    filters?: {
      programId?: string;
      brandId?: string;
      monthFilter?: string;
      searchTerm?: string;
      tab?: string;
      page?: number;
      limit?: number;
      companyId?: string;
    },
  ): Promise<PrivateCollectionsContent> {
    const params = new URLSearchParams();
    if (filters?.programId) params.append("programId", filters.programId);
    if (filters?.brandId) params.append("brandId", filters.brandId);
    if (filters?.monthFilter) params.append("monthFilter", filters.monthFilter);
    if (filters?.searchTerm) params.append("searchTerm", filters.searchTerm);
    if (filters?.tab) params.append("tab", filters.tab);
    if (filters?.page) params.append("page", filters.page.toString());
    if (filters?.limit) params.append("limit", filters.limit.toString());
    if (filters?.companyId) params.append("companyId", filters.companyId);

    const queryString = params.toString();
    const url = `/api/collections/podcast/${podcastId}/content${queryString ? `?${queryString}` : ""}`;

    const response = await api.get(url);
    return response.data;
  },

  // NEW: Unified endpoint - returns content, tab counts, brands, and months in one call
  async getUnifiedCollectionContent(
    podcastId: string,
    filters?: {
      programId?: string;
      brandId?: string;
      monthFilter?: string;
      searchTerm?: string;
      currentTab?: string;
      page?: number;
      limit?: number;
      companyId?: string;
    },
  ): Promise<{
    allContent: {
      instagramContent: PrivateContent[];
      tiktokContent: PrivateContent[];
      youtubeContent: PrivateContent[];
      spotifyContent: PrivateContent[];
      facebookContent: PrivateContent[];
    };
    tabCounts: Record<string, number>;
    brands: Array<{
      _id: string;
      name: string;
      logoUrl?: string;
      s3Key?: string;
    }>;
    months: string[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      itemsPerPage: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
  }> {
    const params = new URLSearchParams();
    if (filters?.programId) params.append("programId", filters.programId);
    if (filters?.brandId) params.append("brandId", filters.brandId);
    if (filters?.monthFilter) params.append("monthFilter", filters.monthFilter);
    if (filters?.searchTerm) params.append("searchTerm", filters.searchTerm);
    if (filters?.currentTab) params.append("currentTab", filters.currentTab);
    if (filters?.page) params.append("page", filters.page.toString());
    if (filters?.limit) params.append("limit", filters.limit.toString());
    if (filters?.companyId) params.append("companyId", filters.companyId);

    const queryString = params.toString();
    const url = `/api/collections/podcast/${podcastId}/content/unified${queryString ? `?${queryString}` : ""}`;

    const response = await api.get(url);
    return response.data;
  },

  // PRIVATE COLLECTION MANAGEMENT
  async getPrivateCollections(podcastId: string): Promise<PrivateCollection[]> {
    const response = await api.get(`/api/collections/podcast/${podcastId}`);
    return response.data;
  },

  // Get podcast metadata (months, brands, programs) in one request - optimized
  async getPodcastMeta(
    podcastId: string,
    filters?: {
      monthFilter?: string;
      programId?: string;
      programIds?: string;
      companyId?: string;
    },
  ): Promise<{
    availableMonths: Array<{ value: string; label: string }>;
    brands: Array<{
      _id: string;
      name: string;
      logoUrl?: string;
      s3Key?: string;
      mentionCount: number;
      presenceCount: number;
    }>;
    programs: Array<{ _id: string; name: string; imageS3Key?: string }>;
    keywords: string[];
  }> {
    const params = new URLSearchParams();
    if (filters?.monthFilter) params.append("monthFilter", filters.monthFilter);
    if (filters?.programId) params.append("programId", filters.programId);
    if (filters?.programIds) params.append("programIds", filters.programIds);
    if (filters?.companyId) params.append("companyId", filters.companyId);

    const queryString = params.toString();
    const url = `/api/collections/podcast/${podcastId}/meta${queryString ? `?${queryString}` : ""}`;

    const response = await api.get(url);
    return response.data;
  },

  /** Most-used hashtags (without `#`) in the company's content, optionally scoped to channels. */
  async getCollectionHashtagSuggestions(
    podcastIds: string[],
  ): Promise<Array<{ tag: string; count: number }>> {
    const params = new URLSearchParams();
    if (podcastIds.length > 0) params.append("podcastIds", podcastIds.join(","));
    const queryString = params.toString();
    const response = await api.get(
      `/api/collections/hashtag-suggestions${queryString ? `?${queryString}` : ""}`,
    );
    return response.data?.hashtags ?? [];
  },

  async getCompanyCollections(
    page: number = 1,
    limit: number = 12,
    search?: string,
    podcastId?: string,
    brandId?: string,
    productSource?: "micontenido" | "clips",
  ): Promise<CompanyCollectionsResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (search) {
      params.append("search", search);
    }

    if (podcastId) {
      params.append("podcastId", podcastId);
    }

    if (brandId) {
      params.append("brandId", brandId);
    }

    if (productSource) {
      params.append("productSource", productSource);
    }

    const response = await api.get(
      `/api/collections/company?${params.toString()}`,
    );
    return response.data;
  },

  async getCompanyCollectionsByIds(
    ids: string[],
  ): Promise<CompanyCollectionsResponse> {
    if (ids.length === 0) {
      return {
        collections: [],
        pagination: { page: 1, limit: 0, total: 0, totalPages: 0 },
      };
    }
    const params = new URLSearchParams({
      page: "1",
      limit: String(ids.length),
      ids: ids.join(","),
    });
    const response = await api.get(
      `/api/collections/company?${params.toString()}`,
    );
    return response.data;
  },

  async createPrivateCollection(
    title: string,
    podcastId?: string,
    assignedBrandId?: string,
    options?: {
      collectionType?: "manual" | "dynamic" | "clips";
      dynamicCriteria?: DynamicCollectionCriteria;
      assignedBrandIds?: string[];
      productSource?: "micontenido" | "clips";
      clipsConfig?: ClipsCollectionConfig;
    },
  ): Promise<PrivateCollection> {
    const body: Record<string, unknown> = {
      title,
    };
    if (podcastId) body["podcastId"] = podcastId;
    if (assignedBrandId) body["assignedBrandId"] = assignedBrandId;
    if (options?.assignedBrandIds?.length) {
      body["assignedBrandIds"] = options.assignedBrandIds;
    }
    if (options?.productSource) {
      body["productSource"] = options.productSource;
    }
    if (options?.collectionType === "clips") {
      body["collectionType"] = "clips";
      body["productSource"] = "clips";
      if (options.clipsConfig) {
        body["clipsConfig"] = options.clipsConfig;
      }
      // Apify profile scrapes on create can exceed the default 2m axios timeout.
      const response = await api.post("/api/collections", body, {
        timeout: 600000,
      });
      return response.data;
    } else if (options?.collectionType === "dynamic") {
      body["collectionType"] = "dynamic";
      body["dynamicCriteria"] = options.dynamicCriteria ?? {};
    }
    const response = await api.post("/api/collections", body);
    return response.data;
  },

  async createMultiPodcastCollection(
    title: string,
    assignedBrandId?: string,
    options?: {
      collectionType?: "manual" | "dynamic";
      dynamicCriteria?: DynamicCollectionCriteria;
      assignedBrandIds?: string[];
      /** When set, limits multi-podcast scope to these channels (otherwise all company podcasts). */
      podcastIds?: string[];
    },
  ): Promise<PrivateCollection> {
    const body: Record<string, unknown> = {
      title,
      isMultiPodcast: true,
    };
    if (assignedBrandId) body["assignedBrandId"] = assignedBrandId;
    if (options?.assignedBrandIds?.length) {
      body["assignedBrandIds"] = options.assignedBrandIds;
    }
    if (options?.podcastIds?.length) {
      body["podcastIds"] = options.podcastIds;
    }
    if (options?.collectionType === "dynamic") {
      body["collectionType"] = "dynamic";
      body["dynamicCriteria"] = options.dynamicCriteria ?? {};
    }
    const response = await api.post("/api/collections", body);
    return response.data;
  },

  async updatePrivateCollection(
    collectionId: string,
    data: {
      title: string;
      assignedBrandId?: string | null;
      assignedBrandIds?: string[];
      includeEstimatedViewers?: boolean;
      includeInstagramReach?: boolean;
      includeLiveViewers?: boolean;
      includeLivestreamChat?: boolean;
      /** `null` clears stored filter (show all placement types). */
      brandPresencePlacementTypes?:
        | ("tagged" | "placement" | "overlay")[]
        | null;
      /** `null` clears stored filter (show all mention buckets). */
      brandMentionBuckets?: ("content" | "credits" | "physical")[] | null;
      includeBrands?: boolean;
      includeMentionSummary?: boolean;
      includeDemographicsInReports?: boolean;
      includeEpisodeRetentionInReports?: boolean;
      includeEngagedViewsInReports?: boolean;
      displayPodcastName?: string;
      sections?: ContentSection[];
      collectionType?: "manual" | "dynamic";
      dynamicCriteria?: DynamicCollectionCriteria;
      isMultiPodcast?: boolean;
      podcastId?: string | null;
      podcastIds?: string[];
      shareLinkPresets?: ShareLinkPreset[];
    },
  ): Promise<PrivateCollection> {
    const response = await api.put(`/api/collections/${collectionId}`, data);
    return response.data;
  },

  async removePrivateCollectionItems(
    collectionId: string,
    contentIds: string[],
  ): Promise<{ message: string }> {
    const response = await api.delete(
      `/api/collections/${collectionId}/items`,
      {
        data: { contentIds },
      },
    );
    return response.data;
  },

  async addPrivateCollectionItems(
    collectionId: string,
    items: Array<{
      contentId: string;
      contentType:
        | "instagram_private"
        | "tiktok_private"
        | "youtube_private"
        | "spotify_private"
        | "facebook_private";
    }>,
  ): Promise<{ message: string }> {
    const response = await api.post(`/api/collections/${collectionId}/items`, {
      items,
    });
    return response.data;
  },

  /** Clips: paste public short-form URLs → Apify / YT → private_content + collection items. */
  async importClipsUrls(
    collectionId: string,
    urls: string[],
  ): Promise<{
    imported: Array<{
      contentId: string;
      url: string;
      platform: string;
      created: boolean;
    }>;
    skipped: Array<{ contentId: string; url: string; platform: string }>;
    failed: Array<{ url: string; reason: string }>;
    attached: number;
  }> {
    const response = await api.post(
      `/api/collections/${collectionId}/import-urls`,
      { urls },
      { timeout: 300000 },
    );
    return response.data;
  },

  /** Clips: add influencer profile, scrape posts from startDate, enrich avatar/followers. */
  async addClipsInfluencer(
    collectionId: string,
    influencer: {
      name?: string;
      instagramHandle?: string;
      tiktokHandle?: string;
    },
  ): Promise<
    PrivateCollection & {
      importSummary?: {
        imported: Array<{
          contentId: string;
          url: string;
          platform: string;
          created: boolean;
        }>;
        skipped: Array<{ contentId: string; url: string; platform: string }>;
        failed: Array<{ url: string; reason: string }>;
        attached: number;
      };
    }
  > {
    const response = await api.post(
      `/api/collections/${collectionId}/clips-influencers`,
      { influencer },
      { timeout: 600000 },
    );
    return response.data;
  },

  /** Clips: fetch/store brand + influencer profile metadata (avatars, followers). */
  async enrichClipsProfiles(collectionId: string): Promise<PrivateCollection> {
    const response = await api.post(
      `/api/collections/${collectionId}/enrich-clips-profiles`,
      {},
      { timeout: 600000 },
    );
    return response.data;
  },

  /** Clips: force-refresh profiles + update metrics for collection items. */
  async refreshClipsCollection(collectionId: string): Promise<
    PrivateCollection & {
      metricsSummary?: {
        attempted: number;
        updated: number;
        failed: number;
        skipped: number;
      };
      importSummary?: {
        imported: Array<{
          contentId: string;
          url: string;
          platform: string;
          created: boolean;
        }>;
        skipped: Array<{ contentId: string; url: string; platform: string }>;
        failed: Array<{ url: string; reason: string }>;
        attached: number;
      };
    }
  > {
    const response = await api.post(
      `/api/collections/${collectionId}/refresh-clips`,
      {},
      { timeout: 600000 },
    );
    return response.data;
  },

  async updatePrivateCollectionItems(
    collectionId: string,
    items: Array<{
      contentId: string;
      contentType:
        | "instagram_private"
        | "tiktok_private"
        | "youtube_private"
        | "spotify_private"
        | "facebook_private";
    }>,
  ): Promise<{ message: string; totalItems: number }> {
    const response = await api.put(`/api/collections/${collectionId}/items`, {
      items,
    });
    return response.data;
  },

  async getPrivateCollectionById(
    collectionId: string,
    opts?: { includeContent?: boolean },
  ): Promise<PrivateCollectionWithContent> {
    const params = new URLSearchParams();
    if (opts?.includeContent === false) {
      params.set("includeContent", "false");
    }
    const queryString = params.toString();
    const response = await api.get(
      `/api/collections/${collectionId}${queryString ? `?${queryString}` : ""}`,
    );
    return response.data;
  },

  async removePrivateCollectionItem(
    collectionId: string,
    itemId: string,
  ): Promise<{ message: string }> {
    const response = await api.delete(
      `/api/collections/${collectionId}/items/${itemId}`,
    );
    return response.data;
  },

  async deletePrivateCollection(collectionId: string): Promise<void> {
    await api.delete(`/api/collections/${collectionId}`);
  },

  /** Delete private content and all related data. Only available in development (backend returns 404 in production). */
  async deletePrivateContent(contentId: string): Promise<{ success: boolean }> {
    const response = await api.delete<{ success: boolean; message?: string }>(
      `/api/collections/content/${contentId}`,
    );
    return response.data;
  },

  // Legacy private group methods (for backward compatibility)
  async getPrivateGroupById(
    groupId: string,
  ): Promise<PrivateCollectionWithContent> {
    const response = await api.get(`/api/collections/${groupId}`);
    return response.data;
  },

  async getPrivateGroupedContent(
    podcastId: string,
  ): Promise<PrivateCollectionsContent> {
    const response = await api.get(
      `/api/collections/podcast/${podcastId}/content`,
    );
    return response.data;
  },

  async getCollectionFirstThumbnails(
    collectionIds: string[],
  ): Promise<Record<string, string | null>> {
    if (collectionIds.length === 0) return {};
    const response = await api.post("/api/collections/first-thumbnails", {
      collectionIds,
    });
    return response.data;
  },

  async getPrivateCollectionGroupedContent(
    collectionId: string,
  ): Promise<PrivateCollectionsContent> {
    const response = await api.get(`/api/collections/${collectionId}/content`);
    return response.data;
  },

  async getPublicCollectionGroupedContent(
    collectionId: string,
    brandId?: string,
  ): Promise<PrivateCollectionsContent> {
    const params = brandId ? `?brandId=${encodeURIComponent(brandId)}` : "";
    const response = await api.get(
      `/api/collections/${collectionId}/content/public${params}`,
    );
    return response.data;
  },

  async updatePrivateGroup(
    groupId: string,
    title: string,
  ): Promise<PrivateCollection> {
    const response = await api.put(`/api/collections/${groupId}`, { title });
    return response.data;
  },

  async addPrivateGroupItems(
    groupId: string,
    items: Array<{
      contentId: string;
      contentType: "instagram_private" | "tiktok_private" | "youtube_private";
    }>,
  ): Promise<void> {
    await api.post(`/api/collections/${groupId}/items`, { items });
  },

  async removePrivateGroupItem(groupId: string, itemId: string): Promise<void> {
    await api.delete(`/api/collections/${groupId}/items/${itemId}`);
  },

  async getContentSponsors(
    request: ContentSponsorRequest,
  ): Promise<ContentSponsorResponse> {
    const response = await api.post(`/api/studio/content/sponsors`, request);
    return response.data;
  },

  async createPostGroup(data: CreatePostGroupRequest): Promise<PostGroup> {
    const response = await api.post(`/api/studio/post-groups`, data);
    return response.data;
  },

  async addPostGroupItems(
    groupId: string,
    data: AddPostGroupItemsRequest,
  ): Promise<void> {
    await api.post(`/api/studio/post-groups/${groupId}/items`, data);
  },

  async getPostGroups(podcastId: string): Promise<PostGroup[]> {
    const response = await api.get(`/api/studio/podcasts/${podcastId}/groups`);
    return response.data;
  },

  async updatePostGroup(groupId: string, title: string): Promise<PostGroup> {
    const response = await api.put(`/api/studio/post-groups/${groupId}`, {
      title,
    });
    return response.data;
  },

  async deletePostGroup(groupId: string): Promise<void> {
    await api.delete(`/api/studio/post-groups/${groupId}`);
  },

  async removePostGroupItem(groupId: string, contentId: string): Promise<void> {
    await api.delete(`/api/studio/post-groups/${groupId}/items/${contentId}`);
  },

  async getPostGroupById(groupId: string): Promise<PostGroup> {
    const response = await api.get(`/api/studio/post-groups/${groupId}`);
    return response.data;
  },

  async getSponsorMentions(
    page = 1,
    limit = 10,
    podcastId?: string,
    sponsorId?: string,
    verified?: boolean,
    postId?: string,
    episodeId?: string,
  ): Promise<PaginatedResponse<SponsorMention>> {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("limit", limit.toString());
    if (podcastId) params.append("podcastId", podcastId);
    if (sponsorId) params.append("sponsorId", sponsorId);
    if (verified !== undefined) params.append("verified", verified.toString());
    if (postId) params.append("postId", postId);
    if (episodeId) params.append("episodeId", episodeId);

    const response = await api.get(
      `/api/admin/sponsor-mentions?${params.toString()}`,
    );
    return response.data;
  },

  async updateSponsorMention(
    mentionId: string,
    data: {
      verified?: boolean;
      confidence?: number;
      sponsorId?: string;
      startTime?: number;
      endTime?: number;
      brandVisible?: boolean;
      mentionType?: string;
    },
  ): Promise<SponsorMention> {
    const response = await api.put(
      `/api/admin/sponsor-mentions/${mentionId}`,
      data,
    );
    return response.data;
  },

  async createSponsorMention(data: {
    episodeId?: string;
    postId?: string;
    sponsorId: string;
    startTime: number;
    endTime: number;
    text: string;
    confidence: number;
    verified: boolean;
    brandVisible?: boolean;
    mentionType?: string;
    contentType?: string;
  }): Promise<SponsorMention> {
    console.log("creating mention", data);
    const response = await api.post(`/api/admin/sponsor-mentions`, data);
    console.log("response", response);
    return response.data;
  },

  async deleteSponsorMention(mentionId: string): Promise<void> {
    await api.delete(`/api/admin/sponsor-mentions/${mentionId}`);
  },

  async getSponsorsWithMentions(
    page = 1,
    limit = 10,
    podcastId?: string,
    search?: string,
  ): Promise<PaginatedResponse<SponsorWithMentions>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (podcastId) params.append("podcastId", podcastId);
    if (search) params.append("search", search);

    const response = await api.get<PaginatedResponse<SponsorWithMentions>>(
      `/api/admin/sponsors-with-mentions?${params}`,
    );
    return response.data;
  },

  // Sponsor management methods
  async getAdminSponsors(
    page = 1,
    limit = 10,
    search?: string,
  ): Promise<PaginatedResponse<Sponsor>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (search) params.append("search", search);

    const response = await api.get<PaginatedResponse<Sponsor>>(
      `/api/admin/sponsors?${params}`,
    );
    return response.data;
  },

  async createSponsor(data: {
    name: string;
    website?: string;
    logo?: string;
    categories?: string[];
    description?: string;
  }): Promise<Sponsor> {
    const response = await api.post<Sponsor>("/api/admin/sponsors", data);
    return response.data;
  },

  async updateSponsor(
    sponsorId: string,
    data: {
      name: string;
      website?: string;
      logo?: string;
      categories?: string[];
      description?: string;
    },
  ): Promise<Sponsor> {
    const response = await api.put<Sponsor>(
      `/api/admin/sponsors/${sponsorId}`,
      data,
    );
    return response.data;
  },

  async deleteSponsor(sponsorId: string): Promise<void> {
    await api.delete(`/api/admin/sponsors/${sponsorId}`);
  },

  // Posts management methods
  async getAdminPosts(
    page = 1,
    limit = 10,
    search?: string,
    source?: string,
  ): Promise<PaginatedResponse<StudioPost>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (search) params.append("search", search);
    if (source) params.append("source", source);

    const response = await api.get<PaginatedResponse<StudioPost>>(
      `/api/admin/posts?${params}`,
    );
    return response.data;
  },

  // Public API methods (no authentication required)
  getPublicGroup: async (groupId: string) => {
    const response = await api.get<any>(`/api/public/groups/${groupId}`);
    return response.data;
  },

  getPublicSponsor: async (sponsorId: string) => {
    const response = await api.get<any>(`/api/public/sponsors/${sponsorId}`);
    return response.data;
  },

  getPublicSponsorMentions: async (groupId: string, sponsorId: string) => {
    const response = await api.get<any[]>(
      `/api/public/groups/${groupId}/sponsors/${sponsorId}/mentions`,
    );
    return response.data;
  },

  // Additional methods needed for the copied implementation
  fetchSponsorById: async (sponsorId: string) => {
    const response = await api.get<any>(`/api/sponsors/${sponsorId}`);
    return response.data;
  },

  fetchMentionFrames: async (mentionId: string) => {
    const response = await api.get<any>(
      `/api/sponsors/mentions/${mentionId}/frames`,
    );
    return response.data;
  },

  fetchPostGroupBySlug: async (groupSlug: string) => {
    const response = await api.get<any>(`/api/post-groups/slug/${groupSlug}`);
    return response.data;
  },

  fetchContentSponsors: async (
    request: ContentSponsorRequest,
  ): Promise<ContentSponsorResponse> => {
    const response = await api.post<ContentSponsorResponse>(
      "/api/studio/content/sponsors",
      request,
    );
    return response.data;
  },

  fetchPodcastBySlug: async (slug: string) => {
    const response = await api.get<any>(`/api/podcasts/slug/${slug}`);
    return response.data;
  },
  // Public invitations (no auth header — safe on public invite pages)
  async getInvitationByToken(token: string): Promise<{
    invitation: {
      token: string;
      status: string;
      expiresAt?: string;
      platform: "meta" | "youtube" | "tiktok" | "spotify";
      podcastId: string;
      podcastTitle?: string;
      companyId: string;
      createdByEmail?: string;
      createdByName?: string;
      companyName?: string;
      companyImageUrl?: string;
      companyImageS3Key?: string;
      companySlug?: string;
      socialAccountId?: string;
      integrationSource?: "podscope" | "prixi";
      prixiPodcastId?: string;
      linkedAccount?: {
        accountId?: string;
        username?: string;
        profileUrl?: string;
      };
      targetPodcast?: {
        podcastId: string;
        title: string;
        image?: string;
        igHandle?: string;
      };
      companyPodcasts?: Array<{
        podcastId: string;
        title: string;
        image?: string;
        igHandle?: string;
        hasMetaConnection: boolean;
      }>;
      metaInviteMultiPodcastEnabled?: boolean;
    };
  }> {
    const response = await fetch(
      `${API_BASE_URL}/api/public/invitations/${encodeURIComponent(token)}`,
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw Object.assign(new Error(data?.message || "Failed to fetch invitation"), {
        response: { status: response.status, data },
      });
    }
    return data;
  },

  async getGoogleOAuthUrlForInvitation(
    token: string,
  ): Promise<{ url: string }> {
    const response = await api.get(
      `/api/public/invitations/${token}/oauth/google/url`,
    );
    return response.data;
  },

  // Political Analysis API methods
  async fetchAllPoliticalStatistics(params: {
    entityType: "candidate" | "topic";
    from: string;
    to: string;
    includeRadio?: boolean;
  }): Promise<PoliticalMention[]> {
    const response = await api.get("/api/political/statistics", { params });
    return response.data;
  },

  async fetchPoliticalPodcastsCount(params: {
    from: string;
    to: string;
    includeRadio?: boolean;
  }): Promise<{
    count: number;
    episodeCount: number;
    podcastCount: number;
    mentionsCount: number;
    totalHours: number;
  }> {
    const response = await api.get("/api/political/podcasts/count", { params });
    return response.data;
  },

  async fetchDailyPoliticalMentions(params: {
    from: string;
    to: string;
    includeRadio?: boolean;
    entityType: "candidate" | "topic";
    period?: "daily" | "weekly";
  }): Promise<
    Array<{
      date: string;
      [entity: string]: any;
    }>
  > {
    const response = await api.get("/api/political/daily-mentions", { params });
    return response.data;
  },

  async fetchCandidateMentions(params: {
    candidateName: string;
    from: string;
    to: string;
    includeRadio?: boolean;
    page?: number;
    limit?: number;
  }): Promise<{
    mentions: Array<{
      _id: string;
      mentionText: string;
      context: string;
      segmentStart: number;
      segmentEnd: number;
      duration: number;
      confidence: number;
      subtopics: string[];
      primaryTopicName: string;
      publishedAt: string;
      episodeTitle: string;
      episodeUrl: string;
      podcastName: string;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    const { candidateName, ...queryParams } = params;
    const response = await api.get(
      `/api/political/candidate-mentions/${encodeURIComponent(candidateName)}`,
      {
        params: queryParams,
      },
    );
    return response.data;
  },

  async fetchTopicMentions(params: {
    topicName: string;
    from: string;
    to: string;
    includeRadio?: boolean;
    page?: number;
    limit?: number;
  }): Promise<{
    mentions: Array<{
      _id: string;
      mentionText: string;
      context: string;
      segmentStart: number;
      segmentEnd: number;
      duration: number;
      confidence: number;
      subtopics: string[];
      primaryTopicName: string;
      publishedAt: string;
      episodeTitle: string;
      episodeUrl: string;
      podcastName: string;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    const { topicName, ...queryParams } = params;
    const response = await api.get(
      `/api/political/topic-mentions/${encodeURIComponent(topicName)}`,
      {
        params: queryParams,
      },
    );
    return response.data;
  },

  // Fetch all political podcasts for CSV export
  async fetchPoliticalPodcasts(params: { includeRadio?: boolean }): Promise<
    Array<{
      title: string;
      url: string;
      channelId: string;
      isRadioTv: boolean;
    }>
  > {
    const response = await api.get("/api/political/podcasts", {
      params,
    });
    return response.data;
  },

  // Fetch all political topics for CSV export
  async fetchPoliticalTopics(): Promise<
    Array<{
      name: string;
      aliases: string[];
    }>
  > {
    const response = await api.get("/api/political/topics");
    return response.data;
  },

  // Fetch candidate-topic cross data aggregated by week for CSV export
  async fetchCandidateTopicsWeekly(params: {
    from: string;
    to: string;
    includeRadio?: boolean;
  }): Promise<
    Array<{
      weekStart: string;
      candidate: string;
      topic: string;
      mentionCount: number;
      totalDuration: number;
      episodeCount: number;
    }>
  > {
    const response = await api.get("/api/political/candidate-topics-weekly", {
      params,
    });
    return response.data;
  },

  async fetchTopicCandidateBreakdown(params: {
    from: string;
    to: string;
    includeRadio?: boolean;
  }): Promise<{
    topics: Array<{
      topic: string;
      totalMentions: number;
      candidates: {
        [candidateName: string]: {
          count: number;
          percentage: number;
        };
      };
    }>;
    candidates: string[];
  }> {
    const response = await api.get("/api/political/topic-candidate-breakdown", {
      params,
    });
    return response.data;
  },

  async fetchCandidateTopicOverTime(params: {
    from: string;
    to: string;
    candidate: string;
    period?: "daily" | "weekly";
    includeRadio?: boolean;
  }): Promise<{
    data: Array<{
      date: string;
      total: number;
      [topicName: string]: number | string;
    }>;
    topics: string[];
    candidate: string;
  }> {
    const response = await api.get("/api/political/candidate-topic-over-time", {
      params,
    });
    return response.data;
  },

  // Instagram Content Collection
  async triggerInstagramCollection(
    podcastId: string,
    options?: {
      dryRun?: boolean;
      limit?: number;
      force?: boolean;
    },
  ): Promise<{
    success: boolean;
    message: string;
    mediaBreakdown?: string;
    result?: string;
    output?: string[];
  }> {
    const response = await api.post(
      `/api/social-accounts/podcasts/${podcastId}/instagram/collect`,
      {
        dryRun: options?.dryRun || false,
        limit: options?.limit || 25,
        force: options?.force || false,
      },
    );
    return response.data;
  },

  // Collect Facebook content for a podcast
  async collectFacebookContent(
    podcastId: string,
    options?: {
      dryRun?: boolean;
      limit?: number;
      force?: boolean;
    },
  ): Promise<{
    success: boolean;
    message: string;
    mediaBreakdown?: string;
    result?: string;
    output?: string[];
  }> {
    const response = await api.post(
      `/api/social-accounts/podcasts/${podcastId}/facebook/collect`,
      {
        dryRun: options?.dryRun || false,
        limit: options?.limit || 25,
        force: options?.force || false,
      },
    );
    return response.data;
  },

  // Get Instagram posts for a podcast
  async getInstagramPosts(
    podcastId: string,
    options?: {
      limit?: number;
      offset?: number;
    },
  ): Promise<{
    posts: Array<StudioPost>;
    pagination: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    };
  }> {
    const response = await api.get(
      `/api/social-accounts/podcasts/${podcastId}/instagram/posts`,
      {
        params: {
          limit: options?.limit || 50,
          offset: options?.offset || 0,
        },
      },
    );
    return response.data;
  },

  // NEW: Get private Instagram content (from PrivateContent model)
  async getInstagramPrivateContent(
    podcastId: string,
    options?: {
      limit?: number;
      offset?: number;
    },
  ): Promise<{
    content: any[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    };
  }> {
    const response = await api.get(
      `/api/social-accounts/podcasts/${podcastId}/instagram/private-content`,
      {
        params: {
          limit: options?.limit || 50,
          offset: options?.offset || 0,
        },
      },
    );
    return response.data;
  },

  // TikTok Content Collection
  async triggerTikTokCollection(
    podcastId: string,
    options?: {
      dryRun?: boolean;
      limit?: number;
      force?: boolean;
    },
  ): Promise<{
    success: boolean;
    message: string;
    result?: string;
    savedCount?: number;
    totalFound?: number;
  }> {
    const response = await api.post(
      `/api/social-accounts/podcasts/${podcastId}/tiktok/collect`,
      {
        dryRun: options?.dryRun || false,
        limit: options?.limit || 20,
        force: options?.force || false,
      },
    );
    return response.data;
  },

  // YouTube Content Collection
  async triggerYouTubeCollection(
    podcastId: string,
    options?: {
      dryRun?: boolean;
      limit?: number;
      force?: boolean;
    },
  ): Promise<{
    success: boolean;
    message: string;
    savedCount?: number;
    totalFound?: number;
  }> {
    const response = await api.post(
      `/api/social-accounts/podcasts/${podcastId}/youtube/collect`,
      {
        dryRun: options?.dryRun || false,
        limit: options?.limit || 20,
        force: options?.force || false,
      },
    );
    return response.data;
  },

  // Get TikTok posts for a podcast
  async getTikTokPosts(
    podcastId: string,
    options?: {
      limit?: number;
      offset?: number;
    },
  ): Promise<{
    posts: Array<StudioPost>;
    pagination: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    };
  }> {
    const response = await api.get(
      `/api/social-accounts/podcasts/${podcastId}/tiktok/posts`,
      {
        params: {
          limit: options?.limit || 50,
          offset: options?.offset || 0,
        },
      },
    );
    return response.data;
  },

  // NEW: Get private TikTok content (from PrivateContent model)
  async getTikTokPrivateContent(
    podcastId: string,
    options?: {
      limit?: number;
      offset?: number;
    },
  ): Promise<{
    content: any[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    };
  }> {
    const response = await api.get(
      `/api/social-accounts/podcasts/${podcastId}/tiktok/private-content`,
      {
        params: {
          limit: options?.limit || 50,
          offset: options?.offset || 0,
        },
      },
    );
    return response.data;
  },

  // Get YouTube posts for a podcast
  async getYouTubePosts(
    podcastId: string,
    options?: {
      limit?: number;
      offset?: number;
    },
  ): Promise<{
    posts: Array<StudioPost>;
    pagination: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    };
  }> {
    const response = await api.get(
      `/api/social-accounts/podcasts/${podcastId}/youtube/posts`,
      {
        params: {
          limit: options?.limit || 50,
          offset: options?.offset || 0,
        },
      },
    );
    return response.data;
  },

  // NEW: Get private YouTube content (from PrivateContent model)
  async getYouTubePrivateContent(
    podcastId: string,
    options?: {
      limit?: number;
      offset?: number;
    },
  ): Promise<{
    content: any[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    };
  }> {
    const response = await api.get(
      `/api/social-accounts/podcasts/${podcastId}/youtube/private-content`,
      {
        params: {
          limit: options?.limit || 50,
          offset: options?.offset || 0,
        },
      },
    );
    return response.data;
  },

  // Brand API methods
  async getAdminBrands(options?: {
    page?: number;
    limit?: number;
    search?: string;
    category?: string;
    isActive?: string;
    canSponsor?: string;
    hasImage?: string;
    minMentions?: number;
    maxMentions?: number;
    sort?: string;
  }): Promise<{
    brands: AdminBrand[];
    pagination: { page: number; limit: number; total: number; pages: number };
  }> {
    const params = new URLSearchParams();
    params.set("page", String(options?.page ?? 1));
    params.set("limit", String(options?.limit ?? 50));
    if (options?.search?.trim()) params.set("search", options.search.trim());
    if (options?.category && options.category !== "all") {
      params.set("category", options.category);
    }
    if (options?.isActive && options.isActive !== "all") {
      params.set("isActive", options.isActive);
    }
    if (options?.canSponsor && options.canSponsor !== "all") {
      params.set("canSponsor", options.canSponsor);
    }
    if (options?.hasImage && options.hasImage !== "all") {
      params.set("hasImage", options.hasImage);
    }
    if (options?.minMentions != null) {
      params.set("minMentions", String(options.minMentions));
    }
    if (options?.maxMentions != null) {
      params.set("maxMentions", String(options.maxMentions));
    }
    if (options?.sort) params.set("sort", options.sort);
    const response = await api.get<{
      brands: AdminBrand[];
      pagination: { page: number; limit: number; total: number; pages: number };
    }>(`/api/brands/admin?${params}`);
    return response.data;
  },

  async getAdminBrand(brandId: string): Promise<{ brand: AdminBrand }> {
    const response = await api.get<{ brand: AdminBrand }>(
      `/api/brands/admin/${brandId}`,
    );
    return response.data;
  },

  async patchAdminBrand(
    brandId: string,
    payload: AdminBrandPatch,
  ): Promise<{ brand: AdminBrand }> {
    const response = await api.patch<{ brand: AdminBrand }>(
      `/api/brands/admin/${brandId}`,
      payload,
    );
    return response.data;
  },

  async uploadAdminBrandLogo(
    brandId: string,
    payload:
      | { imageBase64: string; contentType?: string; fileName?: string }
      | { imageUrl: string },
  ): Promise<{ brand: AdminBrand }> {
    const response = await api.post<{ brand: AdminBrand }>(
      `/api/brands/admin/${brandId}/logo`,
      payload,
    );
    return response.data;
  },

  async deleteAdminBrand(brandId: string): Promise<{
    message: string;
    deletedBrandId: string;
    mentionsDeleted: number;
  }> {
    const response = await api.delete<{
      message: string;
      deletedBrandId: string;
      mentionsDeleted: number;
    }>(`/api/brands/admin/${brandId}`);
    return response.data;
  },

  async bulkDeleteAdminBrands(ids: string[]): Promise<{
    deletedBrandIds: string[];
    notFoundIds: string[];
    failedIds: string[];
    mentionsDeleted: number;
  }> {
    const response = await api.post<{
      deletedBrandIds: string[];
      notFoundIds: string[];
      failedIds: string[];
      mentionsDeleted: number;
    }>("/api/brands/admin/bulk-delete", { ids });
    return response.data;
  },

  async getBrandCategories(): Promise<{
    categories: string[];
  }> {
    const response = await api.get("/api/brands/categories");
    return response.data;
  },

  async getBrands(
    page = 1,
    limit = 20,
    search?: string,
    category?: string,
    canSponsor?: boolean,
    excludeUnknown?: boolean,
    names?: string,
  ): Promise<{
    brands: Array<{
      _id: string;
      name: string;
      aliases: string[];
      antiAliases?: string[];
      category?: string;
      logoUrl?: string;
      s3Key?: string;
      isActive: boolean;
      totalMentions: number;
      averageConfidence: number;
      canSponsor: boolean;
      sponsorshipNotes?: string;
      createdAt: string;
      updatedAt: string;
      lastMentionAt?: string;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    const response = await api.get("/api/brands", {
      params: {
        page,
        limit,
        search,
        category,
        canSponsor,
        excludeUnknown,
        names,
      },
    });
    return response.data;
  },

  async createBrand(data: {
    name: string;
    category?: string;
    aliases?: string[];
  }): Promise<{
    brand: {
      _id: string;
      name: string;
      aliases: string[];
      category?: string;
      canSponsor: boolean;
      isActive: boolean;
    };
  }> {
    const response = await api.post("/api/brands", data);
    return response.data;
  },

  async getBrand(brandId: string): Promise<{
    brand?: {
      _id: string;
      name: string;
      aliases: string[];
      antiAliases?: string[];
      category?: string;
      logoUrl?: string;
      s3Key?: string;
      isActive: boolean;
      totalMentions: number;
      averageConfidence: number;
      canSponsor: boolean;
      sponsorshipNotes?: string;
      createdAt: string;
      updatedAt: string;
      lastMentionAt?: string;
    };
    brands?: Array<{
      _id: string;
      name: string;
      aliases: string[];
      antiAliases?: string[];
      category?: string;
      logoUrl?: string;
      s3Key?: string;
      isActive: boolean;
      totalMentions: number;
      averageConfidence: number;
      canSponsor: boolean;
      sponsorshipNotes?: string;
      createdAt: string;
      updatedAt: string;
      lastMentionAt?: string;
    }>;
    isCombinedReport?: boolean;
  }> {
    const response = await api.get(`/api/brands/${brandId}`);
    return response.data;
  },

  async updateBrandAliases(
    brandId: string,
    aliases?: string[],
    antiAliases?: string[],
  ): Promise<{
    message: string;
    brand: {
      _id: string;
      name: string;
      aliases: string[];
      antiAliases?: string[];
    };
  }> {
    const body: { aliases?: string[]; antiAliases?: string[] } = {};
    if (aliases !== undefined) body.aliases = aliases;
    if (antiAliases !== undefined) body.antiAliases = antiAliases;
    const response = await api.put(`/api/brands/${brandId}/aliases`, body);
    return response.data;
  },

  async updateBrandCategory(
    brandId: string,
    category: string | null,
  ): Promise<{
    message: string;
    brand: {
      _id: string;
      name: string;
      category?: string | null;
    };
  }> {
    const response = await api.patch(`/api/brands/${brandId}/category`, {
      category,
    });
    return response.data;
  },

  async mergeBrand(
    sourceBrandId: string,
    targetBrandId: string,
  ): Promise<{
    message: string;
    mentionsUpdated: number;
    mentionsMatched: number;
    aliasesAdded: string[];
    deletedBrandId: string;
    sourceName: string;
    targetName: string;
  }> {
    const response = await api.post(
      `/api/brands/${sourceBrandId}/merge`,
      { targetBrandId },
    );
    return response.data;
  },

  async getBrandData(
    brandId: string,
    year?: number,
    source?: string,
    startDate?: string,
    endDate?: string,
  ): Promise<{
    brand: {
      _id: string;
      name: string;
      category?: string;
      logoUrl?: string;
      s3Key?: string;
    };
    periods: Array<{
      year: number;
      podcasts: Array<{
        _id: string;
        title: string;
        imageUrl?: string;
        imageS3Key?: string;
        mentionCount: number;
      }>;
      programs: Array<{
        _id: string;
        name: string;
        imageS3Key?: string;
        podcast: {
          _id: string;
          title: string;
          imageUrl?: string;
          imageS3Key?: string;
        } | null;
        mentionCount: number;
      }>;
      mentions: Array<{
        _id: string;
        contentId: string;
        segmentStart: number;
        segmentEnd: number;
        duration: number;
        context: string;
        confidence: number;
        mentionType: string;
        detectionSource?: string;
        detectionMethod?: string;
        contentForm?: "long" | "short";
        content: {
          _id: string;
          title: string;
          url?: string;
          youtubeId?: string;
          publishedAt: string;
          source: string;
          mediaType: string;
          sourceId?: string;
          livestream?: {
            _id: string;
            title?: string;
            peakViewers?: number;
            averageViewers?: number;
            /** Concurrent viewers at the moment of the mention (for "Views en vivo" sum) */
            concurrentViewersAtMention?: number | null;
          };
          thumbnail?: string;
          imageUrl?: string;
          imageS3Key?: string;
          thumbnailS3Key?: string;
          duration?: number;
          views?: number;
          likes?: number;
          comments?: number;
          insights?: {
            owner?: {
              username: string;
              fullName: string;
              id: string;
              isVerified?: boolean;
              profilePicUrl?: string;
            };
            accountMetadata?: {
              followersCount?: number;
              followingCount?: number;
              postsCount?: number;
              isVerified?: boolean;
              isBusinessAccount?: boolean;
              category?: string;
              biography?: string;
              profilePicUrl?: string;
              profilePicS3Key?: string;
            };
          };
        };
        podcast: {
          _id: string;
          title: string;
          imageUrl?: string;
          imageS3Key?: string;
        };
        program: {
          _id: string;
          name: string;
          imageS3Key?: string;
        } | null;
      }>;
      /** YouTube section stats and shorts-by-account (Resumen + table) */
      youtubeSection?: {
        statistics: {
          totalMentions: number;
          uniqueEpisodes: number;
          uniquePrograms: number;
          uniquePodcasts: number;
          accumulatedViews: number;
        };
        youtubeShortsByAccount: Array<{
          channelId: string;
          channelName: string;
          imageUrl?: string;
          imageS3Key?: string;
          totalShorts: number;
          totalViews: number;
          totalLikes: number;
          totalComments: number;
        }>;
      };
      /** Per-creator short-form stats (deduped by contentId); list and creator-detail use same source */
      shortFormByCreator?: {
        instagram: Array<{
          creatorId: string;
          username: string;
          profilePicUrl?: string;
          profilePicS3Key?: string;
          totalReels: number;
          totalViews: number;
          totalLikes: number;
          totalComments: number;
        }>;
        tiktok: Array<{
          creatorId: string;
          username: string;
          profilePicUrl?: string;
          profilePicS3Key?: string;
          totalReels: number;
          totalViews: number;
          totalLikes: number;
          totalComments: number;
        }>;
      };
    }>;
  }> {
    const params = new URLSearchParams();
    if (year) {
      params.append("year", year.toString());
    }
    if (source) {
      params.append("source", source);
    }
    if (startDate) {
      params.append("startDate", startDate);
    }
    if (endDate) {
      params.append("endDate", endDate);
    }
    const response = await api.get(
      `/api/brands/${brandId}/brand-data?${params}`,
    );
    return response.data;
  },

  async getBrandProgramDetail(
    brandId: string,
    programId: string,
    year?: number,
    startDate?: string,
    endDate?: string,
  ): Promise<{
    brand: {
      _id: string;
      name: string;
      category?: string;
      logoUrl?: string;
      s3Key?: string;
    };
    program: {
      _id: string;
      name: string;
      imageS3Key?: string;
      description?: string;
    };
    podcast: {
      _id: string;
      title: string;
      imageUrl?: string;
      imageS3Key?: string;
    } | null;
    year: number | null;
    statistics: {
      totalMentions: number;
      uniqueEpisodes: number;
      accumulatedViews: number;
    };
    mentions: Array<{
      _id: string;
      contentId: string;
      segmentStart: number;
      segmentEnd: number;
      duration: number;
      context: string;
      confidence: number;
      mentionType: string;
      content: {
        _id: string;
        title: string;
        url?: string;
        youtubeId?: string;
        publishedAt: string;
        source: string;
        mediaType: string;
        thumbnail?: string;
        thumbnailS3Key?: string;
        imageUrl?: string;
        imageS3Key?: string;
        duration?: number;
        views?: number;
        likes?: number;
        comments?: number;
        insights?: {
          owner?: {
            username: string;
            fullName: string;
            id: string;
            isVerified?: boolean;
            profilePicUrl?: string;
          };
          accountMetadata?: {
            followersCount?: number;
            followingCount?: number;
            postsCount?: number;
            isVerified?: boolean;
            isBusinessAccount?: boolean;
            category?: string;
            biography?: string;
            profilePicUrl?: string;
          };
        };
      };
    }>;
  }> {
    const params = new URLSearchParams();
    if (year) params.append("year", year.toString());
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    const response = await api.get(
      `/api/brands/${brandId}/programs/${programId}?${params}`,
    );
    return response.data;
  },

  async getBrandLivestreamProgramDetail(
    brandId: string,
    programId: string,
    startDate?: string,
    endDate?: string,
  ): Promise<{
    brand: {
      _id: string;
      name: string;
      category?: string;
      logoUrl?: string;
      s3Key?: string;
    };
    program: {
      _id: string;
      name: string;
      imageS3Key?: string;
      description?: string;
    };
    podcast: {
      _id: string;
      title: string;
      imageUrl?: string;
      imageS3Key?: string;
    } | null;
    year: number | null;
    statistics: {
      totalMentions: number;
      uniqueEpisodes: number;
      accumulatedLiveViewers: number;
      peakViewers: number;
      averageViewers: number;
      chatMessages: number;
      chatUniqueUsers: number;
    };
    mentions: Array<{
      _id: string;
      contentId: string;
      segmentStart: number;
      segmentEnd: number;
      duration: number;
      context: string;
      content: {
        _id: string;
        title: string;
        url?: string;
        publishedAt: string;
        thumbnail?: string;
        thumbnailS3Key?: string;
        imageUrl?: string;
        imageS3Key?: string;
        duration?: number;
        views?: number;
        livestream?: {
          _id: string;
          title?: string;
          peakViewers?: number;
          averageViewers?: number;
          concurrentViewersAtMention?: number | null;
          chatMessageCount?: number;
          chatUniqueUsers?: number;
        };
      };
    }>;
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    const response = await api.get(
      `/api/brands/${brandId}/programs/${programId}/livestream?${params}`,
    );
    return response.data;
  },

  async getBrandCreatorDetail(
    brandId: string,
    creatorId: string,
    year?: number,
    source?: string,
    startDate?: string,
    endDate?: string,
    platform?: "instagram" | "tiktok",
    sortBy?: string,
    sortOrder?: "asc" | "desc",
  ): Promise<{
    brand: {
      _id: string;
      name: string;
      category?: string;
      logoUrl?: string;
      s3Key?: string;
    };
    creator: {
      creatorId: string;
      username: string;
      fullName?: string;
      profilePicUrl?: string;
      profilePicS3Key?: string;
      isVerified?: boolean;
      followersCount?: number;
      platform?: "instagram" | "tiktok";
    };
    year: number | null;
    statistics?: {
      totalReels: number;
      totalViews: number;
      totalLikes: number;
      totalComments: number;
    };
    reels: Array<{
      _id: string;
      contentId: string;
      segmentStart: number;
      segmentEnd: number;
      duration: number;
      context: string;
      confidence: number;
      mentionType: string;
      content: {
        _id: string;
        title: string;
        url?: string;
        publishedAt: string;
        source: string;
        mediaType: string;
        thumbnail?: string;
        thumbnailS3Key?: string;
        imageUrl?: string;
        imageS3Key?: string;
        duration?: number;
        views?: number;
        likes?: number;
        comments?: number;
        insights?: {
          owner?: {
            username: string;
            fullName: string;
            id: string;
            isVerified?: boolean;
            profilePicUrl?: string;
          };
          accountMetadata?: {
            followersCount?: number;
            followingCount?: number;
            postsCount?: number;
            isVerified?: boolean;
            isBusinessAccount?: boolean;
            category?: string;
            biography?: string;
            profilePicUrl?: string;
          };
        };
      };
    }>;
  }> {
    const params = new URLSearchParams();
    if (year) params.append("year", year.toString());
    if (source) params.append("source", source);
    if (platform) params.append("platform", platform);
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (sortBy) params.append("sortBy", sortBy);
    if (sortOrder) params.append("sortOrder", sortOrder);
    const response = await api.get(
      `/api/brands/${brandId}/creators/${creatorId}?${params}`,
    );
    return response.data;
  },

  async getBrandMentions(
    brandId: string,
    page = 1,
    limit = 20,
    podcastId?: string,
    timePeriod?: string,
  ): Promise<{
    mentions: Array<{
      _id: string;
      contentId: string;
      brandId?: string;
      rawMentionName?: string;
      brandName: string;
      startTime: number;
      endTime: number;
      duration: number;
      context: string;
      confidence: number;
      category?: string;
      classification: "paid" | "organic" | "unknown";
      fragmentIndex?: number;
      createdAt: string;
      updatedAt: string;
      content?: {
        _id: string;
        title: string;
        url?: string;
        youtubeId?: string;
        publishedAt: string;
        source: string;
        mediaType: string;
        thumbnail?: string;
        duration?: number;
        podcast?: {
          _id: string;
          title: string;
          imageUrl?: string;
        };
      };
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    const response = await api.get(`/api/brands/${brandId}/mentions`, {
      params: {
        page,
        limit,
        podcastId,
        timePeriod,
      },
    });
    return response.data;
  },

  async searchBrandMentions(
    page = 1,
    limit = 20,
    search?: string,
    brandName?: string,
    podcastId?: string,
    classification?: string,
  ): Promise<{
    mentions: Array<{
      _id: string;
      contentId: string;
      brandId?: string;
      rawMentionName?: string;
      brandName: string;
      startTime: number;
      endTime: number;
      duration: number;
      context: string;
      confidence: number;
      category?: string;
      classification: "paid" | "organic" | "unknown";
      fragmentIndex?: number;
      createdAt: string;
      updatedAt: string;
      content?: {
        _id: string;
        title: string;
        url?: string;
        youtubeId?: string;
        publishedAt: string;
        source: string;
        mediaType: string;
        thumbnail?: string;
        duration?: number;
        podcast?: {
          _id: string;
          title: string;
          imageUrl?: string;
        };
      };
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    const response = await api.get("/api/brands/mentions/search", {
      params: {
        page,
        limit,
        search,
        brandName,
        podcastId,
        classification,
      },
    });
    return response.data;
  },

  async getBrandStats(): Promise<{
    totalBrands: number;
    totalMentions: number;
    sponsoringBrands: number;
    nonSponsoringBrands: number;
    topBrands: Array<{
      _id: string;
      name: string;
      totalMentions: number;
      averageConfidence: number;
      category?: string;
    }>;
  }> {
    const response = await api.get("/api/brands/stats/overview");
    return response.data;
  },

  async getBrandPodcasts(
    search?: string,
    limit = 100,
  ): Promise<{
    podcasts: Array<{
      _id: string;
      title: string;
      imageUrl?: string;
      author?: string;
    }>;
  }> {
    const response = await api.get("/api/brands/podcasts", {
      params: {
        search,
        limit,
      },
    });
    return response.data;
  },

  async getPodcastBrandMentions(
    podcastId: string,
    page = 1,
    limit = 20,
    brandFilter?: string,
    classificationFilter?: string,
  ): Promise<{
    mentions: Array<{
      _id: string;
      contentId: string;
      brandId?: string;
      rawMentionName?: string;
      brandName: string;
      startTime: number;
      endTime: number;
      duration: number;
      context: string;
      confidence: number;
      category?: string;
      classification: "paid" | "organic" | "unknown";
      fragmentIndex?: number;
      createdAt: string;
      updatedAt: string;
      content?: {
        _id: string;
        title: string;
        url?: string;
        youtubeId?: string;
        publishedAt: string;
        source: string;
        mediaType: string;
        thumbnail?: string;
        duration?: number;
      };
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (brandFilter) params.append("brand", brandFilter);
    if (classificationFilter)
      params.append("classification", classificationFilter);

    const response = await api.get(
      `/api/podcasts/${podcastId}/brand-mentions?${params}`,
    );
    return response.data;
  },

  async getEpisodeBrandMentions(
    podcastId: string,
    contentId: string,
    page = 1,
    limit = 20,
    brand?: string,
    classification?: string,
  ): Promise<{
    mentions: Array<{
      _id: string;
      contentId: string;
      brandId?: string;
      rawMentionName?: string;
      brandName: string;
      startTime: number;
      endTime: number;
      duration: number;
      context: string;
      confidence: number;
      category?: string;
      classification: "paid" | "organic" | "unknown";
      fragmentIndex?: number;
      createdAt: string;
      updatedAt: string;
      content?: {
        _id: string;
        title: string;
        url?: string;
        youtubeId?: string;
        publishedAt: string;
        source: string;
        mediaType: string;
        thumbnail?: string;
        duration?: number;
        podcast?: {
          _id: string;
          title: string;
          imageUrl?: string;
        };
      };
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (brand) {
      params.append("brand", brand);
    }

    if (classification) {
      params.append("classification", classification);
    }

    const response = await api.get(
      `/api/podcasts/${podcastId}/episodes/${contentId}/brand-mentions?${params}`,
    );
    return response.data;
  },

  // Fetch private brand mentions for content IDs
  async getPrivateBrandMentions(
    contentIds: string[],
    brandId?: string,
    collectionId?: string,
  ): Promise<{ [key: string]: PrivateBrandMention[] }> {
    const response = await api.post("/api/collections/brand-mentions", {
      contentIds,
      brandId,
      collectionId,
    });
    return response.data;
  },

  // Fetch private brand presence for content IDs
  async createPrivateBrandPresence(
    contentId: string,
    brandId: string,
    brandName: string,
    brandLogo?: string,
  ): Promise<any> {
    const response = await api.post("/api/collections/brand-presence/create", {
      contentId,
      brandId,
      brandName,
      brandLogo,
    });
    return response.data;
  },

  // Create private brand mention (duplicate)
  async createPrivateBrandMention(
    mentionData: Partial<PrivateBrandMention>,
  ): Promise<{ message: string; mention: PrivateBrandMention }> {
    const response = await api.post(
      "/api/collections/brand-mentions/create",
      mentionData,
    );
    return response.data;
  },

  async deletePrivateBrandPresence(presenceId: string): Promise<any> {
    const response = await api.delete(
      `/api/collections/brand-presence/${presenceId}`,
    );
    return response.data;
  },

  async updatePrivateBrandPresencePlacementType(
    presenceId: string,
    placementType: "tagged" | "placement" | "overlay",
  ): Promise<void> {
    await api.patch(`/api/collections/brand-presence/${presenceId}`, {
      placementType,
    });
  },

  async getPrivateBrandPresence(
    contentIds: string[],
    brandId?: string,
    collectionId?: string,
    opts?: {
      /** When 1–2 of tagged | placement | overlay, server filters short-form presences; omit when all three (default). */
      placementTypes?: ("tagged" | "placement" | "overlay")[];
    },
  ): Promise<{ [key: string]: PrivateBrandPresence[] }> {
    const allShort: ("tagged" | "placement" | "overlay")[] = [
      "tagged",
      "placement",
      "overlay",
    ];
    const raw = opts?.placementTypes?.filter(Boolean) ?? [];
    const uniq = Array.from(new Set(raw));
    const narrowed =
      uniq.length > 0 && uniq.length < allShort.length
        ? uniq.filter((t): t is "tagged" | "placement" | "overlay" =>
            allShort.includes(t),
          )
        : undefined;

    const response = await api.post("/api/collections/brand-presence", {
      contentIds,
      brandId,
      collectionId,
      ...(narrowed?.length ? { placementTypes: narrowed } : {}),
    });
    return response.data;
  },

  async getPrivateContentById(
    contentId: string,
    opts?: { collectionId?: string },
  ): Promise<PrivateContent> {
    const q =
      opts?.collectionId != null && opts.collectionId !== ""
        ? `?collectionId=${encodeURIComponent(opts.collectionId)}`
        : "";
    const response = await api.get(`/api/collections/content/${contentId}${q}`);
    return response.data;
  },

  // Content Matching API methods
  async triggerContentMatching(podcastId: string): Promise<{
    success: boolean;
    message: string;
    data: {
      newMatches: number;
      totalProcessed: number;
      errors: string[];
    };
  }> {
    const response = await api.post(
      `/api/content-matching/match-podcast/${podcastId}`,
    );
    return response.data;
  },

  async triggerContentMatchingWithDateRange(
    podcastId: string,
    dateFrom: string,
    dateTo: string,
  ): Promise<{
    success: boolean;
    message: string;
    data: {
      newMatches: number;
      totalProcessed: number;
      errors: string[];
      dateFrom: string;
      dateTo: string;
    };
  }> {
    const response = await api.post(
      `/api/content-matching/match-podcast/${podcastId}/date-range`,
      {
        dateFrom,
        dateTo,
      },
    );
    return response.data;
  },

  async triggerSeptemberContentMatching(
    podcastId: string,
    year?: number,
  ): Promise<{
    success: boolean;
    message: string;
    data: {
      newMatches: number;
      totalProcessed: number;
      errors: string[];
      year: number;
    };
  }> {
    const response = await api.post(
      `/api/content-matching/match-podcast/${podcastId}/september`,
      { year },
    );
    return response.data;
  },

  async getContentLinks(contentIds: string[]): Promise<{ [key: string]: any }> {
    const response = await api.post(`/api/content-matching/links`, {
      contentIds,
    });
    return response.data.data;
  },

  async verifyContentMatch(linkId: string, verified: boolean): Promise<any> {
    const response = await api.put(`/api/content-matching/verify/${linkId}`, {
      verified,
    });
    return response.data;
  },

  async deleteContentMatch(linkId: string): Promise<void> {
    await api.delete(`/api/content-matching/links/${linkId}`);
  },

  async runGlobalContentMatching(): Promise<{
    success: boolean;
    message: string;
    data: {
      totalPodcasts: number;
      totalMatches: number;
      errors: string[];
    };
  }> {
    const response = await api.post("/api/content-matching/run-global");
    return response.data;
  },

  // Spotify Metrics API methods
  async uploadSpotifyMetrics(
    podcastId: string,
    metrics: Array<{
      episode_name: string;
      episode_url: string;
      publish_date: string;
      starts: number;
      streams: number;
      unique_listeners: number;
      average_listen_pct: number;
      impressions: number;
      followers_gained: number;
    }>,
  ): Promise<{
    success: boolean;
    message: string;
    data: {
      totalProcessed: number;
      updatedCount: number;
      createdCount: number;
      errors: string[];
    };
  }> {
    const response = await api.post(
      `/api/spotify-metrics/upload/${podcastId}`,
      { metrics },
    );
    return response.data;
  },

  async updateContentSpotifyMetrics(
    contentId: string,
    metrics: {
      streams: number;
      uniqueListeners: number;
      averageListenPct?: number;
    },
  ): Promise<{
    success: boolean;
    message: string;
    data: {
      contentId: string;
      insights: any;
    };
  }> {
    const response = await api.put(
      `/api/collections/content/${contentId}/spotify-metrics`,
      metrics,
    );
    return response.data;
  },

  async updateYoutubeOAuthManualMetrics(
    contentId: string,
    payload: {
      uniqueViewers?: number;
      thumbnailImpressions?: number;
    },
  ): Promise<{
    success: boolean;
    message: string;
    data: {
      contentId: string;
      insights: PrivateContent["insights"];
    };
  }> {
    const id = String(contentId ?? "")
      .trim()
      .replace(/^["']|["']$/g, "");
    if (!id || id === "undefined" || id === "null") {
      return Promise.reject(new Error("ID de contenido no válido"));
    }
    const response = await api.put(
      `/api/collections/content/${encodeURIComponent(id)}/youtube-oauth-metrics`,
      payload,
    );
    return response.data;
  },

  // Content Collection API methods
  async triggerSpotifyCollection(
    podcastId: string,
    options: {
      dryRun?: boolean;
      limit?: number;
      force?: boolean;
    } = {},
  ): Promise<{
    success: boolean;
    message: string;
    savedCount: number;
    totalFound: number;
  }> {
    const response = await api.post(
      `/api/social-accounts/podcasts/${podcastId}/spotify/collect`,
      options,
    );
    return response.data;
  },

  async getTopicContent(
    topicId: string,
    page = 1,
    limit = 20,
    timePeriod?: string,
    week?: string,
  ): Promise<{
    topic: {
      _id: string;
      name: string;
      displayName: string;
      category: string;
      description?: string;
    };
    content: Array<{
      _id: string;
      title: string;
      podcastId: string;
      podcastTitle: string;
      publishedAt: string;
      duration: number;
      topicDuration: number;
      topicPercentage: number;
      confidence: number;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (timePeriod) params.append("timePeriod", timePeriod);
    if (week) params.append("week", week);

    const response = await api.get(
      `/api/podcasts/topics/${topicId}/content?${params.toString()}`,
    );
    return response.data;
  },

  // Livestreams
  async getLivestream(livestreamId: string): Promise<{
    _id: string;
    videoId: string;
    url: string;
    title: string;
    channelName: string;
    channelId: string;
    status:
      | "scheduled"
      | "live"
      | "ended"
      | "processing"
      | "completed"
      | "error";
    actualStartTime?: string;
    actualEndTime?: string;
    totalDuration?: number; // Duration in seconds
    releaseTime?: string; // Official stream start (e.g. from YouTube)
    youtubeDuration?: number; // Official duration in seconds (from YouTube)
    peakViewers?: number;
    averageViewers?: number;
    finalLikeCount?: number;
    chatMessageCount: number;
    chatUniqueUsers?: number;
    editOffset?: number; // Time in seconds removed from the beginning of the YouTube video
    channelImageS3Key?: string; // From livestream_channels.imageS3Key
    chatMessages?: Array<{
      messageId: string;
      authorName: string;
      message: string;
      publishedAt: string;
      type: string;
      superChatAmount?: string;
    }>;
    createdAt: string;
    updatedAt: string;
  }> {
    const response = await api.get(`/api/livestreams/${livestreamId}`);
    return response.data;
  },

  async getLivestreamByVideoId(
    videoId: string,
    opts?: { collectionId?: string },
  ): Promise<{
    _id: string;
    videoId: string;
    title: string;
    channelName: string;
    actualStartTime?: string;
    actualEndTime?: string;
    totalDuration?: number;
    releaseTime?: string;
    youtubeDuration?: number;
    peakViewers?: number;
    averageViewers?: number;
    finalChatCount?: number;
    finalChatUniqueUsers?: number;
    finalLikeCount?: number;
    finalCommentCount?: number;
  } | null> {
    try {
      const q =
        opts?.collectionId != null && opts.collectionId !== ""
          ? `?collectionId=${encodeURIComponent(opts.collectionId)}`
          : "";
      const response = await api.get(
        `/api/livestreams/by-video-id/${encodeURIComponent(videoId)}${q}`,
      );
      return response.data;
    } catch (err: any) {
      if (err?.response?.status === 404 || err?.response?.status === 403)
        return null;
      throw err;
    }
  },

  /** Same payload as getLivestreamByVideoId; lookup by livestream_streams _id (fallback when video id on content mismatches DB). */
  async getLivestreamReportSummaryByMongoId(
    livestreamId: string,
    opts?: { collectionId?: string },
  ): Promise<{
    _id: string;
    videoId: string;
    title: string;
    channelName: string;
    actualStartTime?: string;
    actualEndTime?: string;
    totalDuration?: number;
    releaseTime?: string;
    youtubeDuration?: number;
    peakViewers?: number;
    averageViewers?: number;
    finalChatCount?: number;
    finalChatUniqueUsers?: number;
    finalLikeCount?: number;
    finalCommentCount?: number;
  } | null> {
    try {
      const q =
        opts?.collectionId != null && opts.collectionId !== ""
          ? `?collectionId=${encodeURIComponent(opts.collectionId)}`
          : "";
      const response = await api.get(
        `/api/livestreams/report-summary/${encodeURIComponent(livestreamId)}${q}`,
      );
      return response.data;
    } catch (err: any) {
      if (err?.response?.status === 404 || err?.response?.status === 403)
        return null;
      throw err;
    }
  },

  async getLivestreamReportSummaries(args: {
    videoIds: string[];
    livestreamIds?: string[];
    collectionId?: string;
  }): Promise<{
    byVideoId: Record<
      string,
      {
        _id: string;
        videoId: string;
        title: string;
        channelName: string;
        actualStartTime?: string;
        actualEndTime?: string;
        totalDuration?: number;
        releaseTime?: string;
        youtubeDuration?: number;
        peakViewers?: number;
        averageViewers?: number;
        finalChatCount?: number;
        finalChatUniqueUsers?: number;
        finalLikeCount?: number;
        finalCommentCount?: number;
      }
    >;
    byLivestreamId: Record<
      string,
      {
        _id: string;
        videoId: string;
        title: string;
        channelName: string;
        actualStartTime?: string;
        actualEndTime?: string;
        totalDuration?: number;
        releaseTime?: string;
        youtubeDuration?: number;
        peakViewers?: number;
        averageViewers?: number;
        finalChatCount?: number;
        finalChatUniqueUsers?: number;
        finalLikeCount?: number;
        finalCommentCount?: number;
      }
    >;
  }> {
    try {
      const q =
        args.collectionId != null && args.collectionId !== ""
          ? `?collectionId=${encodeURIComponent(args.collectionId)}`
          : "";
      const response = await api.post(`/api/livestreams/report-summaries${q}`, {
        videoIds: args.videoIds,
        livestreamIds: args.livestreamIds ?? [],
      });
      return response.data;
    } catch (err: any) {
      if (err?.response?.status === 403) {
        return { byVideoId: {}, byLivestreamId: {} };
      }
      throw err;
    }
  },

  async getLivestreamSnapshots(livestreamId: string): Promise<{
    snapshots: Array<{
      _id: string;
      timestamp: string;
      concurrentViewers: number;
      likeCount: number;
      totalChatCount?: number;
    }>;
  }> {
    const response = await api.get(
      `/api/livestreams/${livestreamId}/snapshots`,
    );
    return response.data;
  },

  async getLivestreamDailyMetrics(
    livestreamId: string,
  ): Promise<ContentDailyMetricsResponse> {
    const response = await api.get<ContentDailyMetricsResponse>(
      `/api/livestreams/${encodeURIComponent(livestreamId)}/daily-metrics`,
    );
    return response.data;
  },

  async getLivestreamTimeline(
    livestreamId: string,
    interval = 300,
    aggregated = true,
    opts?: { collectionId?: string },
  ): Promise<LivestreamTimelineResponse | null> {
    try {
      const params = new URLSearchParams();
      params.set("interval", String(interval));
      params.set("aggregated", String(aggregated));
      if (opts?.collectionId != null && opts.collectionId !== "") {
        params.set("collectionId", opts.collectionId);
      }
      const response = await api.get<LivestreamTimelineResponse>(
        `/api/livestreams/${livestreamId}/timeline?${params.toString()}`,
      );
      return response.data;
    } catch (err: any) {
      if (err?.response?.status === 403 || err?.response?.status === 404)
        return null;
      throw err;
    }
  },

  // Explorer API methods
  async getExplorerPrograms(
    startDate?: string,
    endDate?: string,
    taxonomy: PodcastTaxonomyFilters = {},
    sortBy = "totalViews",
    sortOrder = "desc",
    search?: string,
    withTrends = false,
    signal?: AbortSignal,
    podcastId?: string,
    page?: number,
    limit?: number,
    contentCategory?: string | string[],
    ids?: string[],
    exportDetails?: boolean,
    withBrands?: boolean,
  ): Promise<{
    programs: Array<{
      _id: string;
      name: string;
      imageS3Key?: string;
      podcast: {
        _id: string;
        title: string;
        type?: "network" | "show" | null;
        imageS3Key?: string;
        imageUrl?: string;
      };
      contentCount: number;
      totalViews: number;
      /** Sum of likes+comments at ~day 7 per episode in the range (incomplete episodes contribute 0). */
      interactionsAt7Days: number;
      viewsPerEp: number;
      totalInteractions: number;
      engagementRate: number;
      uniquePaidBrandsCount?: number;
      /** Company rate-card investment estimate (only when flag enabled). */
      estimatedInvestment?: number;
      /** Set when exportDetails=true — all paid placements (credits, placement, content). */
      paidBrandNames?: string[];
      positionDelta?: number | null;
      viewsDelta?: number | null;
      isNew?: boolean;
      globalRank?: number | null;
    }>;
    pagination?: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    appendPodcastTaxonomyParams(params, taxonomy);
    if (sortBy) params.append("sortBy", sortBy);
    if (sortOrder) params.append("sortOrder", sortOrder);
    if (search) params.append("search", search);
    if (withTrends) params.append("withTrends", "true");
    if (podcastId) params.append("podcastId", podcastId);
    if (page !== undefined) params.append("page", page.toString());
    if (limit !== undefined) params.append("limit", limit.toString());
    if (contentCategory) {
      const cats = Array.isArray(contentCategory)
        ? contentCategory
        : [contentCategory];
      cats.forEach((c) => params.append("contentCategory", c));
    }
    if (ids && ids.length > 0) {
      ids.forEach((id) => params.append("ids", id));
    }
    if (exportDetails) params.append("exportDetails", "true");
    if (withBrands) params.append("withBrands", "true");

    const response = await api.get(
      `/api/explorer/programs?${params.toString()}`,
      { signal },
    );
    return response.data;
  },

  async getExplorerProgramsBrandCounts(
    startDate: string,
    endDate: string,
    programIds: string[],
    signal?: AbortSignal,
  ): Promise<{ brandCounts: Record<string, number> }> {
    const params = new URLSearchParams();
    params.append("startDate", startDate);
    params.append("endDate", endDate);
    programIds.forEach((id) => params.append("ids", id));

    const response = await api.get(
      `/api/explorer/programs/brand-counts?${params.toString()}`,
      { signal },
    );
    return response.data;
  },

  async getExplorerProgramsInvestment(
    startDate: string,
    endDate: string,
    programIds: string[],
    signal?: AbortSignal,
  ): Promise<{ investments: Record<string, number> }> {
    const params = new URLSearchParams();
    params.append("startDate", startDate);
    params.append("endDate", endDate);
    programIds.forEach((id) => params.append("ids", id));

    const response = await api.get(
      `/api/explorer/programs/investment?${params.toString()}`,
      { signal },
    );
    return response.data;
  },

  async getExplorerProgramsAutocomplete(
    q?: string,
    taxonomy: PodcastTaxonomyFilters = {},
    limit = 20,
    startDate?: string,
    endDate?: string,
    podcastIds?: string[],
    contentCategories?: string[],
  ): Promise<{
    programs: Array<{
      _id: string;
      name: string;
      imageS3Key?: string;
      podcast: {
        _id: string;
        title: string;
        imageS3Key?: string;
        imageUrl?: string;
        imageUpdatedAt?: string | null;
      };
    }>;
  }> {
    const params = new URLSearchParams();
    if (q) params.append("q", q);
    appendPodcastTaxonomyParams(params, taxonomy);
    params.append("limit", limit.toString());
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (podcastIds && podcastIds.length > 0)
      params.append("podcastIds", podcastIds.join(","));
    if (contentCategories && contentCategories.length > 0) {
      contentCategories.forEach((c) => params.append("contentCategory", c));
    }
    const response = await api.get(
      `/api/explorer/programs/autocomplete?${params.toString()}`,
    );
    return response.data;
  },

  async getExplorerProgramPositionDeltas(
    startDate: string,
    endDate: string,
    taxonomy: PodcastTaxonomyFilters = {},
    sortBy = "totalViews",
    sortOrder = "desc",
  ): Promise<{
    deltas: Record<
      string,
      {
        positionDelta: number | null;
        isNew: boolean;
        globalPosition: number | null;
      }
    >;
  }> {
    const params = new URLSearchParams();
    params.append("startDate", startDate);
    params.append("endDate", endDate);
    appendPodcastTaxonomyParams(params, taxonomy);
    params.append("sortBy", sortBy);
    params.append("sortOrder", sortOrder);

    const response = await api.get(
      `/api/explorer/programs/position-deltas?${params.toString()}`,
    );
    return response.data;
  },

  async getExplorerProgramsWeeklyMovements(
    anchor?: string,
    taxonomy: PodcastTaxonomyFilters = {},
    window: "week" | "month" = "week",
  ): Promise<{
    period: {
      current: { start: string; end: string };
      previous: { start: string; end: string };
    };
    risers: Array<{
      _id: string;
      name: string;
      podcastTitle: string;
      imageS3Key: string | null;
      podcastImageS3Key?: string | null;
      podcastImageUrl?: string | null;
      gainedBrands?: number | null;
    }>;
    fallers: Array<{
      _id: string;
      name: string;
      podcastTitle: string;
      imageS3Key: string | null;
      podcastImageS3Key?: string | null;
      podcastImageUrl?: string | null;
    }>;
    newEntries: Array<{
      _id: string;
      name: string;
      podcastTitle: string;
      imageS3Key: string | null;
      podcastImageS3Key?: string | null;
      podcastImageUrl?: string | null;
    }>;
    leaving: Array<{
      _id: string;
      name: string;
      podcastTitle: string;
      imageS3Key: string | null;
      podcastImageS3Key?: string | null;
      podcastImageUrl?: string | null;
    }>;
  }> {
    const params = new URLSearchParams();
    if (anchor) params.append("anchor", anchor);
    if (window && window !== "week") params.append("window", window);
    appendPodcastTaxonomyParams(params, taxonomy);
    const query = params.toString();
    const response = await api.get(
      `/api/explorer/programs/weekly-movements${query ? `?${query}` : ""}`,
    );
    return response.data;
  },

  async getExplorerBrandsWeeklyMovements(
    anchor?: string,
    newEntriesLimit?: number,
    category?: string,
    window: "week" | "month" = "week",
  ): Promise<{
    period: {
      current: { start: string; end: string };
      previous: { start: string; end: string };
    };
    risers: Array<{
      _id: string;
      name: string;
      category: string | null;
      logoUrl: string | null;
      s3Key: string | null;
      mentionsDelta?: number | null;
    }>;
    fallers: Array<{
      _id: string;
      name: string;
      category: string | null;
      logoUrl: string | null;
      s3Key: string | null;
    }>;
    newEntries: Array<{
      _id: string;
      name: string;
      category: string | null;
      logoUrl: string | null;
      s3Key: string | null;
    }>;
    leaving: Array<{
      _id: string;
      name: string;
      category: string | null;
      logoUrl: string | null;
      s3Key: string | null;
    }>;
  }> {
    const params: Record<string, string | number> = {};
    if (anchor) params.anchor = anchor;
    if (newEntriesLimit != null) params.limit = newEntriesLimit;
    if (category) params.category = category;
    if (window && window !== "week") params.window = window;
    const response = await api.get("/api/explorer/brands/weekly-movements", {
      params: Object.keys(params).length > 0 ? params : undefined,
    });
    return response.data;
  },

  async getExplorerProgramsMonthlyMetrics(
    startDate: string,
    endDate: string,
    taxonomy: PodcastTaxonomyFilters = {},
    signal?: AbortSignal,
  ): Promise<{
    rows: Array<{
      programId: string;
      month: string;
      views: number;
      videoCount: number;
      distinctPaidBrands: number;
    }>;
  }> {
    const params = new URLSearchParams();
    params.append("startDate", startDate);
    params.append("endDate", endDate);
    appendPodcastTaxonomyParams(params, taxonomy);
    const response = await api.get(
      `/api/explorer/programs/monthly-metrics?${params.toString()}`,
      { signal },
    );
    return response.data;
  },

  async getExplorerBrandsMonthlyMetrics(
    startDate: string,
    endDate: string,
    category?: string,
    signal?: AbortSignal,
  ): Promise<{
    rows: Array<{
      brandId: string;
      month: string;
      brandName: string;
      brandCategory: string;
      mentions: number;
      distinctPrograms: number;
      distinctPodcasts: number;
    }>;
  }> {
    const params = new URLSearchParams();
    params.append("startDate", startDate);
    params.append("endDate", endDate);
    if (category && category !== "all") params.append("category", category);
    const response = await api.get(
      `/api/explorer/brands/monthly-metrics?${params.toString()}`,
      { signal },
    );
    return response.data;
  },

  async getExplorerPodcasts(
    startDate?: string,
    endDate?: string,
    taxonomy: PodcastTaxonomyFilters = {},
    sortBy = "totalViews",
    sortOrder = "desc",
    search?: string,
    signal?: AbortSignal,
    page?: number,
    limit?: number,
    contentCategory?: string | string[],
    withBrands?: boolean,
    ids?: string[],
  ): Promise<{
    podcasts: Array<{
      _id: string;
      title: string;
      imageS3Key?: string;
      imageUrl?: string;
      programCount: number;
      contentCount: number;
      totalViews: number;
      viewsPerEp: number;
      totalInteractions?: number;
      engagementRate?: number;
      uniquePaidBrandsCount?: number;
      estimatedInvestment?: number;
      globalRank?: number | null;
    }>;
    pagination?: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    appendPodcastTaxonomyParams(params, taxonomy);
    if (sortBy) params.append("sortBy", sortBy);
    if (sortOrder) params.append("sortOrder", sortOrder);
    if (search) params.append("search", search);
    if (page !== undefined) params.append("page", page.toString());
    if (limit !== undefined) params.append("limit", limit.toString());
    if (withBrands) params.append("withBrands", "true");
    if (ids && ids.length > 0) {
      ids.forEach((id) => params.append("ids", id));
    }
    if (contentCategory) {
      const cats = Array.isArray(contentCategory)
        ? contentCategory
        : [contentCategory];
      cats.forEach((c) => params.append("contentCategory", c));
    }

    const response = await api.get(
      `/api/explorer/podcasts?${params.toString()}`,
      { signal },
    );
    return response.data;
  },

  async getExplorerPodcastById(podcastId: string) {
    const response = await api.get<Podcast>(
      `/api/explorer/podcasts/${podcastId}`,
    );
    return response.data;
  },

  async getExplorerSocialChannels(
    startDate: string,
    endDate: string,
    sortBy = "views",
    sortOrder: "asc" | "desc" = "desc",
    search?: string,
    signal?: AbortSignal,
    page?: number,
    limit?: number,
    platform?: string,
    channelType?: string | string[],
    all?: boolean,
    ids?: string[],
  ): Promise<{
    channels: ExplorerSocialRankingChannel[];
    pagination: ExplorerSocialPagination;
    totalViews?: number;
  }> {
    const params = new URLSearchParams();
    params.append("startDate", startDate);
    params.append("endDate", endDate);
    if (sortBy) params.append("sortBy", sortBy);
    if (sortOrder) params.append("sortOrder", sortOrder);
    if (search) params.append("search", search);
    if (page) params.append("page", String(page));
    if (limit) params.append("limit", String(limit));
    if (platform && platform !== "all") params.append("platform", platform);
    const channelTypeParam = taxonomyChannelTypeParam(channelType);
    if (channelTypeParam) params.append("channelType", channelTypeParam);
    if (all) params.append("all", "1");
    if (ids && ids.length > 0) {
      ids.forEach((id) => params.append("ids", id));
    }
    const response = await api.get(`/api/explorer/social/channels?${params.toString()}`, {
      signal,
    });
    return response.data;
  },

  async getExplorerSocialPostingHeatmap(
    startDate: string,
    endDate: string,
    signal?: AbortSignal,
    platform?: string,
    channelType?: string | string[],
  ): Promise<ExplorerSocialPostingHeatmap> {
    const params = new URLSearchParams();
    params.append("startDate", startDate);
    params.append("endDate", endDate);
    if (platform && platform !== "all") params.append("platform", platform);
    const channelTypeParam = taxonomyChannelTypeParam(channelType);
    if (channelTypeParam) params.append("channelType", channelTypeParam);
    const response = await api.get(`/api/explorer/social/posting-heatmap?${params.toString()}`, {
      signal,
    });
    return response.data;
  },

  async getExplorerSocialBrands(
    startDate: string,
    endDate: string,
    sortBy = "views",
    sortOrder: "asc" | "desc" = "desc",
    search?: string,
    signal?: AbortSignal,
    page?: number,
    limit?: number,
    platform?: string,
    channelType?: string | string[],
    ids?: string[],
    placementType?: string,
  ): Promise<{
    brands: ExplorerSocialBrand[];
    pagination: ExplorerSocialPagination;
  }> {
    const params = new URLSearchParams();
    params.append("startDate", startDate);
    params.append("endDate", endDate);
    if (sortBy) params.append("sortBy", sortBy);
    if (sortOrder) params.append("sortOrder", sortOrder);
    if (search) params.append("search", search);
    if (page) params.append("page", String(page));
    if (limit) params.append("limit", String(limit));
    if (platform && platform !== "all") params.append("platform", platform);
    const channelTypeParam = taxonomyChannelTypeParam(channelType);
    if (channelTypeParam) params.append("channelType", channelTypeParam);
    if (
      placementType === "tagged" ||
      placementType === "overlay" ||
      placementType === "placement"
    ) {
      params.append("placementType", placementType);
    }
    if (ids && ids.length > 0) {
      ids.forEach((id) => params.append("ids", id));
    }
    const response = await api.get(`/api/explorer/social/brands?${params.toString()}`, {
      signal,
    });
    return response.data;
  },

  async getExplorerSocialBrand(
    brandId: string,
    startDate: string,
    endDate: string,
    opts?: {
      platform?: string;
      channelType?: string | string[];
      placementType?: string;
      signal?: AbortSignal;
    },
  ): Promise<ExplorerSocialBrand> {
    const params = new URLSearchParams();
    params.append("startDate", startDate);
    params.append("endDate", endDate);
    if (opts?.platform && opts.platform !== "all") params.append("platform", opts.platform);
    const channelTypeParam = taxonomyChannelTypeParam(opts?.channelType);
    if (channelTypeParam) params.append("channelType", channelTypeParam);
    if (
      opts?.placementType === "tagged" ||
      opts?.placementType === "overlay" ||
      opts?.placementType === "placement"
    ) {
      params.append("placementType", opts.placementType);
    }
    const response = await api.get(`/api/explorer/social/brands/${brandId}?${params.toString()}`, {
      signal: opts?.signal,
    });
    return response.data;
  },

  async getExplorerSocialBrandClips(
    brandId: string,
    startDate: string,
    endDate: string,
    opts?: {
      platform?: string;
      channelType?: string | string[];
      placementType?: string;
      sortBy?: string;
      sortOrder?: "asc" | "desc";
      page?: number;
      limit?: number;
      signal?: AbortSignal;
    },
  ): Promise<{
    brand: ExplorerSocialBrand;
    clips: ExplorerSocialBrandClip[];
    pagination: ExplorerSocialPagination;
  }> {
    const params = new URLSearchParams();
    params.append("startDate", startDate);
    params.append("endDate", endDate);
    if (opts?.platform && opts.platform !== "all") params.append("platform", opts.platform);
    const channelTypeParam = taxonomyChannelTypeParam(opts?.channelType);
    if (channelTypeParam) params.append("channelType", channelTypeParam);
    if (
      opts?.placementType === "tagged" ||
      opts?.placementType === "overlay" ||
      opts?.placementType === "placement"
    ) {
      params.append("placementType", opts.placementType);
    }
    if (opts?.sortBy) params.append("sortBy", opts.sortBy);
    if (opts?.sortOrder) params.append("sortOrder", opts.sortOrder);
    if (opts?.page) params.append("page", String(opts.page));
    if (opts?.limit) params.append("limit", String(opts.limit));
    const response = await api.get(
      `/api/explorer/social/brands/${brandId}/clips?${params.toString()}`,
      { signal: opts?.signal },
    );
    return response.data;
  },

  async getExplorerSocialChannel(
    channelId: string,
    startDate: string,
    endDate: string,
    opts?: {
      ownership?: "owned" | "collab";
      platform?: string;
      kind?: "clip" | "post";
      hashtags?: string[];
      search?: string;
      signal?: AbortSignal;
    },
  ): Promise<ExplorerSocialChannel> {
    const params = new URLSearchParams();
    params.append("startDate", startDate);
    params.append("endDate", endDate);
    if (opts?.ownership) params.append("ownership", opts.ownership);
    if (opts?.platform) params.append("platform", opts.platform);
    if (opts?.kind) params.append("kind", opts.kind);
    if (opts?.hashtags?.length) params.append("hashtags", opts.hashtags.join(","));
    if (opts?.search) params.append("search", opts.search);
    const response = await api.get(
      `/api/explorer/social/channels/${channelId}?${params.toString()}`,
      { signal: opts?.signal },
    );
    return response.data;
  },

  async getExplorerSocialChannelPosts(
    channelId: string,
    startDate: string,
    endDate: string,
    opts?: {
      platform?: string;
      ownership?: "owned" | "collab";
      kind?: "clip" | "post";
      hashtags?: string[];
      search?: string;
      sortBy?: string;
      sortOrder?: "asc" | "desc";
      page?: number;
      limit?: number;
      signal?: AbortSignal;
    },
  ): Promise<{
    channel: ExplorerSocialChannel;
    posts: ExplorerSocialPost[];
    counts: { clips: number; staticPosts: number };
    pagination: ExplorerSocialPagination;
  }> {
    const params = new URLSearchParams();
    params.append("startDate", startDate);
    params.append("endDate", endDate);
    if (opts?.platform) params.append("platform", opts.platform);
    if (opts?.ownership) params.append("ownership", opts.ownership);
    if (opts?.kind) params.append("kind", opts.kind);
    if (opts?.hashtags?.length) params.append("hashtags", opts.hashtags.join(","));
    if (opts?.search) params.append("search", opts.search);
    if (opts?.sortBy) params.append("sortBy", opts.sortBy);
    if (opts?.sortOrder) params.append("sortOrder", opts.sortOrder);
    if (opts?.page) params.append("page", String(opts.page));
    if (opts?.limit) params.append("limit", String(opts.limit));
    const response = await api.get(
      `/api/explorer/social/channels/${channelId}/posts?${params.toString()}`,
      { signal: opts?.signal },
    );
    return response.data;
  },

  async getExplorerSocialChannelHashtags(
    channelId: string,
    startDate: string,
    endDate: string,
    opts?: {
      platform?: string;
      ownership?: "owned" | "collab";
      kind?: "clip" | "post";
      signal?: AbortSignal;
    },
  ): Promise<Array<{ tag: string; count: number }>> {
    const params = new URLSearchParams();
    params.append("startDate", startDate);
    params.append("endDate", endDate);
    if (opts?.platform) params.append("platform", opts.platform);
    if (opts?.ownership) params.append("ownership", opts.ownership);
    if (opts?.kind) params.append("kind", opts.kind);
    const response = await api.get(
      `/api/explorer/social/channels/${channelId}/hashtags?${params.toString()}`,
      { signal: opts?.signal },
    );
    return response.data?.hashtags ?? [];
  },

  async getExplorerPodcastsBrandCounts(
    startDate: string,
    endDate: string,
    podcastIds: string[],
    signal?: AbortSignal,
  ): Promise<{ brandCounts: Record<string, number> }> {
    const params = new URLSearchParams();
    params.append("startDate", startDate);
    params.append("endDate", endDate);
    podcastIds.forEach((id) => params.append("ids", id));

    const response = await api.get(
      `/api/explorer/podcasts/brand-counts?${params.toString()}`,
      { signal },
    );
    return response.data;
  },

  async getExplorerPodcastsInvestment(
    startDate: string,
    endDate: string,
    podcastIds: string[],
    signal?: AbortSignal,
  ): Promise<{ investments: Record<string, number> }> {
    const params = new URLSearchParams();
    params.append("startDate", startDate);
    params.append("endDate", endDate);
    podcastIds.forEach((id) => params.append("ids", id));

    const response = await api.get(
      `/api/explorer/podcasts/investment?${params.toString()}`,
      { signal },
    );
    return response.data;
  },

  async getExplorerBrands(
    startDate?: string,
    endDate?: string,
    category?: string,
    sortBy?: string,
    sortOrder?: "asc" | "desc",
    signal?: AbortSignal,
    page?: number,
    limit?: number,
    search?: string,
    ids?: string[],
    exportDetails?: boolean,
    placementOnly?: boolean,
    channelType?: string | string[],
  ): Promise<{
    brands: Array<{
      _id: string;
      name: string;
      category?: string;
      logoUrl?: string;
      s3Key?: string;
      totalPaidMentions: number;
      /** Distinct episodes with a content-bucket paid mention. */
      contentMentions?: number;
      /** Distinct episodes with a physical placement / banner mention. */
      physicalMentions?: number;
      /** Distinct episodes with an opening/closing credits mention. */
      creditsMentions?: number;
      uniqueProgramCount: number;
      uniqueChannelCount: number;
      lastMentionAt?: string;
      isNew?: boolean;
      totalViews?: number;
      estimatedInvestment?: number;
      positionDelta?: number | null;
      globalRank?: number | null;
      /** Set when exportDetails=true — programs with any paid mention in range (all placement types). */
      programNames?: string[];
      /** Podcast channel titles when exportDetails=true */
      channelNames?: string[];
    }>;
    categories: string[];
    pagination?: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (category) params.append("category", category);
    if (sortBy) params.append("sortBy", sortBy);
    if (sortOrder) params.append("sortOrder", sortOrder);
    if (page) params.append("page", page.toString());
    if (limit) params.append("limit", limit.toString());
    if (search) params.append("search", search);
    if (ids && ids.length > 0) {
      ids.forEach((id) => params.append("ids", id));
    }
    if (exportDetails) params.append("exportDetails", "true");
    if (placementOnly) params.append("placementOnly", "true");
    const channelTypeParam = taxonomyChannelTypeParam(channelType);
    if (channelTypeParam) params.append("channelType", channelTypeParam);

    const response = await api.get(
      `/api/explorer/brands?${params.toString()}`,
      { signal },
    );
    return response.data;
  },

  /** Category names for the compare filter. Does not rank brands. */
  async getExplorerBrandCategories(signal?: AbortSignal): Promise<string[]> {
    const response = await api.get<{ categories?: string[] }>(
      "/api/explorer/brands?categoriesOnly=true",
      { signal },
    );
    return response.data.categories ?? [];
  },

  async getExplorerBrandsMonthlySponsorExport(
    startDate?: string,
    endDate?: string,
    category?: string,
    signal?: AbortSignal,
  ): Promise<{
    rows: Array<{
      month: string;
      brandName: string;
      programs: string;
      channels: string;
      programCount: number;
      podcastCount: number;
      mentions: number;
      episodeCount: number;
      mentionsPerEpisode: number;
      totalViews: number;
      avgViewsPerEpisode: number;
    }>;
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (category) params.append("category", category);

    const response = await api.get(
      `/api/explorer/brands/monthly-sponsor-export?${params.toString()}`,
      { signal },
    );
    return response.data;
  },

  async getExplorerBrandPositionDeltas(
    startDate?: string,
    endDate?: string,
    category?: string,
    sortBy?: string,
    sortOrder?: "asc" | "desc",
  ): Promise<{
    deltas: Record<string, { positionDelta: number | null; isNew: boolean }>;
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (category) params.append("category", category);
    if (sortBy) params.append("sortBy", sortBy);
    if (sortOrder) params.append("sortOrder", sortOrder);

    const response = await api.get(
      `/api/explorer/brands/position-deltas?${params.toString()}`,
    );
    return response.data;
  },

  async getExplorerBrandTrends(
    startDate?: string,
    endDate?: string,
    granularity?: string,
    category?: string,
    signal?: AbortSignal,
    withMoverDetails = false,
    channelType?: string | string[],
  ): Promise<{
    trends: Array<{
      week: string;
      weekStart: string;
      activeBrands: number;
      activeBrandsPhysical?: number;
      activeBrandsCredits?: number;
      totalMentions: number;
      programsWithBrands: number;
      newBrands: number;
      leavingBrands: number;
    }>;
    periodTotals: {
      activeBrands: number;
      activeBrandsPhysical?: number;
      activeBrandsCredits?: number;
      totalMentions: number;
      programsWithBrands: number;
      newBrands: number;
      leavingBrands: number;
    };
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (granularity) params.append("granularity", granularity);
    if (category) params.append("category", category);
    if (withMoverDetails) params.append("withMoverDetails", "true");
    const channelTypeParam = taxonomyChannelTypeParam(channelType);
    if (channelTypeParam) params.append("channelType", channelTypeParam);

    const response = await api.get(
      `/api/explorer/brands/trends?${params.toString()}`,
      { signal },
    );
    return response.data;
  },

  async getExplorerBrandMentions(
    brandId: string,
    startDate?: string,
    endDate?: string,
  ): Promise<{
    mentions: Array<{
      _id: string;
      context: string;
      startTime: number;
      endTime: number;
      createdAt?: string;
      content: {
        _id: string;
        title: string;
        youtubeId?: string;
        publishedAt: string;
        analyzedAt?: string;
        thumbnail?: string;
        podcast?: {
          title: string;
        };
      } | null;
    }>;
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    const queryString = params.toString();
    const response = await api.get(
      `/api/explorer/brands/${brandId}/mentions${queryString ? `?${queryString}` : ""}`,
    );
    return response.data;
  },

  async getExplorerBrandDetail(
    brandId: string,
    startDate?: string,
    endDate?: string,
    placementType?: import("../types/placement").PlacementBucket,
  ): Promise<{
    brand: {
      _id: string;
      name: string;
      category?: string;
      s3Key?: string;
      logoUrl?: string;
    };
    programs: Array<{
      _id: string;
      name: string;
      imageS3Key?: string;
      podcast?: {
        imageS3Key?: string;
        imageUrl?: string;
      } | null;
      mentionCount: number;
      episodeCount: number;
      contentMentions: number;
      physicalMentions: number;
      creditsMentions: number;
      bannerMentions?: number;
    }>;
    channels?: Array<{
      _id: string;
      title: string;
      imageS3Key?: string;
      imageUrl?: string;
      mentionCount: number;
    }>;
    mentionsBreakdown: {
      content: number;
      physical: number;
      credits: number;
      banner?: number;
    };
    totalMentions: number;
    totalPrograms: number;
    uniqueChannelCount?: number;
    totalViews: number;
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (placementType) params.append("placementType", placementType);
    const queryString = params.toString();
    const response = await api.get(
      `/api/explorer/brands/${brandId}${queryString ? `?${queryString}` : ""}`,
    );
    return response.data;
  },

  async getExplorerBrandDetailTrends(
    brandId: string,
    startDate?: string,
    endDate?: string,
    granularity: "daily" | "weekly" = "weekly",
  ): Promise<{
    trends: Array<{
      week: string;
      weekStart: string;
      mentions: number;
      programs: number;
      views: number;
      mentionsContent: number;
      mentionsPhysical: number;
      mentionsCredits: number;
      mentionsDelta: number | null;
      programsDelta: number | null;
      viewsDelta: number | null;
    }>;
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (granularity) params.append("granularity", granularity);
    const queryString = params.toString();
    const response = await api.get(
      `/api/explorer/brands/${brandId}/trends${queryString ? `?${queryString}` : ""}`,
    );
    return response.data;
  },

  async getExplorerProgramDetail(
    programId: string,
    startDate?: string,
    endDate?: string,
    placementType?: import("../types/placement").PlacementBucket,
  ): Promise<{
    program: {
      _id: string;
      name: string;
      imageS3Key?: string;
      youtubePlaylistIds?: string[];
      podcast: {
        _id: string;
        title: string;
        imageS3Key?: string;
        imageUrl?: string;
        /** YouTube channel id (UC…) from podcast youtubeData / platforms.youtube */
        youtubeChannelId?: string;
      } | null;
    };
    brands: Array<{
      _id: string;
      name: string;
      s3Key?: string;
      category?: string;
      mentionCount: number;
      totalMentions: number;
      contentMentions: number;
      physicalMentions: number;
      bannerMentions?: number;
      creditsMentions: number;
    }>;
    mentionsBreakdown: {
      content: number;
      physical: number;
      credits: number;
      banner?: number;
    };
    topics: Array<{
      topicId: string;
      topicName: string;
      contentCount: number;
      totalDuration: number;
      avgPercentage: number;
      avgConfidence: number;
    }>;
    socialStats?: {
      totalViews: number;
      totalLikes: number;
      contentCount: number;
    };
    /** Long-form episodes in the period; the list comes from getExplorerProgramEpisodes. */
    episodeStats: { count: number; totalViews: number };
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (placementType) params.append("placementType", placementType);

    const response = await api.get(
      `/api/explorer/programs/${programId}?${params.toString()}`,
    );
    return response.data;
  },

  async getExplorerPodcastEpisodes(
    podcastId: string,
    startDate: string | undefined,
    endDate: string | undefined,
    page: number,
    pageSize: number,
    options?: { programId?: string; search?: string },
  ): Promise<{
    episodes: Array<{
      _id: string;
      title: string;
      titleHistory?: Array<{ title: string; replacedAt: string }>;
      url?: string;
      youtubeId?: string;
      publishedAt: string;
      duration?: number;
      views: number;
      likes?: number;
      comments?: number;
      programId?: string | null;
      programName?: string | null;
      livestream?: { channelId: string };
    }>;
    total: number;
    page: number;
    pageSize: number;
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    params.append("page", String(page));
    params.append("pageSize", String(pageSize));
    if (options?.programId) params.append("programId", options.programId);
    if (options?.search) params.append("search", options.search);
    const response = await api.get(
      `/api/explorer/podcasts/${podcastId}/episodes?${params.toString()}`,
    );
    return response.data;
  },

  async getExplorerProgramEpisodes(
    programId: string,
    startDate: string | undefined,
    endDate: string | undefined,
    page: number,
    pageSize: number,
  ): Promise<{
    episodes: Array<{
      _id: string;
      title: string;
      titleHistory?: Array<{ title: string; replacedAt: string }>;
      url?: string;
      youtubeId?: string;
      publishedAt: string;
      duration?: number;
      views: number;
      likes?: number;
      comments?: number;
      livestream?: { channelId: string };
    }>;
    total: number;
    page: number;
    pageSize: number;
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    params.append("page", String(page));
    params.append("pageSize", String(pageSize));
    const response = await api.get(
      `/api/explorer/programs/${programId}/episodes?${params.toString()}`,
    );
    return response.data;
  },

  async getExplorerProgramTrends(
    programId: string,
    startDate?: string,
    endDate?: string,
  ): Promise<{
    trends: Array<{
      week: string;
      weekStart: string;
      position: number | null;
      views: number;
      brands: number;
      socialViews: number;
      mentionsContent: number;
      mentionsPhysical: number;
      mentionsCredits: number;
      positionDelta: number | null;
      viewsDelta: number | null;
      brandsDelta: number | null;
      socialViewsDelta: number | null;
    }>;
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    const queryString = params.toString();
    const response = await api.get(
      `/api/explorer/programs/${programId}/trends${queryString ? `?${queryString}` : ""}`,
    );
    return response.data;
  },

  async getExplorerProgramPodscope(
    programId: string,
    startDate?: string,
    endDate?: string,
  ): Promise<{
    podscope: {
      streamCount: number;
      avgViewers: number;
      peakViewers: number;
    } | null;
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    const queryString = params.toString();
    const response = await api.get(
      `/api/explorer/programs/${programId}/podscope${queryString ? `?${queryString}` : ""}`,
    );
    return response.data;
  },

  async getExplorerPodcastsAutocomplete(
    q?: string,
    limit = 20,
    startDate?: string,
    endDate?: string,
    contentCategories?: string[],
    taxonomy: PodcastTaxonomyFilters = {},
    catalog = false,
  ): Promise<{
    podcasts: Array<{
      _id: string;
      title: string;
      imageS3Key?: string;
      imageUrl?: string;
    }>;
  }> {
    const params = new URLSearchParams();
    if (q) params.append("q", q);
    appendPodcastTaxonomyParams(params, taxonomy);
    params.append("limit", limit.toString());
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (contentCategories && contentCategories.length > 0) {
      contentCategories.forEach((c) => params.append("contentCategory", c));
    }
    if (catalog) params.append("catalog", "1");
    const response = await api.get(
      `/api/explorer/podcasts/autocomplete?${params.toString()}`,
    );
    return response.data;
  },

  async getExplorerFollowedPrograms(): Promise<{
    programs: ExplorerFollowedProgram[];
  }> {
    const response = await api.get("/api/explorer/followed-programs");
    return response.data;
  },

  /** Most-used hashtags on explorer content for the selected channels. */
  async getExplorerFollowedProgramHashtagSuggestions(
    podcastIds: string[],
  ): Promise<Array<{ tag: string; count: number }>> {
    const params = new URLSearchParams();
    if (podcastIds.length > 0) {
      params.append("podcastIds", podcastIds.join(","));
    }
    const query = params.toString();
    const response = await api.get(
      `/api/explorer/followed-programs/hashtag-suggestions${query ? `?${query}` : ""}`,
    );
    return response.data?.hashtags ?? [];
  },

  async previewExplorerFollowedProgram(
    body: ExplorerFollowedProgramInput,
  ): Promise<ExplorerFollowedProgramPreview> {
    const response = await api.post(
      "/api/explorer/followed-programs/preview",
      body,
    );
    return response.data;
  },

  async createExplorerFollowedProgram(
    body: ExplorerFollowedProgramInput & { name: string },
  ): Promise<{ program: ExplorerFollowedProgram }> {
    const response = await api.post("/api/explorer/followed-programs", body);
    return response.data;
  },

  async updateExplorerFollowedProgram(
    id: string,
    body: Partial<ExplorerFollowedProgramInput> & { name?: string },
  ): Promise<{ program: ExplorerFollowedProgram }> {
    const response = await api.patch(
      `/api/explorer/followed-programs/${encodeURIComponent(id)}`,
      body,
    );
    return response.data;
  },

  async deleteExplorerFollowedProgram(id: string): Promise<{ success: boolean }> {
    const response = await api.delete(
      `/api/explorer/followed-programs/${encodeURIComponent(id)}`,
    );
    return response.data;
  },

  async getExplorerFollowedProgramFeed(
    id: string,
    window: "7d" | "30d" = "7d",
  ): Promise<ExplorerFollowedProgramFeedResponse> {
    const params = new URLSearchParams();
    params.append("window", window);
    const response = await api.get(
      `/api/explorer/followed-programs/${encodeURIComponent(id)}/feed?${params.toString()}`,
    );
    return response.data;
  },

  async getExplorerFollowedProgramFeedPage(
    id: string,
    bucket: "ondemand" | "livestreams" | "clips",
    opts: { window?: "7d" | "30d"; skip?: number; limit?: number } = {},
  ): Promise<{
    items: ExplorerFollowedProgramFeedItem[];
    total: number;
    hasMore: boolean;
    window: { start: string; end: string; key: string };
  }> {
    const params = new URLSearchParams();
    params.append("window", opts.window ?? "7d");
    if (opts.skip != null) params.append("skip", String(opts.skip));
    if (opts.limit != null) params.append("limit", String(opts.limit));
    const response = await api.get(
      `/api/explorer/followed-programs/${encodeURIComponent(id)}/feed/${encodeURIComponent(bucket)}?${params.toString()}`,
    );
    return response.data;
  },

  async getExplorerPodcastStats(
    podcastId: string,
    startDate?: string,
    endDate?: string,
  ): Promise<{
    totalViews: number;
    contentCount: number;
    avgViews: number;
    brands: Array<{
      _id: string;
      name: string;
      s3Key?: string;
      mentionCount: number;
    }>;
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    const queryString = params.toString();
    const response = await api.get(
      `/api/explorer/podcasts/${podcastId}/stats${queryString ? `?${queryString}` : ""}`,
    );
    return response.data;
  },

  async getExplorerPodcastPodscope(
    podcastId: string,
    startDate?: string,
    endDate?: string,
  ): Promise<{
    podscope: {
      streamCount: number;
      avgViewers: number;
      peakViewers: number;
    } | null;
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    const queryString = params.toString();
    const response = await api.get(
      `/api/explorer/podcasts/${podcastId}/podscope${queryString ? `?${queryString}` : ""}`,
    );
    return response.data;
  },

  async getExplorerBrandPodscope(
    brandId: string,
    startDate?: string,
    endDate?: string,
  ): Promise<{
    podscope: {
      streamCount: number;
      avgViewers: number;
      peakViewers: number;
    } | null;
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    const queryString = params.toString();
    const response = await api.get(
      `/api/explorer/brands/${brandId}/podscope${queryString ? `?${queryString}` : ""}`,
    );
    return response.data;
  },

  async getExplorerPodcastTrends(
    podcastId: string,
    startDate?: string,
    endDate?: string,
    withBrandMetrics = false,
    signal?: AbortSignal,
    granularity: "daily" | "weekly" = "weekly",
  ): Promise<{
    trends: Array<{
      week: string;
      weekStart: string;
      episodes: number;
      views: number;
      brands: number;
      socialViews: number;
      episodesDelta: number | null;
      viewsDelta: number | null;
      brandsDelta: number | null;
      socialViewsDelta: number | null;
    }>;
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (withBrandMetrics) params.append("withBrandMetrics", "true");
    if (granularity) params.append("granularity", granularity);
    const queryString = params.toString();
    const response = await api.get(
      `/api/explorer/podcasts/${podcastId}/trends${queryString ? `?${queryString}` : ""}`,
      { signal },
    );
    return response.data;
  },

  async getExplorerPodcastTrendsBrandMetrics(
    podcastId: string,
    startDate?: string,
    endDate?: string,
    signal?: AbortSignal,
    granularity: "daily" | "weekly" = "weekly",
  ): Promise<{
    trends: Array<{
      week: string;
      weekStart: string;
      brands: number;
      brandsDelta: number | null;
    }>;
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (granularity) params.append("granularity", granularity);
    const queryString = params.toString();
    const response = await api.get(
      `/api/explorer/podcasts/${podcastId}/trends/brand-metrics${queryString ? `?${queryString}` : ""}`,
      { signal },
    );
    return response.data;
  },

  async getExplorerAggregateTrends(
    taxonomy: PodcastTaxonomyFilters = {},
    startDate?: string,
    endDate?: string,
    granularity?: string,
    search?: string,
    contentCategory?: string | string[],
    signal?: AbortSignal,
    withBrandMetrics = false,
  ): Promise<{
    trends: Array<{
      week: string;
      weekStart: string;
      totalViews: number;
      /** Sum of each episode's views at the 7-day mark after publication. Episodes under 7 days old contribute 0. */
      viewsAt7Days: number;
      /** Sum of likes+comments at ~day 7 per episode (same completeness gate as viewsAt7Days). */
      interactionsAt7Days: number;
      /** False when one or more episodes in the week have not yet reached their 7-day mark. */
      viewsComplete: boolean;
      /** Number of episodes excluded from viewsAt7Days because their 7-day mark has not yet passed. */
      incompleteViewsCount: number;
      totalDurationSeconds: number;
      episodeCount: number;
      brandCount: number;
      programCount: number;
      programsWithBrands: number;
      podcastCount: number;
      podcastsWithBrands: number;
      podcastBrandCount: number;
    }>;
    periodTotals: {
      totalViews: number;
      viewsAt7Days: number;
      interactionsAt7Days: number;
      viewsComplete: boolean;
      incompleteViewsCount: number;
      totalDurationSeconds: number;
      episodeCount: number;
      brandCount: number;
      programCount: number;
      programsWithBrands: number;
      podcastCount: number;
      podcastsWithBrands: number;
      podcastBrandCount: number;
    };
  }> {
    const params = new URLSearchParams();
    appendPodcastTaxonomyParams(params, taxonomy, {
      channelTypeQueryKey: "channelType",
    });
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (granularity) params.append("granularity", granularity);
    if (search) params.append("search", search);
    if (contentCategory) {
      const cats = Array.isArray(contentCategory)
        ? contentCategory
        : [contentCategory];
      cats.forEach((c) => params.append("contentCategory", c));
    }
    if (withBrandMetrics) params.append("withBrandMetrics", "true");

    const response = await api.get(
      `/api/explorer/aggregate-trends?${params.toString()}`,
      { signal },
    );
    return response.data;
  },

  async getExplorerAggregateTrendsBrandMetrics(
    taxonomy: PodcastTaxonomyFilters = {},
    startDate?: string,
    endDate?: string,
    granularity?: string,
    search?: string,
    contentCategory?: string | string[],
    signal?: AbortSignal,
    type?: "programs" | "podcasts" | "all",
  ): Promise<{
    trends: Array<{
      weekStartChilean: string;
      weekEndChilean: string;
      brandCount: number;
      programsWithBrands: number;
      podcastBrandCount: number;
      podcastsWithBrands: number;
    }>;
    periodTotals: {
      brandCount: number;
      programsWithBrands: number;
      podcastBrandCount: number;
      podcastsWithBrands: number;
    };
  }> {
    const params = new URLSearchParams();
    appendPodcastTaxonomyParams(params, taxonomy, {
      channelTypeQueryKey: "channelType",
    });
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (granularity) params.append("granularity", granularity);
    if (search) params.append("search", search);
    if (contentCategory) {
      const cats = Array.isArray(contentCategory)
        ? contentCategory
        : [contentCategory];
      cats.forEach((c) => params.append("contentCategory", c));
    }
    if (type && type !== "all") params.append("type", type);

    const response = await api.get(
      `/api/explorer/aggregate-trends/brand-metrics?${params.toString()}`,
      { signal },
    );
    return response.data;
  },

  async getExplorerProgramBrandMentions(
    programId: string,
    brandId: string,
    startDate?: string,
    endDate?: string,
    placementType?: import("../types/placement").PlacementBucket | "all",
  ): Promise<{
    mentions: Array<{
      _id: string;
      context: string;
      startTime: number;
      endTime: number;
      createdAt?: string;
      placementType: import("../types/placement").PlacementBucket;
      content: {
        _id: string;
        title: string;
        youtubeId?: string;
        publishedAt: string;
        analyzedAt?: string;
        duration?: number;
      } | null;
    }>;
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (placementType) params.append("placementType", placementType);
    const queryString = params.toString();
    const response = await api.get(
      `/api/explorer/programs/${programId}/brands/${brandId}/mentions${queryString ? `?${queryString}` : ""}`,
    );
    return response.data;
  },

  async getExplorerPodcastBrands(
    podcastId: string,
    startDate?: string,
    endDate?: string,
    signal?: AbortSignal,
  ): Promise<{
    brands: Array<{
      _id: string;
      name: string;
      s3Key?: string;
      category?: string;
      mentionCount: number;
      totalMentions: number;
      contentMentions: number;
      physicalMentions: number;
      bannerMentions?: number;
      creditsMentions: number;
    }>;
    mentionsBreakdown: {
      content: number;
      physical: number;
      credits: number;
      banner?: number;
    };
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    const queryString = params.toString();
    const response = await api.get(
      `/api/explorer/podcasts/${podcastId}/brands${queryString ? `?${queryString}` : ""}`,
      { signal },
    );
    return response.data;
  },

  async getExplorerPodcastBrandMentions(
    podcastId: string,
    brandId: string,
    startDate?: string,
    endDate?: string,
    placementType?: import("../types/placement").PlacementBucket | "all",
  ): Promise<{
    mentions: Array<{
      _id: string;
      context: string;
      startTime: number;
      endTime: number;
      createdAt?: string;
      placementType: import("../types/placement").PlacementBucket;
      content: {
        _id: string;
        title: string;
        youtubeId?: string;
        publishedAt: string;
        analyzedAt?: string;
        duration?: number;
        programId?: string;
        programName?: string | null;
      } | null;
    }>;
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (placementType) params.append("placementType", placementType);
    const queryString = params.toString();
    const response = await api.get(
      `/api/explorer/podcasts/${podcastId}/brands/${brandId}/mentions${queryString ? `?${queryString}` : ""}`,
    );
    return response.data;
  },

  async getExplorerContentMentions(contentId: string): Promise<{
    content: {
      _id: string;
      title: string;
      youtubeId?: string;
      publishedAt: string;
    };
    mentions: Array<{
      _id: string;
      brandId: string;
      brandName: string;
      brandS3Key?: string;
      context: string;
      startTime: number;
      endTime: number;
    }>;
  }> {
    const response = await api.get(
      `/api/explorer/content/${contentId}/mentions`,
    );
    return response.data;
  },

  async getExplorerLivestreams(
    startDate?: string,
    endDate?: string,
    country?: "cl" | "ar" | "all",
    category?: string | readonly string[],
    options?: {
      titleSearch?: string;
      signal?: AbortSignal;
      /** Server-side ranking table pagination + sort (Explorer livestreams tab). */
      ranking?: {
        page: number;
        pageSize: number;
        mode: "channels" | "programs";
        sortColumn: string;
        sortDirection: "asc" | "desc";
        channelSearch?: string;
        programSearch?: string;
        channelIds?: string[];
        programIds?: string[];
      };
      /** When false, ranking responses omit full channel stream lists (faster). Default true without ranking. */
      includeLivestreams?: boolean;
      /** Filter streams/episodes to FIFA World Cup keyword matches in title or description. */
      worldCupOnly?: boolean;
      /** Only `periodTotals` from the daily rollup (fast); `totalViews` is null — read it from a ranking response. */
      totalsOnly?: boolean;
    },
  ): Promise<{
    channels: Array<{
      _id: string;
      channelId: string;
      channelName: string;
      imageS3Key?: string;
      color?: string;
      category?: "tv" | "noticias" | "radio" | "digital";
      country?: "cl" | "ar";
      episodeCount: number;
      livestreams?: Array<{
        _id: string;
        title: string;
        videoId?: string;
        actualStartTime?: string;
        viewsAfter24h?: number;
        interactionsAfter24h?: number;
        avgViewers?: number;
        peakViewers?: number;
        chatMessages?: number;
        uniqueChatUsers?: number;
        /** Explorer `content` program when the YouTube video is linked */
        programId?: string | null;
        /** From `podcast_programs` when `programId` resolves */
        programName?: string | null;
      }>;
      metrics: {
        totalPeakViewers: number;
        avgPeakViewers: number;
        maxPeakViewers: number;
        minPeakViewers: number;
        avgViewers: number;
        totalLikes: number;
        avgLikes: number;
        totalDurationMinutes: number;
        avgChatMessages?: number;
        avgUniqueChatUsers?: number;
        /** Sum of latest YouTube VOD views from daily metrics per stream. */
        totalViews?: number;
        /** Sum of views from content.dailyMetrics (first available) per stream, matched by videoId. */
        viewsAfter24h?: number;
        /** Sum of likes from content.dailyMetrics (first available) per stream, matched by videoId. */
        interactionsAfter24h?: number;
      };
    }>;
    periodTotals: {
      totalChannels: number;
      totalStreams: number;
      totalPeakViewers: number;
      avgViewers: number;
      totalLikes: number;
      totalDurationMinutes: number;
      totalViews?: number | null;
    };
    ranking?: {
      total: number;
      page: number;
      pageSize: number;
      mode: "channels" | "programs";
      channelRows?: Array<{
        _id: string;
        channelId: string;
        channelName: string;
        globalRank?: number | null;
        imageS3Key?: string;
        category?: "tv" | "noticias" | "radio" | "digital";
        country?: "cl" | "ar";
        episodeCount: number;
        livestreams?: Array<{
          _id: string;
          title: string;
          videoId?: string;
          actualStartTime?: string;
          viewsAfter24h?: number;
          interactionsAfter24h?: number;
          avgViewers?: number;
          peakViewers?: number;
          chatMessages?: number;
          uniqueChatUsers?: number;
          programId?: string | null;
          programName?: string | null;
        }>;
        metrics: {
          totalPeakViewers: number;
          avgPeakViewers: number;
          maxPeakViewers: number;
          minPeakViewers: number;
          avgViewers: number;
          totalLikes: number;
          avgLikes: number;
          totalDurationMinutes: number;
          avgChatMessages?: number;
          avgUniqueChatUsers?: number;
          totalViews?: number;
          viewsAfter24h?: number;
          interactionsAfter24h?: number;
        };
      }>;
      programRows?: Array<{
        key: string;
        programName: string;
        programId: string | null;
        globalRank?: number | null;
        episodeCount: number;
        channelCount: number;
        sampleChannelId: string;
        sampleChannelName: string;
        sampleChannelImageS3Key?: string;
        imageS3Key?: string;
        imageUpdatedAt?: string;
        metrics: {
          avgViewers: number;
          avgPeakViewers: number;
          maxPeakViewers: number;
          avgChatMessages?: number;
          avgUniqueChatUsers?: number;
          totalViews?: number;
          totalDurationMinutes?: number;
          viewsUnavailableReason?: "shared_live" | "title_slices" | null;
        };
      }>;
    };
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (country && country !== "all") params.append("country", country);
    const categoryValue = (Array.isArray(category) ? [...category] : [category ?? ""])
      .map((item) => item.trim())
      .filter((item) => item && item !== "all")
      .join(",");
    if (categoryValue) params.append("category", categoryValue);
    const opts =
      options != null && typeof (options as any).addEventListener === "function"
        ? { signal: options as AbortSignal }
        : options;
    const titleSearch = opts?.titleSearch?.trim();
    if (titleSearch) params.append("titleSearch", titleSearch);
    if (opts?.worldCupOnly) params.append("worldCupOnly", "true");
    const rk = opts?.ranking;
    if (opts?.totalsOnly) {
      params.append("totalsOnly", "true");
      params.append("includeLivestreams", "false");
    } else if (rk) {
      params.append("rankingMode", rk.mode);
      params.append("rankingPage", String(rk.page));
      params.append("rankingPageSize", String(rk.pageSize));
      params.append("rankingSortColumn", rk.sortColumn);
      params.append("rankingSortDirection", rk.sortDirection);
      if (rk.channelSearch?.trim())
        params.append("channelSearch", rk.channelSearch.trim());
      if (rk.programSearch?.trim())
        params.append("programSearch", rk.programSearch.trim());
      rk.channelIds?.forEach((id) => params.append("channelIds", id));
      rk.programIds?.forEach((id) => params.append("programIds", id));
      params.append("includeLivestreams", "false");
    } else if (opts?.includeLivestreams !== false) {
      params.append("includeLivestreams", "true");
    } else {
      params.append("includeLivestreams", "false");
    }
    const response = await api.get(
      `/api/livestreams/grouped-by-channel?${params.toString()}`,
      {
        signal: opts?.signal,
      },
    );
    return response.data;
  },

  async getLiveStreams(
    country?: "cl" | "ar" | "all",
    category?: "tv" | "noticias" | "radio" | "digital" | "all",
  ): Promise<{
    count: number;
    streams: Array<{
      _id: string;
      videoId: string;
      channelId: string;
      channelName: string;
      title: string;
      actualStartTime: string;
      thumbnailUrl?: string;
      streamType?: "live" | "premiere";
      channelImageUrl?: string;
      channelImageS3Key?: string;
      currentViewers: number;
      peakViewers: number;
      lastUpdate?: string;
      chatUniqueUsers: number;
      chatMessageCount: number;
    }>;
  }> {
    const params = new URLSearchParams();
    if (country && country !== "all") params.append("country", country);
    if (category && category !== "all") params.append("category", category);
    const queryString = params.toString();
    const response = await api.get(
      `/api/livestreams/live${queryString ? `?${queryString}` : ""}`,
    );
    return response.data;
  },

  async getLivestreamViewersHeatmap(
    startDate?: string,
    endDate?: string,
    channel?: string,
    country?: "cl" | "ar",
    options?: { signal?: AbortSignal },
  ): Promise<{
    heatmap: Array<{
      dayIndex: number;
      dayName: string;
      hours: Array<{
        hour: number;
        totalViewers: number;
        channels: Array<{
          name: string;
          avgViewers: number;
          maxViewers: number;
        }>;
      }>;
    }>;
    maxViewers: number;
    dateRange: {
      start?: string;
      end?: string;
    };
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (channel) params.append("channel", channel);
    if (country) params.append("country", country); // Always pass country if provided
    const response = await api.get(
      `/api/livestreams/viewers-heatmap?${params.toString()}`,
      { signal: options?.signal },
    );
    return response.data;
  },

  async getLivestreamViewersHeatmapDetails(
    dayIndex: number,
    hour: number,
    startDate?: string,
    endDate?: string,
    channel?: string,
    country?: "cl" | "ar",
    /** When set (tv | radio | digital), slot details only include streams on channels in that category */
    category?: string,
    options?: {
      signal?: AbortSignal;
      /** Explorer-scoped stream ids (from grouped-by-channel); skips server scope resolve when ≤5000 */
      scopeIds?: string[];
    },
  ): Promise<{
    dayIndex: number;
    hour: number;
    totalViewers: number;
    channels: Array<{
      name: string;
      avgViewers: number;
      maxViewers: number;
      livestreams: Array<{
        id: string;
        title: string;
        url: string;
        avgViewers: number;
        maxViewers: number;
        programId?: string | null;
        programName?: string | null;
        programSlices?: Array<{
          programId?: string | null;
          programName?: string | null;
          minuteCount: number;
        }>;
        airDates?: string[];
        minuteCount?: number;
        broadcastAvgViewers?: number | null;
        broadcastPeakViewers?: number | null;
        broadcastStart?: string | null;
        broadcastEnd?: string | null;
      }>;
    }>;
  }> {
    const params = new URLSearchParams();
    params.append("dayIndex", dayIndex.toString());
    params.append("hour", hour.toString());
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (channel) params.append("channel", channel);
    if (country) params.append("country", country); // Always pass country if provided
    if (category && category !== "all") params.append("category", category);

    const scopeIds = options?.scopeIds;
    if (scopeIds?.length) {
      const response = await api.post(
        "/api/livestreams/viewers-heatmap/details",
        {
          dayIndex,
          hour,
          startDate,
          endDate,
          channel,
          country,
          category: category && category !== "all" ? category : undefined,
          scopeIds,
        },
        { signal: options?.signal },
      );
      return response.data;
    }

    const response = await api.get(
      `/api/livestreams/viewers-heatmap/details?${params.toString()}`,
      { signal: options?.signal },
    );
    return response.data;
  },

  /**
   * Bulk version of getLivestreamViewersHeatmapDetails: fetches the channel +
   * livestream breakdown for many (dayIndex, hour) slots in a single round-trip.
   * Used by the "Rating por franja" picker so a wide selection (e.g. all 7 days
   * × 24 hours) doesn't fan out into one HTTP request per cell.
   */
  async getLivestreamViewersHeatmapSlotsBreakdown(
    slots: Array<{ dayIndex: number; hour: number }>,
    startDate?: string,
    endDate?: string,
    country?: "cl" | "ar",
    category?: string,
    channel?: string,
    options?: { signal?: AbortSignal },
  ): Promise<{
    slots: Array<{
      dayIndex: number;
      hour: number;
      totalViewers: number;
      channels: Array<{
        name: string;
        avgViewers: number;
        maxViewers: number;
        livestreams: Array<{
          id: string;
          title: string;
          url: string;
          avgViewers: number;
          maxViewers: number;
          programId?: string | null;
          programName?: string | null;
          programSlices?: Array<{
            programId?: string | null;
            programName?: string | null;
            minuteCount: number;
          }>;
          minuteCount?: number;
          broadcastAvgViewers?: number | null;
          broadcastPeakViewers?: number | null;
          broadcastStart?: string | null;
          broadcastEnd?: string | null;
        }>;
      }>;
    }>;
  }> {
    const body: Record<string, unknown> = { slots };
    if (startDate) body["startDate"] = startDate;
    if (endDate) body["endDate"] = endDate;
    if (country) body["country"] = country;
    if (category && category !== "all") body["category"] = category;
    if (channel) body["channel"] = channel;
    const response = await api.post(
      `/api/livestreams/viewers-heatmap/slots-breakdown`,
      body,
      { signal: options?.signal },
    );
    return response.data;
  },

  async getLivestreamViewersHeatmapExport(
    startDate: string,
    endDate: string,
    country?: "cl" | "ar",
  ): Promise<{
    rows: Array<{
      dayIndex: number;
      dayName: string;
      hour: number;
      channelName: string;
      streamId: string;
      streamTitle: string;
      streamUrl: string;
      avgViewers: number;
      maxViewers: number;
    }>;
  }> {
    const params = new URLSearchParams();
    params.append("startDate", startDate);
    params.append("endDate", endDate);
    if (country) params.append("country", country);
    const response = await api.get(
      `/api/livestreams/viewers-heatmap/export?${params.toString()}`,
    );
    return response.data;
  },

  async getLivestreamTrends(
    startDate?: string,
    endDate?: string,
    granularity?: "daily" | "weekly" | "monthly",
  ): Promise<{
    trends: Array<{
      week: string;
      periodStart: string;
      periodEnd: string;
      activeChannels: number;
      totalStreams: number;
      totalPeakViewers: number;
      totalHours: number;
    }>;
    periodTotals: {
      totalChannels: number;
      totalStreams: number;
      totalPeakViewers: number;
      totalDurationMinutes: number;
    } | null;
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (granularity) params.append("granularity", granularity);
    const response = await api.get(
      `/api/livestreams/trends?${params.toString()}`,
    );
    return response.data;
  },

  async getExplorerLivestreamChannelTrends(
    channelId: string,
    startDate?: string,
    endDate?: string,
    programId?: string,
  ): Promise<{
    trends: Array<{
      week: string;
      weekStart: string;
      views: number;
      viewsPerStream: number;
      avgViewers: number;
      peakViewers: number;
      streamCount: number;
    }>;
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (programId) params.append("programId", programId);
    const response = await api.get(
      `/api/livestreams/channels/${channelId}/trends?${params.toString()}`,
    );
    return response.data;
  },

  async getExplorerLivestreamProgramPeriodMetrics(
    channelId: string,
    programId: string,
    startDate: string,
    endDate: string,
  ): Promise<ExplorerLivestreamPeriodMetrics> {
    const params = new URLSearchParams({ startDate, endDate });
    const response = await api.get(
      `/api/livestreams/channels/${channelId}/programs/${programId}/period-metrics?${params.toString()}`,
    );
    return response.data;
  },

  async getExplorerLivestreamChannelPeriodMetrics(
    channelId: string,
    startDate: string,
    endDate: string,
  ): Promise<ExplorerLivestreamPeriodMetrics> {
    const params = new URLSearchParams({ startDate, endDate });
    const response = await api.get(
      `/api/livestreams/channels/${channelId}/period-metrics?${params.toString()}`,
    );
    return response.data;
  },

  async getExplorerLivestreamChannelCompeting(
    channelId: string,
    startDate: string,
    endDate: string,
  ): Promise<ExplorerLivestreamCompetingWindows> {
    const params = new URLSearchParams({ startDate, endDate });
    const response = await api.get(
      `/api/livestreams/channels/${channelId}/competing?${params.toString()}`,
    );
    return response.data;
  },

  async getExplorerLivestreamCompetition(
    query: ExplorerLivestreamCompetitionQuery,
  ): Promise<ExplorerLivestreamCompetition> {
    const params = new URLSearchParams();
    if (query.start) params.set("start", query.start);
    if (query.end) params.set("end", query.end);
    if (query.id) params.set("id", query.id);
    if (query.category) params.set("category", query.category);
    const response = await api.get(
      `/api/livestreams/competition?${params.toString()}`,
    );
    return response.data;
  },

  async getExplorerLivestreamProgramTrends(
    programId: string,
    startDate?: string,
    endDate?: string,
    filters?: { country?: string; category?: string; channel?: string },
  ): Promise<{
    trends: Array<{
      week: string;
      weekStart: string;
      views: number;
      viewsPerStream: number;
      avgViewers: number;
      peakViewers: number;
      streamCount: number;
    }>;
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (filters?.country) params.append("country", filters.country);
    if (filters?.category) params.append("category", filters.category);
    if (filters?.channel) params.append("channel", filters.channel);
    const response = await api.get(
      `/api/livestreams/livestream-programs/${programId}/trends?${params.toString()}`,
    );
    return response.data;
  },

  async getExplorerLivestreamChannel(
    channelId: string,
    startDate?: string,
    endDate?: string,
  ): Promise<{
    channel: {
      _id: string;
      channelId: string;
      channelName: string;
      imageS3Key?: string;
      podcastId?: string | null;
      episodeCount: number;
      metrics: {
        totalPeakViewers: number;
        avgPeakViewers: number;
        avgViewers: number;
        maxPeakViewers: number;
        totalLikes: number;
        avgLikes: number;
        totalDurationMinutes: number;
        totalViews?: number;
      };
    };
    livestreams: Array<{
      _id: string;
      /** Parent livestream_streams id when this row is a title-program segment. */
      livestreamId?: string;
      videoId: string;
      url: string;
      title: string;
      status: string;
      actualStartTime?: string;
      actualEndTime?: string;
      releaseTime?: string;
      totalDuration?: number;
      /** YouTube Data API content length (seconds); does not replace totalDuration */
      youtubeDuration?: number;
      peakViewers?: number;
      averageViewers?: number;
      finalLikeCount?: number;
      postProcessingCompletedAt?: string;
      createdAt: string;
      updatedAt: string;
      /** Explorer livestream_programs assignment */
      livestreamProgramId?: string | null;
      programName?: string | null;
      /** Latest cumulative YouTube views from dailyMetrics (stream or linked content). */
      totalViews?: number;
      latestMetricsCollectedAt?: string;
    }>;
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    const response = await api.get(
      `/api/livestreams/channels/${channelId}?${params.toString()}`,
    );
    return response.data;
  },

  /** Explorer: livestream_programs row + streams (same shape as channel detail streams). */
  async getExplorerLivestreamProgram(
    programId: string,
    startDate?: string,
    endDate?: string,
    filters?: { country?: string; category?: string; channel?: string },
  ): Promise<{
    program: {
      _id: string;
      name: string;
      imageS3Key?: string;
      /** Fallback when the program has no image — from livestream_channels.imageS3Key. */
      channelImageS3Key?: string;
      /** Channel this page is showing, when the program airs on one channel. */
      channelName?: string;
      /** livestream_channels._id — programs for this catalog row belong to this channel only. */
      channelMongoId?: string;
      /** YouTube channel id (UC…) from livestream_channels.channelId */
      youtubeChannelId?: string;
      /** Stored channel URL from livestream_channels (fallback). */
      youtubeChannelUrl?: string;
      /** livestream_programs vs podcast_programs vs reserved "Sin programa" slug. */
      programKind?: "livestream_catalog" | "podcast_program" | "unassigned";
      episodeCount: number;
      metrics: {
        totalPeakViewers: number;
        avgPeakViewers: number;
        avgViewers: number;
        maxPeakViewers: number;
        totalLikes: number;
        avgLikes: number;
        totalDurationMinutes: number;
        totalViews?: number;
      };
    };
    livestreams: Array<{
      _id: string;
      livestreamId?: string;
      channelId?: string;
      channelName?: string;
      videoId: string;
      url: string;
      title: string;
      status: string;
      actualStartTime?: string;
      actualEndTime?: string;
      releaseTime?: string;
      totalDuration?: number;
      youtubeDuration?: number;
      peakViewers?: number;
      averageViewers?: number;
      finalLikeCount?: number;
      postProcessingCompletedAt?: string;
      createdAt: string;
      updatedAt: string;
      livestreamProgramId?: string | null;
      programName?: string | null;
      totalViews?: number;
      latestMetricsCollectedAt?: string;
    }>;
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (filters?.country) params.append("country", filters.country);
    if (filters?.category) params.append("category", filters.category);
    if (filters?.channel) params.append("channel", filters.channel);
    const response = await api.get(
      `/api/livestreams/livestream-programs/${encodeURIComponent(programId)}?${params.toString()}`,
    );
    return response.data;
  },

  async getExplorerLivestreamProgramViewerShape(
    programId: string,
    startDate: string,
    endDate: string,
    filters?: { country?: string; category?: string; channel?: string },
  ): Promise<ExplorerLivestreamProgramViewerShape> {
    const params = new URLSearchParams({ startDate, endDate });
    if (filters?.country) params.append("country", filters.country);
    if (filters?.category) params.append("category", filters.category);
    if (filters?.channel) params.append("channel", filters.channel);
    const response = await api.get(
      `/api/livestreams/livestream-programs/${encodeURIComponent(programId)}/viewer-shape?${params.toString()}`,
    );
    return response.data;
  },

  async updateLivestreamChannelCategory(
    channelId: string,
    category: "tv" | "noticias" | "radio" | "digital" | null,
  ): Promise<{
    message: string;
    channel: {
      _id: string;
      channelId: string;
      channelName: string;
      category?: "tv" | "noticias" | "radio" | "digital";
    };
  }> {
    const response = await api.patch(
      `/api/livestream-channels/${channelId}/category`,
      { category },
    );
    return response.data;
  },

  async updateLivestreamChannelCountry(
    channelId: string,
    country: "cl" | "ar",
  ): Promise<{
    message: string;
    channel: {
      _id: string;
      channelId: string;
      channelName: string;
      country?: "cl" | "ar";
    };
  }> {
    const response = await api.patch(
      `/api/livestream-channels/${channelId}/country`,
      { country },
    );
    return response.data;
  },

  async toggleProgramHidden(
    programId: string,
    hidden: boolean,
  ): Promise<{ success: boolean; hidden: boolean }> {
    const response = await api.patch(
      `/api/explorer/programs/${programId}/hidden`,
      { hidden },
    );
    return response.data;
  },

  // ============================================
  // Livestream Channels
  // ============================================

  async getLivestreamChannels(activeOnly = false): Promise<{
    channels: Array<{
      _id: string;
      channelId: string;
      channelName: string;
      channelUrl: string;
      podcastId?: string;
      category?: "tv" | "noticias" | "radio" | "digital";
      country?: "cl" | "ar";
      liveSchedules?: Array<{
        programName: string;
        dayOfWeek: number;
        dayName: string;
        hour: number;
        minute: number;
        timezone: string;
        isActive: boolean;
        lastTriggeredAt?: string;
      }>;
      isActive: boolean;
      lastCheckedAt?: string;
      lastLivestreamFoundAt?: string;
      lastLivestreamVideoId?: string;
      imageS3Key?: string;
      createdAt: string;
      updatedAt: string;
      analytics?: {
        episodeCount: number;
        avgPeakViewers: number;
        totalPeakViewers: number;
        maxPeakViewers: number;
        minPeakViewers: number;
      };
    }>;
    total: number;
  }> {
    const params = activeOnly ? "?active=true" : "";
    const response = await api.get(`/api/livestream-channels${params}`);
    return response.data;
  },

  async getLivestreamChannel(channelId: string): Promise<{
    _id: string;
    channelId: string;
    channelName: string;
    channelUrl: string;
    podcastId?: string;
    liveSchedules?: Array<{
      programName: string;
      dayOfWeek: number;
      dayName: string;
      hour: number;
      minute: number;
      timezone: string;
      isActive: boolean;
    }>;
    isActive: boolean;
    lastCheckedAt?: string;
    lastLivestreamFoundAt?: string;
    createdAt: string;
    updatedAt: string;
  }> {
    const response = await api.get(`/api/livestream-channels/${channelId}`);
    return response.data;
  },

  async createLivestreamChannel(data: {
    channelId: string;
    channelName: string;
    channelUrl?: string;
    podcastId?: string;
    liveSchedules?: Array<{
      programName: string;
      dayOfWeek: number;
      hour: number;
      minute: number;
      timezone?: string;
      isActive?: boolean;
    }>;
    isActive?: boolean;
  }): Promise<{
    message: string;
    channel: {
      _id: string;
      channelId: string;
      channelName: string;
      channelUrl: string;
    };
  }> {
    const response = await api.post("/api/livestream-channels", data);
    return response.data;
  },

  async updateLivestreamChannel(
    id: string,
    data: {
      channelName?: string;
      channelUrl?: string;
      podcastId?: string | null;
      liveSchedules?: Array<{
        programName: string;
        dayOfWeek: number;
        hour: number;
        minute: number;
        timezone?: string;
        isActive?: boolean;
      }>;
      isActive?: boolean;
    },
  ): Promise<{
    message: string;
    channel: any;
  }> {
    const response = await api.put(`/api/livestream-channels/${id}`, data);
    return response.data;
  },

  async addLivestreamChannelSchedule(
    channelId: string,
    schedule: {
      programName: string;
      dayOfWeek: number;
      hour: number;
      minute: number;
      timezone?: string;
      isActive?: boolean;
    },
  ): Promise<{
    message: string;
    schedule: any;
  }> {
    const response = await api.post(
      `/api/livestream-channels/${channelId}/schedules`,
      schedule,
    );
    return response.data;
  },

  async deleteLivestreamChannelSchedule(
    channelId: string,
    programName: string,
  ): Promise<{
    message: string;
  }> {
    const response = await api.delete(
      `/api/livestream-channels/${channelId}/schedules/${encodeURIComponent(programName)}`,
    );
    return response.data;
  },

  async deleteLivestreamChannel(id: string): Promise<{
    message: string;
  }> {
    const response = await api.delete(`/api/livestream-channels/${id}`);
    return response.data;
  },

  // ============================================
  // Livestream Programs
  // ============================================

  async getLivestreamPrograms(
    channelId?: string,
    activeOnly = false,
  ): Promise<{
    programs: Array<{
      _id: string;
      channelId: {
        _id: string;
        channelName: string;
        channelUrl: string;
        imageS3Key?: string;
      };
      name: string;
      description?: string;
      imageS3Key?: string;
      keywords?: string[];
      hashtags?: string[];
      socialAccounts?: string[];
      schedules: Array<{
        dayOfWeek: number;
        dayName: string;
        hour: number;
        minute: number;
        timezone: string;
        isActive: boolean;
      }>;
      isActive: boolean;
      hidden: boolean;
      createdAt: string;
      updatedAt: string;
    }>;
    total: number;
  }> {
    const params = new URLSearchParams();
    if (channelId) params.append("channelId", channelId);
    if (activeOnly) params.append("activeOnly", "true");
    const queryString = params.toString();
    const response = await api.get(
      `/api/livestream-programs${queryString ? `?${queryString}` : ""}`,
    );
    return response.data;
  },

  async getLivestreamProgram(id: string): Promise<{
    _id: string;
    channelId: {
      _id: string;
      channelName: string;
      channelUrl: string;
      imageS3Key?: string;
    };
    name: string;
    description?: string;
    imageS3Key?: string;
    keywords?: string[];
    hashtags?: string[];
    socialAccounts?: string[];
    schedules: Array<{
      dayOfWeek: number;
      dayName: string;
      hour: number;
      minute: number;
      timezone: string;
      isActive: boolean;
    }>;
    isActive: boolean;
    hidden: boolean;
    createdAt: string;
    updatedAt: string;
  }> {
    const response = await api.get(`/api/livestream-programs/${id}`);
    return response.data;
  },

  async createLivestreamProgram(data: {
    channelId: string;
    name: string;
    description?: string;
    schedules?: Array<{
      dayOfWeek: number;
      hour: number;
      minute: number;
      timezone?: string;
      isActive?: boolean;
    }>;
    keywords?: string[];
    hashtags?: string[];
    socialAccounts?: string[];
    isActive?: boolean;
  }): Promise<{
    message: string;
    program: any;
  }> {
    const response = await api.post("/api/livestream-programs", data);
    return response.data;
  },

  async updateLivestreamProgram(
    id: string,
    data: {
      name?: string;
      description?: string;
      schedules?: Array<{
        dayOfWeek: number;
        hour: number;
        minute: number;
        timezone?: string;
        isActive?: boolean;
      }>;
      keywords?: string[];
      hashtags?: string[];
      socialAccounts?: string[];
      isActive?: boolean;
      hidden?: boolean;
    },
  ): Promise<{
    message: string;
    program: any;
  }> {
    const response = await api.put(`/api/livestream-programs/${id}`, data);
    return response.data;
  },

  /** Admin: assign or clear livestream_streams.livestreamProgramId (same channel as stream). */
  async patchLivestreamStreamProgram(
    streamId: string,
    body: { livestreamProgramId: string | null },
  ): Promise<{
    message: string;
    streamId?: string;
    livestreamProgramId?: string | null;
  }> {
    const response = await api.patch(
      `/api/livestreams/streams/${encodeURIComponent(streamId)}/program`,
      body,
    );
    return response.data;
  },

  async deleteLivestreamProgram(id: string): Promise<{
    message: string;
  }> {
    const response = await api.delete(`/api/livestream-programs/${id}`);
    return response.data;
  },

  async addLivestreamProgramSchedule(
    programId: string,
    schedule: {
      dayOfWeek: number;
      hour: number;
      minute: number;
      timezone?: string;
      isActive?: boolean;
    },
  ): Promise<{
    message: string;
    program: any;
  }> {
    const response = await api.post(
      `/api/livestream-programs/${programId}/schedules`,
      schedule,
    );
    return response.data;
  },

  async deleteLivestreamProgramSchedule(
    programId: string,
    scheduleIndex: number,
  ): Promise<{
    message: string;
    program: any;
  }> {
    const response = await api.delete(
      `/api/livestream-programs/${programId}/schedules/${scheduleIndex}`,
    );
    return response.data;
  },

  // ============================================
  // Public Social Accounts API
  // ============================================

  async getPublicSocialAccounts(
    page = 1,
    limit = 20,
    filters?: {
      search?: string;
      platform?: "instagram" | "tiktok";
      accountType?: "podcast" | "program";
      isActive?: boolean;
    },
  ): Promise<{
    accounts: PublicSocialAccount[];
    pagination: { page: number; limit: number; total: number; pages: number };
  }> {
    const params = new URLSearchParams();
    params.append("page", String(page));
    params.append("limit", String(limit));
    if (filters?.search) params.append("search", filters.search);
    if (filters?.platform) params.append("platform", filters.platform);
    if (filters?.accountType) params.append("accountType", filters.accountType);
    if (filters?.isActive !== undefined)
      params.append("isActive", String(filters.isActive));
    const response = await api.get(
      `/api/public-social/accounts?${params.toString()}`,
    );
    return response.data;
  },

  async getPublicSocialAccount(id: string): Promise<PublicSocialAccount> {
    const response = await api.get(`/api/public-social/accounts/${id}`);
    return response.data;
  },

  async createPublicSocialAccount(data: {
    platform: "instagram" | "tiktok";
    username: string;
    displayName?: string;
    accountType?: "podcast" | "program";
    podcastId?: string;
    programId?: string;
    isActive?: boolean;
    notes?: string;
  }): Promise<PublicSocialAccount> {
    const response = await api.post("/api/public-social/accounts", data);
    return response.data;
  },

  async updatePublicSocialAccount(
    id: string,
    data: {
      displayName?: string;
      accountType?: "podcast" | "program";
      podcastId?: string | null;
      programId?: string | null;
      isActive?: boolean;
      notes?: string;
    },
  ): Promise<PublicSocialAccount> {
    const response = await api.put(`/api/public-social/accounts/${id}`, data);
    return response.data;
  },

  async deletePublicSocialAccount(id: string): Promise<{ message: string }> {
    const response = await api.delete(`/api/public-social/accounts/${id}`);
    return response.data;
  },

  async searchPodcastsForSocial(
    search?: string,
    limit = 20,
  ): Promise<{
    podcasts: Array<{
      _id: string;
      title: string;
      author?: string;
      imageUrl?: string;
      imageS3Key?: string;
      slug?: string;
      socialAccounts?: { instagram?: string; tiktok?: string };
    }>;
  }> {
    const params = new URLSearchParams();
    if (search) params.append("search", search);
    params.append("limit", String(limit));
    const response = await api.get(
      `/api/public-social/podcasts-search?${params.toString()}`,
    );
    return response.data;
  },

  async searchProgramsForSocial(
    search?: string,
    podcastId?: string,
    limit = 20,
  ): Promise<{
    programs: Array<{
      _id: string;
      name: string;
      podcastId?: string;
      imageS3Key?: string;
      socialAccounts?: { instagram?: string; tiktok?: string };
      podcast?: { title: string; imageUrl?: string; imageS3Key?: string };
    }>;
  }> {
    const params = new URLSearchParams();
    if (search) params.append("search", search);
    if (podcastId) params.append("podcastId", podcastId);
    params.append("limit", String(limit));
    const response = await api.get(
      `/api/public-social/programs-search?${params.toString()}`,
    );
    return response.data;
  },

  async getPublicSocialAccountContent(
    accountId: string,
    page = 1,
    limit = 20,
  ): Promise<{
    content: PublicSocialContent[];
    pagination: { page: number; limit: number; total: number; pages: number };
  }> {
    const response = await api.get(
      `/api/public-social/accounts/${accountId}/content?page=${page}&limit=${limit}`,
    );
    return response.data;
  },

  async getPublicSocialStats(): Promise<{
    accounts: {
      total: number;
      active: number;
      byPlatform: { instagram: number; tiktok: number };
      byType: { podcast: number; program: number };
    };
    content: { total: number };
  }> {
    const response = await api.get("/api/public-social/stats");
    return response.data;
  },

  async getPodcastsWithSocial(
    page = 1,
    limit = 20,
    options?: {
      search?: string;
      sortBy?: "title" | "views";
      sortOrder?: "asc" | "desc";
      dateFrom?: string;
      dateTo?: string;
    },
  ): Promise<{
    podcasts: Array<{
      _id: string;
      title: string;
      author?: string;
      imageUrl?: string;
      imageS3Key?: string;
      slug?: string;
      category?: "politics" | "other";
      type?: "tv" | "noticias" | "radio" | "digital";
      socialAccounts?: { instagram?: string; tiktok?: string };
      programs: Array<{
        _id: string;
        name: string;
        podcastId: string;
        imageS3Key?: string;
        socialAccounts?: { instagram?: string; tiktok?: string };
        assignedSocialAccounts: PublicSocialAccount[];
        hosts?: Array<{
          _id: string;
          name: string;
          imageS3Key?: string;
          linkedSocialAccounts: PublicSocialAccount[];
        }>;
        stats?: { views: number; likes: number; followers: number };
      }>;
      assignedSocialAccounts: PublicSocialAccount[];
      hosts?: Array<{
        _id: string;
        name: string;
        imageS3Key?: string;
        linkedSocialAccounts: PublicSocialAccount[];
      }>;
      hasLivestreamChannel?: boolean;
      stats?: {
        views: number;
        likes: number;
        followers: number;
        contentCount?: number;
      };
    }>;
    pagination: { page: number; limit: number; total: number; pages: number };
  }> {
    const params = new URLSearchParams();
    params.append("page", String(page));
    params.append("limit", String(limit));
    if (options?.search) params.append("search", options.search);
    if (options?.sortBy) params.append("sortBy", options.sortBy);
    if (options?.sortOrder) params.append("sortOrder", options.sortOrder);
    if (options?.dateFrom) params.append("dateFrom", options.dateFrom);
    if (options?.dateTo) params.append("dateTo", options.dateTo);
    const response = await api.get(
      `/api/public-social/podcasts-with-social?${params.toString()}`,
    );
    return response.data;
  },

  async mergePrograms(
    sourceId: string,
    targetId: string,
  ): Promise<{
    success: boolean;
    message: string;
    contentUpdated: number;
    socialAccountsUpdated: number;
  }> {
    const response = await api.post("/api/public-social/merge-programs", {
      sourceId,
      targetId,
    });
    return response.data;
  },

  async deletePodcast(podcastId: string): Promise<{
    success: boolean;
    deleted: {
      podcast: number;
      programs: number;
      content: number;
      socialAccounts: number;
      s3Files: number;
      s3FilesFailed: number;
    };
  }> {
    const response = await api.delete(
      `/api/public-social/podcasts/${podcastId}`,
    );
    return response.data;
  },

  // Host management
  async getHosts(options: { podcastId?: string; programId?: string }): Promise<{
    hosts: Array<{
      _id: string;
      name: string;
      imageS3Key?: string;
      linkedSocialAccounts: PublicSocialAccount[];
    }>;
  }> {
    const params = new URLSearchParams();
    if (options.podcastId) params.append("podcastId", options.podcastId);
    if (options.programId) params.append("programId", options.programId);
    const response = await api.get(
      `/api/public-social/hosts?${params.toString()}`,
    );
    return response.data;
  },

  async getAllHosts(
    page = 1,
    limit = 50,
    options?: {
      search?: string;
      filterBySocial?: "with" | "without";
      sortBy?: "name" | "podcast" | "program";
    },
  ): Promise<{
    hosts: Array<{
      _id: string;
      name: string;
      imageS3Key?: string;
      linkedSocialAccounts: PublicSocialAccount[];
      assignedPodcasts: Array<{
        _id: string;
        title: string;
        imageS3Key?: string;
        imageUrl?: string;
      }>;
      assignedPrograms: Array<{
        _id: string;
        name: string;
        imageS3Key?: string;
        podcastTitle: string;
      }>;
      hasSocialAccounts: boolean;
      contentCount: number;
      totalViews: number;
      totalLikes: number;
    }>;
    pagination: { page: number; limit: number; total: number; pages: number };
  }> {
    const params = new URLSearchParams();
    params.append("page", String(page));
    params.append("limit", String(limit));
    if (options?.search) params.append("search", options.search);
    if (options?.filterBySocial)
      params.append("filterBySocial", options.filterBySocial);
    if (options?.sortBy) params.append("sortBy", options.sortBy);
    const response = await api.get(
      `/api/public-social/hosts/all?${params.toString()}`,
    );
    return response.data;
  },

  async searchGuests(
    search: string,
    limit = 20,
  ): Promise<{
    guests: Array<{
      _id: string;
      name: string;
      normalizedName: string;
      imageS3Key?: string;
      socialAccounts?: Array<{ platform: string; handle: string }>;
      publicSocialAccountIds?: string[];
    }>;
  }> {
    const params = new URLSearchParams();
    if (search) params.append("search", search);
    params.append("limit", String(limit));
    const response = await api.get(
      `/api/public-social/search-guests?${params.toString()}`,
    );
    return response.data;
  },

  async createGuest(data: {
    name: string;
    imageS3Key?: string;
    socialAccounts?: Array<{ platform: string; handle: string }>;
  }): Promise<{
    _id: string;
    name: string;
    normalizedName: string;
  }> {
    const response = await api.post("/api/public-social/guests", data);
    return response.data;
  },

  async updateGuest(
    guestId: string,
    data: { name: string },
  ): Promise<{ success: boolean; name: string }> {
    const response = await api.put(
      `/api/public-social/guests/${guestId}`,
      data,
    );
    return response.data;
  },

  async deleteGuest(guestId: string): Promise<{
    success: boolean;
    deleted: { name: string; linkedSocialAccounts: number };
  }> {
    const response = await api.delete(`/api/public-social/guests/${guestId}`);
    return response.data;
  },

  async mergeGuests(
    sourceGuestId: string,
    targetGuestId: string,
  ): Promise<{
    success: boolean;
    merged: {
      sourceName: string;
      targetName: string;
      socialAccountsMerged: number;
      podcastsMerged: number;
      programsMerged: number;
    };
  }> {
    const response = await api.post("/api/public-social/guests/merge", {
      sourceGuestId,
      targetGuestId,
    });
    return response.data;
  },

  async getHostAssignmentOptions(): Promise<{
    podcasts: Array<{ _id: string; title: string }>;
    programs: Array<{
      _id: string;
      name: string;
      podcastId: string;
      podcastTitle: string;
    }>;
  }> {
    const response = await api.get(
      "/api/public-social/host-assignment-options",
    );
    return response.data;
  },

  async assignHostToPodcast(
    guestId: string,
    podcastId: string,
    role = "host",
  ): Promise<{ success: boolean }> {
    const response = await api.post("/api/public-social/hosts/assign-podcast", {
      guestId,
      podcastId,
      role,
    });
    return response.data;
  },

  async assignHostToProgram(
    guestId: string,
    programId: string,
    role = "host",
  ): Promise<{ success: boolean }> {
    const response = await api.post("/api/public-social/hosts/assign-program", {
      guestId,
      programId,
      role,
    });
    return response.data;
  },

  async removeHostFromPodcast(
    guestId: string,
    podcastId: string,
  ): Promise<{ success: boolean }> {
    const response = await api.post("/api/public-social/hosts/remove-podcast", {
      guestId,
      podcastId,
    });
    return response.data;
  },

  async removeHostFromProgram(
    guestId: string,
    programId: string,
  ): Promise<{ success: boolean }> {
    const response = await api.post("/api/public-social/hosts/remove-program", {
      guestId,
      programId,
    });
    return response.data;
  },

  async linkSocialAccountToHost(
    guestId: string,
    socialAccountId: string,
  ): Promise<{ success: boolean }> {
    const response = await api.post(
      "/api/public-social/hosts/link-social-account",
      { guestId, socialAccountId },
    );
    return response.data;
  },

  async unlinkSocialAccountFromHost(
    guestId: string,
    socialAccountId: string,
  ): Promise<{ success: boolean }> {
    const response = await api.post(
      "/api/public-social/hosts/unlink-social-account",
      { guestId, socialAccountId },
    );
    return response.data;
  },

  // ─── Favorites ────────────────────────────────────────────────────────────

  async getFavorites(): Promise<{
    favorites: UserFavorite[];
  }> {
    const response = await api.get("/api/favorites");
    return response.data;
  },

  async addFavorite(
    entityType: FavoriteEntityType,
    entityId: string,
  ): Promise<{
    favorite: UserFavorite;
  }> {
    const response = await api.post("/api/favorites", { entityType, entityId });
    return response.data;
  },

  async removeFavorite(
    entityType: FavoriteEntityType,
    entityId: string,
  ): Promise<{ success: boolean }> {
    const response = await api.delete(
      `/api/favorites/${entityType}/${entityId}`,
    );
    return response.data;
  },

  // ── Pipeline orchestrator ──────────────────────────────────────────────────

  async getPipelineSchedules(): Promise<PipelineSchedule[]> {
    const response = await api.get("/api/admin/pipeline/schedules");
    return response.data;
  },

  async createPipelineSchedule(payload: {
    kind: PipelineJobKind;
    cronExpr: string;
    timezone?: string;
    enabled?: boolean;
    scope?: { mode: string; ids?: string[] };
    pipelineOptions?: Record<string, unknown>;
  }): Promise<PipelineSchedule> {
    const response = await api.post("/api/admin/pipeline/schedules", payload);
    return response.data;
  },

  async togglePipelineSchedule(
    id: string,
    enabled: boolean,
  ): Promise<PipelineSchedule> {
    const response = await api.patch(
      `/api/admin/pipeline/schedules/${id}/enable`,
      { enabled },
    );
    return response.data;
  },

  async updatePipelineSchedule(
    id: string,
    payload: { cronExpr?: string; timezone?: string; enabled?: boolean },
  ): Promise<PipelineSchedule> {
    const response = await api.patch(
      `/api/admin/pipeline/schedules/${id}`,
      payload,
    );
    return response.data;
  },

  async deletePipelineSchedule(id: string): Promise<void> {
    await api.delete(`/api/admin/pipeline/schedules/${id}`);
  },

  async getPipelineRuns(params?: {
    status?: string;
    kind?: string;
    page?: number;
    limit?: number;
  }): Promise<{
    runs: PipelineRun[];
    pagination: { page: number; limit: number; total: number; pages: number };
  }> {
    const response = await api.get("/api/admin/pipeline/runs", { params });
    return response.data;
  },

  async triggerPipelineRun(payload: {
    kind: PipelineJobKind;
    tenantType?: "podcast" | "company" | "global";
    tenantId?: string;
    pipelineOptions?: Record<string, unknown>;
  }): Promise<{ runId: string; status: string }> {
    const response = await api.post("/api/admin/pipeline/runs", payload);
    return response.data;
  },

  async sendWeeklyIgEmail(payload: {
    to: string;
    week?: string;
  }): Promise<{ ok: boolean; to: string; week?: string; message: string }> {
    // Capture + Resend can take several minutes (Puppeteer).
    const response = await api.post("/api/admin/pipeline/weekly-ig-email", payload, {
      timeout: 600000,
    });
    return response.data;
  },

  async getPipelineDigestPreview(date?: string): Promise<PipelineDigestPreview> {
    const response = await api.get("/api/admin/pipeline/digest-preview", {
      params: date ? { date } : undefined,
    });
    return response.data;
  },

  // ── Company content plans ──────────────────────────────────────────────────
  // Pure relationship config (company → podcasts → programs). Shared by the
  // `company:mentions:plans` and `company:short:plans` CLIs. Execution lives
  // in those CLIs, not the UI.

  async getCompanyContentPlans(): Promise<CompanyContentPlan[]> {
    const response = await api.get("/api/admin/pipeline/content-plans");
    return response.data;
  },

  async createCompanyContentPlan(
    payload: CompanyContentPlanInput,
  ): Promise<CompanyContentPlan> {
    const response = await api.post(
      "/api/admin/pipeline/content-plans",
      payload,
    );
    return response.data;
  },

  async updateCompanyContentPlan(
    id: string,
    payload: Partial<CompanyContentPlanInput>,
  ): Promise<CompanyContentPlan> {
    const response = await api.patch(
      `/api/admin/pipeline/content-plans/${id}`,
      payload,
    );
    return response.data;
  },

  async deleteCompanyContentPlan(id: string): Promise<void> {
    await api.delete(`/api/admin/pipeline/content-plans/${id}`);
  },

  async getCompanyContentPlan(
    companyId: string,
  ): Promise<CompanyContentPlan | null> {
    const response = await api.get(
      `/api/admin/pipeline/companies/${companyId}/content-plan`,
    );
    return response.data;
  },

  async upsertCompanyContentPlan(
    companyId: string,
    payload: Omit<CompanyContentPlanInput, "companyId">,
  ): Promise<CompanyContentPlan> {
    const response = await api.put(
      `/api/admin/pipeline/companies/${companyId}/content-plan`,
      payload,
    );
    return response.data;
  },

  async getCompaniesLite(): Promise<CompanyLite[]> {
    const response = await api.get("/api/admin/pipeline/companies-lite");
    return response.data;
  },

  async getCompanyPodcastsWithPrograms(
    companyId: string,
    opts?: { includeAssignable?: boolean },
  ): Promise<PodcastWithPrograms[]> {
    const params = opts?.includeAssignable ? "?includeAssignable=true" : "";
    const response = await api.get(
      `/api/admin/pipeline/companies/${companyId}/podcasts-with-programs${params}`,
    );
    return response.data;
  },

  async getPodcastPresentBrands(
    podcastId: string,
    opts?: { programIds?: string[]; days?: number },
  ): Promise<
    Array<{
      _id: string;
      name: string;
      mentionCount: number;
      presenceCount: number;
    }>
  > {
    const params = new URLSearchParams();
    if (opts?.programIds && opts.programIds.length > 0) {
      params.set("programIds", opts.programIds.join(","));
    }
    if (opts?.days != null) {
      params.set("days", String(opts.days));
    }
    const q = params.toString();
    const response = await api.get(
      `/api/admin/pipeline/podcasts/${podcastId}/present-brands${q ? `?${q}` : ""}`,
    );
    return response.data.brands;
  },

  async listGoLinks(): Promise<{ links: GoLink[] }> {
    const response = await api.get("/api/go-links");
    return response.data;
  },

  async createGoLink(payload: {
    url: string;
    platform: string;
    description?: string;
    campaignId?: string;
  }): Promise<GoLink> {
    const response = await api.post("/api/go-links", payload);
    return response.data;
  },

  async updateGoLink(
    slug: string,
    payload: {
      description?: string | null;
      campaignId?: string | null;
    },
  ): Promise<GoLink> {
    const response = await api.patch(
      `/api/go-links/${encodeURIComponent(slug)}`,
      payload,
    );
    return response.data;
  },

  async deleteGoLink(slug: string): Promise<{ success: boolean }> {
    const response = await api.delete(
      `/api/go-links/${encodeURIComponent(slug)}`,
    );
    return response.data;
  },

  async getGoLinkStats(
    startDate: string,
    endDate: string,
  ): Promise<GoLinkStatsResponse> {
    const response = await api.get("/api/go-links/stats", {
      params: { startDate, endDate },
    });
    return response.data;
  },

  async listCampaigns(): Promise<{ campaigns: Campaign[] }> {
    const response = await api.get("/api/campaigns");
    return response.data;
  },

  async getCampaign(campaignId: string): Promise<Campaign> {
    const response = await api.get(
      `/api/campaigns/${encodeURIComponent(campaignId)}`,
    );
    return response.data;
  },

  async createCampaign(payload: CampaignInput): Promise<Campaign> {
    const response = await api.post("/api/campaigns", payload);
    return response.data;
  },

  async updateCampaign(
    campaignId: string,
    payload: Partial<CampaignInput>,
  ): Promise<Campaign> {
    const response = await api.patch(
      `/api/campaigns/${encodeURIComponent(campaignId)}`,
      payload,
    );
    return response.data;
  },

  async deleteCampaign(campaignId: string): Promise<{ success: boolean }> {
    const response = await api.delete(
      `/api/campaigns/${encodeURIComponent(campaignId)}`,
    );
    return response.data;
  },

  async getCampaignStats(
    campaignId: string,
    startDate: string,
    endDate: string,
  ): Promise<CampaignStatsResponse> {
    const response = await api.get(
      `/api/campaigns/${encodeURIComponent(campaignId)}/stats`,
      { params: { startDate, endDate } },
    );
    return response.data;
  },

  async getCampaignOverview(
    campaignId: string,
    startDate: string,
    endDate: string,
  ): Promise<CampaignOverviewResponse> {
    const response = await api.get(
      `/api/campaigns/${encodeURIComponent(campaignId)}/overview`,
      { params: { startDate, endDate } },
    );
    return response.data;
  },

  async getCampaignMiContenidoStats(
    campaignId: string,
    startDate: string,
    endDate: string,
    opts?: { contentBrandFilter?: string },
  ) {
    const params = new URLSearchParams({ startDate, endDate });
    if (opts?.contentBrandFilter && opts.contentBrandFilter !== "all") {
      params.set("contentBrandFilter", opts.contentBrandFilter);
    }
    const response = await api.get(
      `/api/campaigns/${encodeURIComponent(campaignId)}/mi-contenido-stats?${params.toString()}`,
    );
    return response.data;
  },

  async exportCampaignMiContenidoMetrics(
    campaignId: string,
    startDate: string,
    endDate: string,
    _opts?: { campaignTitle: string },
  ): Promise<void> {
    const params = new URLSearchParams({ startDate, endDate });
    const response = await api.get<Blob>(
      `/api/campaigns/${encodeURIComponent(campaignId)}/mi-contenido-metrics-pdf?${params.toString()}`,
      { responseType: "blob" },
    );
    downloadMiContenidoMetricsPdfBlob(
      response.data,
      response.headers["content-disposition"],
    );
  },

  async getCampaignCompare(
    campaignId: string,
    startDate: string,
    endDate: string,
    dimension: CampaignCompareDimension = "program",
  ): Promise<CampaignCompareResponse> {
    const response = await api.get(
      `/api/campaigns/${encodeURIComponent(campaignId)}/compare`,
      { params: { startDate, endDate, dimension } },
    );
    return response.data;
  },

  async getCampaignContentDailyMetrics(
    campaignId: string,
    contentId: string,
    startDate: string,
    endDate: string,
  ): Promise<{
    _id: string;
    title?: string;
    publishedAt?: string;
    dailyMetrics: Array<Record<string, unknown>>;
  }> {
    const response = await api.get(
      `/api/campaigns/${encodeURIComponent(campaignId)}/content/${encodeURIComponent(contentId)}/daily-metrics`,
      { params: { startDate, endDate } },
    );
    return response.data;
  },

  async getCampaignTrends(
    campaignId: string,
    startDate: string,
    endDate: string,
    granularity: "day" | "week" | "month" = "day",
    mode: "published_at" | "views_gained" = "published_at",
  ): Promise<CampaignTrendsResponse> {
    const response = await api.get(
      `/api/campaigns/${encodeURIComponent(campaignId)}/trends`,
      { params: { startDate, endDate, granularity, mode } },
    );
    return response.data;
  },

  async getCampaignAudience(
    campaignId: string,
    startDate: string,
    endDate: string,
  ): Promise<CampaignAudienceResponse> {
    const response = await api.get(
      `/api/campaigns/${encodeURIComponent(campaignId)}/audience`,
      { params: { startDate, endDate } },
    );
    return response.data;
  },

  async getCampaignRetentionSummary(
    campaignId: string,
    startDate: string,
    endDate: string,
  ): Promise<CampaignRetentionSummaryResponse> {
    const response = await api.get(
      `/api/campaigns/${encodeURIComponent(campaignId)}/retention-summary`,
      { params: { startDate, endDate } },
    );
    return response.data;
  },

  async listCampaignGoLinks(campaignId: string): Promise<{ links: GoLink[] }> {
    const response = await api.get(
      `/api/campaigns/${encodeURIComponent(campaignId)}/links`,
    );
    return response.data;
  },

  async getCampaignLinkTraffic(
    campaignId: string,
    startDate: string,
    endDate: string,
    granularity: GoLinkSeriesGranularity = "day",
    alignLivestream = false,
  ): Promise<CampaignLinkTrafficResponse> {
    const response = await api.get(
      `/api/campaigns/${encodeURIComponent(campaignId)}/links/traffic`,
      {
        params: {
          startDate,
          endDate,
          granularity,
          ...(alignLivestream ? { alignLivestream: "true" } : {}),
        },
      },
    );
    return response.data;
  },

  async getCampaignContent(
    campaignId: string,
    startDate: string,
    endDate: string,
    page = 1,
    limit = 20,
    platform: CampaignPlatformKey | "all" = "all",
    sortBy: CampaignContentSortKey = "views",
    sortDir: CampaignContentSortDir = "desc",
    mainTopicCategory?: string,
    taggedAccount?: string,
    q?: string,
  ): Promise<CampaignContentResponse> {
    const response = await api.get(
      `/api/campaigns/${encodeURIComponent(campaignId)}/content`,
      {
        params: {
          startDate,
          endDate,
          page: String(page),
          limit: String(limit),
          platform,
          sortBy,
          sortDir,
          ...(mainTopicCategory ? { mainTopicCategory } : {}),
          ...(taggedAccount ? { taggedAccount } : {}),
          ...(q?.trim() ? { q: q.trim() } : {}),
        },
      },
    );
    return response.data;
  },

  async getCampaignContentClassificationSummary(
    campaignId: string,
    startDate: string,
    endDate: string,
    platform: CampaignPlatformKey | "all" = "all",
  ): Promise<CampaignContentClassificationSummary> {
    const response = await api.get(
      `/api/campaigns/${encodeURIComponent(campaignId)}/content/classification-summary`,
      {
        params: {
          startDate,
          endDate,
          platform,
        },
      },
    );
    return response.data;
  },

  async getCampaignContentClassificationInsights(
    campaignId: string,
    startDate: string,
    endDate: string,
    platform: CampaignPlatformKey | "all" = "all",
    sortBy: CampaignInsightSortBy = "views",
  ): Promise<CampaignContentClassificationInsights> {
    const response = await api.get(
      `/api/campaigns/${encodeURIComponent(campaignId)}/content/classification-insights`,
      {
        params: {
          startDate,
          endDate,
          platform,
          sortBy,
        },
      },
    );
    return response.data;
  },

  async getCampaignHashtagMatches(
    campaignId: string,
    startDate: string,
    endDate: string,
  ): Promise<CampaignHashtagResponse> {
    const response = await api.get(
      `/api/campaigns/${encodeURIComponent(campaignId)}/hashtag-matches`,
      { params: { startDate, endDate } },
    );
    return response.data;
  },

  async getGoLinkDetailStats(
    slug: string,
    startDate: string,
    endDate: string,
    granularity: GoLinkSeriesGranularity = "hour",
  ): Promise<GoLinkDetailStatsResponse> {
    const response = await api.get(
      `/api/go-links/${encodeURIComponent(slug)}/stats`,
      { params: { startDate, endDate, granularity } },
    );
    return response.data;
  },

  // ─── Admin marketing email ────────────────────────────────────────────────

  async getMarketingEmailRecipients(params?: {
    q?: string;
    page?: number;
    limit?: number;
    activeOnly?: boolean;
  }): Promise<MarketingEmailRecipientsResponse> {
    const response = await api.get("/api/admin/emailing/recipients", {
      params: {
        ...(params?.q ? { q: params.q } : {}),
        page: params?.page ?? 1,
        limit: params?.limit ?? 25,
        activeOnly: params?.activeOnly === false ? "false" : "true",
      },
    });
    return response.data;
  },

  async previewMarketingEmail(
    payload: MarketingEmailComposePayload,
  ): Promise<MarketingEmailPreview> {
    const response = await api.post("/api/admin/emailing/preview", payload);
    return response.data;
  },

  async sendMarketingEmailTest(
    payload: MarketingEmailComposePayload,
  ): Promise<{ message: string; to: string }> {
    const response = await api.post("/api/admin/emailing/test-send", payload);
    return response.data;
  },

  async sendMarketingEmailCampaign(
    payload: MarketingEmailComposePayload & {
      userIds: string[];
      confirm: true;
    },
  ): Promise<{ campaign: MarketingEmailCampaignDto }> {
    const response = await api.post("/api/admin/emailing/send", payload);
    return response.data;
  },

  async getMarketingEmailCampaigns(params?: {
    page?: number;
    limit?: number;
  }): Promise<MarketingEmailCampaignsResponse> {
    const response = await api.get("/api/admin/emailing/campaigns", {
      params: {
        page: params?.page ?? 1,
        limit: params?.limit ?? 20,
      },
    });
    return response.data;
  },

  // ─── Admin metrics report schedules ───────────────────────────────────────

  async getAdminMetricsReportSchedules(params?: {
    q?: string;
    enabled?: boolean;
    companyId?: string;
    page?: number;
    limit?: number;
  }): Promise<import("./metricsReportAdminTypes").AdminMetricsReportsListResponse> {
    const response = await api.get("/api/admin/metrics-reports/schedules", {
      params: {
        ...(params?.q ? { q: params.q } : {}),
        ...(params?.enabled === true ? { enabled: "true" } : {}),
        ...(params?.companyId ? { companyId: params.companyId } : {}),
        page: params?.page ?? 1,
        limit: params?.limit ?? 25,
      },
    });
    return response.data;
  },

  async patchAdminMetricsReportSchedule(
    scheduleId: string,
    patch: Partial<
      import("../miContenido/metricsReportSchedule").MetricsReportScheduleUpsertPayload
    >,
  ): Promise<{ schedule: import("./metricsReportAdminTypes").AdminMetricsReportScheduleDto }> {
    const response = await api.patch(
      `/api/admin/metrics-reports/schedules/${encodeURIComponent(scheduleId)}`,
      patch,
    );
    return response.data;
  },

  async sendAdminMetricsReportNow(
    scheduleId: string,
  ): Promise<{ ok: boolean; status?: string }> {
    const response = await api.post(
      `/api/admin/metrics-reports/schedules/${encodeURIComponent(scheduleId)}/send-now`,
      { confirm: true },
    );
    return response.data;
  },
};

// ── Pipeline orchestrator types ────────────────────────────────────────────────

export type PipelineJobKind =
  | "tokens_all_weekly"
  | "explorer_long_pipeline"
  | "explorer_long_discover_classify"
  | "explorer_long_complete_pipeline"
  | "explorer_long_categories_weekly"
  | "explorer_metrics_youtube"
  | "company_short_metrics_refresh"
  | "company_long_metrics_daily"
  | "company_oauth_pipeline_weekly"
  | "company_short_daily"
  | "email_weekly_ig_posts"
  | "email_daily_digest";

export interface PipelineSchedule {
  _id: string;
  kind: PipelineJobKind;
  enabled: boolean;
  cronExpr: string;
  timezone: string;
  scope: { mode: string; ids?: string[] };
  pipelineOptions?: Record<string, unknown>;
  lastRunAt?: string | null;
  lastRunStatus?: "success" | "failed" | "skipped" | null;
  lastErrorMessage?: string | null;
  lastFailedStep?: string | null;
  isStale?: boolean;
  nextRunAt?: string | null;
  updatedAt: string;
}

export interface PipelineRun {
  _id: string;
  kind: PipelineJobKind;
  tenantType: "podcast" | "company" | "global";
  tenantId?: string;
  status: "pending" | "running" | "success" | "failed" | "skipped";
  trigger: "scheduled" | "manual";
  command: Record<string, unknown>;
  exitCode?: number;
  errorMessage?: string;
  failedStep?: string;
  requestedAt: string;
  startedAt?: string;
  finishedAt?: string;
  createdAt: string;
}

export interface PipelineDigestPreview {
  reportDateStr: string;
  windowStart: string;
  windowEnd: string;
  html: string;
  text: string;
  digest: {
    reportDateStr: string;
    totals: {
      runs: number;
      success: number;
      failed: number;
      skipped: number;
      missingJobs: number;
    };
    perKind: Array<{
      kind: PipelineJobKind;
      label: string;
      counts: {
        success: number;
        failed: number;
        skipped: number;
        pending: number;
        running: number;
        total: number;
      };
      worstStatus: string;
      windowFailedStep: string | null;
      companiesOk?: string;
    }>;
    failureGroups: Array<{
      kind: PipelineJobKind;
      label: string;
      failedCount: number;
      failedStep: string | null;
      detailLine: string;
      contextNote?: string;
      items: Array<{
        tenantLabel: string;
        failedStep: string | null;
        reason: string;
      }>;
    }>;
    missing: Array<{
      kind: PipelineJobKind;
      label: string;
      reason: string;
    }>;
  };
  livestream: {
    remoteIngestMode: "auto" | "manual";
    remoteIngestEnabled: boolean;
    workers: Array<{
      workerKey: string;
      label: string;
      scriptName: string;
      displayHostname: string;
      livestreamProfile: "local" | "remote" | null;
      isRemote: boolean;
      percentHealthy: number;
      percentError: number;
      percentStaleOrOffline: number;
      secondsHealthy: number;
      secondsError: number;
      secondsStaleOrOffline: number;
      sampleCount: number;
      noData: boolean;
    }>;
  };
}

// ── Company content plan types ─────────────────────────────────────────────────

export interface ContentPlanScope {
  podcastId: string;
  programIds?: string[];
  brandIds?: string[];
  /** Populated only by the list endpoint (`getCompanyContentPlans`). */
  podcastTitle?: string | null;
  programNames?: string[];
}

export interface CompanyContentPlanInput {
  companyId: string;
  label?: string;
  enabled?: boolean;
  scopes: ContentPlanScope[];
}

export interface CompanyContentPlan {
  _id: string;
  companyId: string;
  companyName?: string | null;
  label?: string;
  enabled: boolean;
  scopes: ContentPlanScope[];
  createdAt: string;
  updatedAt: string;
}

export interface CompanyLite {
  _id: string;
  name: string;
  slug?: string;
}

export interface PodcastWithPrograms {
  _id: string;
  title: string;
  programs: { _id: string; name: string }[];
  hasConnectedAccounts?: boolean;
  socialHandles?: { instagram?: string; tiktok?: string; facebook?: string };
  canApifyFallback?: boolean;
}

export interface GoLink {
  slug: string;
  shortUrl: string;
  destination: string;
  youtubeVideoId: string;
  platform: string;
  description: string | null;
  campaignId: string | null;
  companyId: string;
  createdAt: string;
  updatedAt: string | null;
}

/** @deprecated Use GoLink */
export type PodcastGoLink = GoLink;

export interface GoLinkBreakdownRow {
  key: string;
  count: number;
  pct: number;
}

export interface GoLinkStatRow {
  slug: string;
  totalClicks: number;
  lastClickAt: string | null;
}

export interface GoLinkStatsResponse {
  startDate: string;
  endDate: string;
  totalClicks: number;
  activeLinks: number;
  byPlatform: Array<{ platform: string; count: number }>;
  linkStats: GoLinkStatRow[];
}

/** @deprecated Use GoLinkStatsResponse */
export type PodcastGoLinkStatsResponse = GoLinkStatsResponse;

export interface CampaignProgramSummary {
  _id: string;
  name: string;
  podcastId: string;
  podcastTitle: string;
}

export interface Campaign {
  _id: string;
  companyId: string;
  name: string;
  description: string | null;
  startDate: string | null;
  endDate: string | null;
  status: "scheduled" | "active" | "ended";
  programIds: string[];
  programs: CampaignProgramSummary[];
  hashtags: string[];
  createdAt: string;
  updatedAt: string;
}

export interface CampaignInput {
  name: string;
  description?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  programIds: string[];
  hashtags?: string[];
}

export interface CampaignStatsResponse {
  startDate: string;
  endDate: string;
  totalClicks: number;
  activeLinks: number;
  totalLinks: number;
  linkStats: GoLinkStatRow[];
}

export interface CampaignLinkGeoBreakdown {
  key: string;
  count: number;
  pct: number;
}

export interface CampaignTopContentItem {
  _id: string;
  title: string | null;
  url: string | null;
  thumbnail: string | null;
  publishedAt: string | null;
  views: number;
  likes: number;
  comments: number;
  platformBucket: CampaignPlatformKey;
  programId: string | null;
  programName: string | null;
  hasBrand: boolean;
  brandNames: string[];
}

export interface CampaignPodscopeSummary {
  avgViewers: number;
  peakViewers: number;
  avgChatMessages: number;
  avgChatUniqueUsers: number;
  streamCount: number;
}

export interface CampaignOverviewResponse {
  startDate: string;
  endDate: string;
  linkStats: CampaignStatsResponse;
  platformSummaries: CampaignPlatformSummary[];
  podscopeSummary: CampaignPodscopeSummary | null;
  contentCount: number;
  linkGeo: {
    startDate: string;
    endDate: string;
    totalClicks: number;
    byCountry: CampaignLinkGeoBreakdown[];
  };
  topContent: CampaignTopContentItem[];
}

export interface CampaignTrendRow {
  period: string;
  periodStart: string;
  youtubeLongViews: number;
  youtubeShortsViews: number;
  instagramViews: number;
  facebookViews: number;
  tiktokViews: number;
  podscopeAvgViewers: number;
  podscopePeakViewers: number;
}

export interface CampaignTrendsResponse {
  startDate: string;
  endDate: string;
  mode: "published_at" | "views_gained";
  granularity: "day" | "week" | "month";
  trends: CampaignTrendRow[];
}

export interface CampaignAudiencePodcast {
  podcastId: string;
  podcastTitle: string;
  slug: string | null;
  mediaKitEnabled: boolean;
  stats: Record<string, unknown> | null;
  error?: string;
}

export interface CampaignAudienceResponse {
  startDate: string;
  endDate: string;
  podcasts: CampaignAudiencePodcast[];
}

export interface CampaignRetentionSummaryResponse {
  startDate: string;
  endDate: string;
  totalYoutubeLongForm: number;
  withYoutubeRetention: number;
  totalSpotify: number;
  withSpotifyListenPct: number;
  youtubeRetentionCoveragePct: number;
  spotifyListenCoveragePct: number;
}

export type CampaignContentSortKey =
  | "views"
  | "publishedAt"
  | "title"
  | "platform"
  | "program"
  | "interactions";

export type CampaignContentSortDir = "asc" | "desc";

export interface CampaignContentItem {
  _id: string;
  title: string;
  publishedAt: string;
  source: string;
  mediaType: string;
  views?: number;
  likes?: number;
  comments?: number;
  podcastId: string;
  podcastTitle: string;
  programId?: string;
  programName?: string;
  programImageS3Key?: string | null;
  programImageUpdatedAt?: string | null;
  platformBucket?: CampaignPlatformKey;
  url?: string | null;
  imageS3Key?: string | null;
  thumbnail?: string | null;
  hasBrand?: boolean;
  brandNames?: string[];
  brands?: Array<{ brandKey: string; name: string; logo: string | null }>;
  livestreamAvgViewers?: number | null;
  livestreamPeakViewers?: number | null;
  mainTopic?: string | null;
  mainTopicCategory?: string | null;
  taggedAccounts?: string[];
  primaryCategories?: Array<{ category: string; percentage: number }>;
  topics?: Array<{ topicName: string; percentageOfContent?: number; source: string }>;
  namedPeople?: Array<{ name: string; handle?: string; source: string }>;
}

export interface CampaignContentClassificationSummary {
  startDate: string;
  endDate: string;
  mainTopics: Array<{ value: string; label: string; contentCount: number }>;
  taggedAccounts: Array<{ handle: string; contentCount: number }>;
}

export type CampaignInsightSortBy = "views" | "interactions";

export interface CampaignInsightTopicRow {
  value: string;
  label: string;
  contentCount: number;
  totalViews: number;
  totalInteractions: number;
  shareOfViews: number;
}

export interface CampaignInsightAccountRow {
  handle: string;
  contentCount: number;
  totalViews: number;
  totalInteractions: number;
  shareOfViews: number;
}

export interface CampaignContentClassificationInsights {
  startDate: string;
  endDate: string;
  sortBy: CampaignInsightSortBy;
  topTopics: CampaignInsightTopicRow[];
  topAccounts: CampaignInsightAccountRow[];
  campaignTotalViews: number;
  campaignTotalInteractions: number;
}

export interface CampaignContentResponse {
  startDate: string;
  endDate: string;
  sortBy?: CampaignContentSortKey;
  sortDir?: CampaignContentSortDir;
  items: CampaignContentItem[];
  total: number;
  page: number;
  limit: number;
}

export interface CampaignHashtagMatchItem {
  _id: string;
  title: string;
  publishedAt: string;
  source: string;
  matchedHashtags: string[];
  podcastId: string;
  podcastTitle: string;
  programName?: string;
}

export interface CampaignHashtagResponse {
  startDate: string;
  endDate: string;
  hashtags: string[];
  items: CampaignHashtagMatchItem[];
  total: number;
}

export type GoLinkSeriesGranularity = "day" | "hour" | "minute";

export interface GoLinkDetailStatsResponse {
  slug: string;
  startDate: string;
  endDate: string;
  seriesStartDate?: string;
  seriesEndDate?: string;
  granularity: GoLinkSeriesGranularity;
  totalClicks: number;
  linkCreatedAt: string | null;
  firstClickAt: string | null;
  lastClickAt: string | null;
  clickSeries: Array<{ bucket: string; count: number }>;
  byCountry: GoLinkBreakdownRow[];
  byDeviceType: GoLinkBreakdownRow[];
  byDevice: GoLinkBreakdownRow[];
  byOs: GoLinkBreakdownRow[];
  byLanguage: GoLinkBreakdownRow[];
  byReferrer: GoLinkBreakdownRow[];
}

export interface CampaignLinkTrafficSourceRow {
  key: string;
  label: string;
  count: number;
  pct: number;
}

export interface CampaignLinkTrafficLinkRow {
  slug: string;
  description: string | null;
  shortUrl: string;
  totalClicks: number;
  lastClickAt: string | null;
}

export interface CampaignLinkTrafficResponse {
  startDate: string;
  endDate: string;
  granularity: GoLinkSeriesGranularity;
  destinationLabel: string;
  totalClicks: number;
  totalLinks: number;
  firstClickAt: string | null;
  lastClickAt: string | null;
  seriesStartDate: string | null;
  seriesEndDate: string | null;
  clickSeries: Array<{ bucket: string; count: number }>;
  byReferrer: GoLinkBreakdownRow[];
  bySource: CampaignLinkTrafficSourceRow[];
  byDevice: CampaignLinkTrafficSourceRow[];
  byOs?: GoLinkBreakdownRow[];
  linkStats: CampaignLinkTrafficLinkRow[];
  livestreamViewerSeries: Array<{ bucket: string; avgViewers: number }>;
  hasLivestreamData: boolean;
  livestreamStreamCount: number;
  seriesStartBucket: string | null;
  seriesEndBucket: string | null;
  livestreamWindows?: Array<{ startBucket: string; endBucket: string }>;
}

export interface ChatUsageFilters {
  dateFrom: string;
  dateTo: string;
  /** `none` selects questions asked without a company. */
  companyId?: string;
  userId?: string;
}

function chatUsageQuery(params: ChatUsageFilters): URLSearchParams {
  const q = new URLSearchParams({ dateFrom: params.dateFrom, dateTo: params.dateTo });
  if (params.companyId) q.set("companyId", params.companyId);
  if (params.userId) q.set("userId", params.userId);
  return q;
}

export interface ChatUsageGroupStats {
  questions: number;
  costUsd: number;
  users: number;
  conversations: number;
  lastActiveAt: string | null;
  topIntent: string | null;
  clicks: number;
  collectionsSaved: number;
  errors: number;
}

export interface ChatUsageSummary {
  filters: ChatUsageFilters;
  totals: {
    questions: number;
    liveQuestions: number;
    backfilledQuestions: number;
    rateLimited: number;
    conversations: number;
    activeUsers: number;
    activeCompanies: number;
    modelCostUsd: number;
    classifierCostUsd: number;
    totalCostUsd: number;
    costPerQuestion: number;
    promptTokens: number;
    completionTokens: number;
    cachedTokens: number;
    errorRate: number;
    abortRate: number;
    maxTurnsRate: number;
    p50LatencyMs: number;
    p95LatencyMs: number;
    answersWithLinks: number;
    answersClicked: number;
    clickThroughRate: number;
    entityClicks: number;
    linkClicks: number;
    collectionsShown: number;
    collectionLinkClicks: number;
    collectionsSaved: number;
    verifyExportsShown: number;
    verifyExportClicks: number;
    unclassified: number;
    intentMismatches: number;
  };
  daily: Array<{ date: string; questions: number; costUsd: number; users: number; clicks: number }>;
  companies: Array<ChatUsageGroupStats & { companyId: string; name: string }>;
  users: Array<
    ChatUsageGroupStats & {
      userId: string;
      name: string;
      email: string;
      companyId: string;
      companyName: string;
    }
  >;
  intents: Array<{ intent: string; questions: number; costUsd: number }>;
  toolIntents: Array<{ intent: string; questions: number; costUsd: number }>;
  tools: Array<{ name: string; calls: number; errors: number; avgMs: number }>;
  actions: Array<{ type: string; count: number }>;
  clickedEntityTypes: Array<{ entityType: string; count: number }>;
  statuses: Array<{ status: string; count: number }>;
  topCostTurns: Array<{
    turnId: string;
    conversationId: string | null;
    userId: string;
    userName: string;
    companyName: string;
    question: string;
    intent: string;
    costUsd: number;
    modelCalls: number;
    promptTokens: number;
    completionTokens: number;
    toolCalls: number;
    status: string;
    createdAt: string;
  }>;
  truncated: boolean;
}

export interface ChatUsageConversationList {
  total: number;
  page: number;
  pageSize: number;
  conversations: Array<{
    conversationId: string;
    title: string;
    /** Set when the user deleted it from /chat. */
    deletedAt?: string | null;
    userId: string;
    userName: string;
    companyId: string;
    companyName: string;
    turns: number;
    costUsd: number;
    errors: number;
    firstAt: string;
    lastAt: string;
    topIntent: string | null;
    clicks: number;
    collectionsSaved: number;
  }>;
}

/** Mirrors `PermissionDef` in app-backend utils/permissions.ts. */
export interface PermissionDef {
  id: string;
  label: string;
  group: "explorer" | "product" | "chat";
  description?: string;
  requiresAll?: string[];
  requiresAny?: string[];
  available?: boolean;
}

export interface PermissionParityMismatch {
  expectedProducts: string[];
  actualProducts: string[];
  expectedExport: boolean;
  actualExport: boolean;
}

export interface CompanyPermissionsOverview {
  catalog: PermissionDef[];
  companyId: string;
  /** Null until the backfill or the panel writes it; the chat grants nothing until then. */
  stored: string[] | null;
  effective: string[];
  derived: string[];
  users: Array<{
    userId: string;
    name: string;
    email: string;
    /** Null or empty = inherits the company. */
    stored: string[] | null;
    /** What the chat grants this user. */
    resolved: string[];
    parity: PermissionParityMismatch | null;
  }>;
  mismatches: number;
}

export interface ChatUsageEvent {
  type: string;
  payload: Record<string, unknown> | null;
  createdAt: string;
}

export interface ChatUsageTurnDetail {
  turnId: string;
  source: "live" | "backfill";
  status: string;
  intent: string | null;
  toolIntent: string;
  topic: string | null;
  entities: string[];
  model: string;
  modelCalls: number;
  promptTokens: number;
  completionTokens: number;
  cachedTokens: number;
  modelCostUsd: number;
  classifierCostUsd: number;
  latencyMs: number;
  tools: Array<{ name: string; args?: string; ok: boolean; error?: string; durationMs: number }>;
  events: ChatUsageEvent[];
}

export interface ReviewTimeByPodcastRow {
  podcastId: string;
  title: string | null;
  imageUrl: string | null;
  totalMs: number;
  contentSeconds: number;
  episodesReviewed: number;
  mentionsReviewed: number;
  mentionsAccepted: number;
  byUser: Array<{ userId: string; name: string; totalMs: number }>;
}

export interface ReviewTimeByPodcastResponse {
  weekKey: string;
  totalMs: number;
  untrackedMs: number;
  podcasts: ReviewTimeByPodcastRow[];
}

export interface ChatUsageConversationDetail {
  conversationId: string;
  title: string;
  userId: string;
  userName: string;
  userEmail: string;
  companyName: string;
  createdAt: string;
  updatedAt: string;
  /** Set when the user deleted it from /chat. */
  deletedAt?: string | null;
  totalCostUsd: number;
  messages: Array<{
    role: "user" | "assistant";
    text: string;
    createdAt: string;
    error?: string;
    collectionLinks?: number;
    verifyExports?: number;
    turnId?: string;
    turns?: ChatUsageTurnDetail[];
  }>;
  otherEvents: ChatUsageEvent[];
}

export interface ReviewAuditKeyCount {
  key: string;
  count: number;
}

export type ReviewAuditCaseKind = "rejected" | "edited" | "added";

export interface ReviewAuditCaseOriginal {
  brandName?: string;
  segmentStart?: number;
  segmentEnd?: number;
  mentionType?: string;
  placementType?: string;
  commercialConfidence?: number;
  visualCue?: string | null;
}

export interface ReviewAuditCaseMention {
  mentionId: string;
  contentId: string;
  podcastId: string;
  contentTitle: string;
  podcastTitle: string;
  brandName: string;
  segmentStart: number;
  segmentEnd: number;
  mentionType: string | null;
  placementType: string | null;
  evidenceType: string | null;
  visualCue: string | null;
  model: string | null;
  kind: ReviewAuditCaseKind;
  rejectReason: string | null;
  editedFields: string[];
  original: ReviewAuditCaseOriginal | null;
  reviewedAt: string | null;
  lastProbe: ReviewAuditCaseLastProbe | null;
}

/** Latest analyze replay for a case row. */
export interface BrandTranscriptFingerprint {
  mentionId: string;
  text: string;
  createdAt: string;
  manual?: boolean;
}

export interface ReviewAuditCaseClip {
  mentionId: string;
  fileName: string;
  filePath: string;
  padSec: number;
  startSec: number;
  endSec: number;
  sizeBytes: number;
  createdAt: string;
}

export const LOUPE_CUE_CLASSES = [
  "banner",
  "curtain",
  "lateral",
  "overlay",
  "placement",
  "program_banner",
  "show_promo",
] as const;

/** A Loupe dataset label (loupe_frames) on the case's episode. */
export interface ReviewAuditDatasetFrame {
  frameId: string;
  tsec: number;
  present: boolean;
  cues: Array<{ cueClass: string; brands?: string[] }>;
  note: string | null;
  labeledAt: string | null;
}

export interface ReviewAuditCaseLastProbe {
  probeId: string;
  emitted: boolean;
  /** Rejected: replay no longer emits the brand. Added / edited: replay emits it. */
  matchesReview: boolean;
  whyCategory: string;
  createdAt: string | null;
}

export interface ReviewAuditCaseCategory {
  mode: string;
  label: string;
  kind: ReviewAuditCaseKind;
  count: number;
  mentions: ReviewAuditCaseMention[];
}

export interface ReviewAuditCasesSummary {
  window: { dateFrom: string; dateTo: string };
  totalWrong: number;
  categories: ReviewAuditCaseCategory[];
}

export interface ReviewAuditCaseDetail {
  mention: ReviewAuditCaseMention & {
    mentionText: string | null;
    /** The mention's brand, or the brand whose name matches it exactly. */
    brandId: string | null;
  };
  categories: Array<{ mode: string; label: string }>;
  contentUrl: string | null;
  transcript: Array<{
    start: number;
    end: number;
    text: string;
    /** Episode-absolute word timings, when the transcript has them. */
    words?: Array<{ word: string; start: number; end: number }>;
  }>;
  transcriptAvailable: boolean;
  transcriptWindow: { start: number; end: number };
  loupe: ReviewAuditCaseLoupe;
  probe: ReviewAuditCaseProbe | null;
}

export interface ReviewAuditCaseProbe {
  mentionId: string;
  kind: ReviewAuditCaseKind;
  brandName: string;
  run: { id: string; algorithmVersion: string; transcriptOnly: boolean } | null;
  window: { viewStart: number; viewEnd: number; coreStart: number; coreEnd: number };
  inputs: {
    catalogSize: number;
    brandStatus: string;
    brandSpoken: boolean;
    brandOnScreen: boolean;
    transcriptLines: number;
  };
  mentions: Array<{
    brand: string;
    cue: string;
    startSec: number;
    endSec: number;
    evidenceType?: string;
    confidence?: number;
    reason?: string;
    mentionText?: string;
  }>;
  emitted: boolean;
  /** Edited mentions: corrected fields, detector vs human values. */
  review?: {
    correctedFields: string[];
    detector: Record<string, string | number | null | undefined>;
    human: Record<string, string | number | null | undefined>;
  } | null;
  /** Timing corrections: replay span vs human span. */
  timing?: {
    replayStart: number | null;
    replayEnd: number | null;
    startDeltaSec: number | null;
    endDeltaSec: number | null;
    ok: boolean;
  } | null;
  matchesReview?: boolean;
  why: { category: string; reason: string };
  createdAt: string;
  /** Missing on replays run before ids were returned. */
  probeId?: string;
}

export interface ReviewAuditCaseLoupeFrame {
  tsec: number;
  present: boolean;
  cue: string | null;
  effectiveCue: string | null;
  sievePresent: boolean | null;
  sieveCue: string | null;
  cueSet: string[];
  cueDecision: string | null;
  brandsByCue: Record<string, string[]>;
  brands: string[];
  sieveConfidence: number | null;
  cueConfidence: number | null;
  segmentConfidence: number | null;
  sieveReason: string | null;
  cueReason: string | null;
  segmentReason: string | null;
}

export interface ReviewAuditCaseLoupe {
  status: "none" | "transcript-only" | "visual";
  runId: string | null;
  algorithmVersion: string | null;
  completedAt: string | null;
  brandGroups: Array<{
    brand: string;
    cue: string;
    startSec: number;
    endSec: number;
    cueConfidence: number | null;
    brandConfidence: number | null;
  }>;
  frames: ReviewAuditCaseLoupeFrame[];
}

export interface ReviewAuditAiTokenCost {
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  estimatedCostUsd: number;
}

export interface ReviewAuditRunpodSpend {
  processingDurationSeconds: number;
  gpuHours: number;
  estimatedCostUsd: number;
}

export interface ReviewAuditAiSpendStep {
  scriptVersion: string;
  totalTokens: number;
  estimatedCostUsd: number;
}

export interface ReviewAuditAiSpendWeekly {
  week: string;
  weekStart: string;
  episodesWithUsage: number;
  gpt: ReviewAuditAiTokenCost;
  gemini: ReviewAuditAiTokenCost;
  runpod: ReviewAuditRunpodSpend;
  whisper: ReviewAuditRunpodSpend;
  sieve4b: ReviewAuditRunpodSpend;
  validate32b: ReviewAuditRunpodSpend;
  shortQwen: ReviewAuditRunpodSpend;
  loupeQwen: ReviewAuditRunpodSpend;
  totalEstimatedCostUsd: number;
}

export interface ReviewAuditAiSpendPerContent {
  contentId: string;
  gpt: ReviewAuditAiTokenCost;
  gemini: ReviewAuditAiTokenCost;
  whisper: ReviewAuditRunpodSpend;
  sieve4b: ReviewAuditRunpodSpend;
  validate32b: ReviewAuditRunpodSpend;
  shortQwen: ReviewAuditRunpodSpend;
  loupeQwen: ReviewAuditRunpodSpend;
  totalEstimatedCostUsd: number;
}

export interface ReviewAuditAiSpend {
  episodesInQuality: number;
  episodesWithUsage: number;
  gpt: ReviewAuditAiTokenCost;
  gemini: ReviewAuditAiTokenCost;
  runpod: ReviewAuditRunpodSpend;
  whisper: ReviewAuditRunpodSpend;
  sieve4b: ReviewAuditRunpodSpend;
  validate32b: ReviewAuditRunpodSpend;
  shortQwen: ReviewAuditRunpodSpend;
  loupeQwen: ReviewAuditRunpodSpend;
  totalEstimatedCostUsd: number;
  byStep: ReviewAuditAiSpendStep[];
  weekly: ReviewAuditAiSpendWeekly[];
  perContent: ReviewAuditAiSpendPerContent[];
}

export interface ReviewAuditSummary {
  windows: {
    quality: { dateFrom: string; dateTo: string };
    effort: { dateFrom: string; dateTo: string };
  };
  effort: {
    touched: number;
    episodesTouched: number;
    episodesCompleted: number;
    actions: {
      rejected: number;
      added: number;
      edited: number;
      cleanAccept: number;
    };
    byPlacement: ReviewAuditKeyCount[];
    byFlow: ReviewAuditKeyCount[];
    rejectReasons: ReviewAuditKeyCount[];
    rejectLeverage: ReviewAuditKeyCount[];
    editedFields: ReviewAuditKeyCount[];
    perPodcast: Array<{
      podcastId: string;
      title: string;
      rejected: number;
      added: number;
      edited: number;
      cleanAccept: number;
      total: number;
    }>;
    perContent: Array<{
      contentId: string;
      podcastId: string;
      title: string;
      rejected: number;
      added: number;
      edited: number;
      cleanAccept: number;
      total: number;
    }>;
  };
  quality: {
    episodes: number;
    proposed: number;
    accepted: number;
    corrected: number;
    rejected: number;
    missing: number;
    pending: number;
    precision: number | null;
    recall: number | null;
    editRate: number | null;
    cleanAcceptRate: number | null;
    flows: Array<{
      flow: string;
      label: string;
      proposed: number;
      rejected: number;
      fpRate: number | null;
      topRejectReasons: ReviewAuditKeyCount[];
    }>;
    leverage: Array<{ mode: string; label: string; count: number }>;
    editedFields: ReviewAuditKeyCount[];
    weekly: Array<{
      week: string;
      weekStart: string;
      detected: number;
      accepted: number;
      corrected: number;
      rejected: number;
      missing: number;
      pending: number;
      precision: number | null;
      recall: number | null;
      editRate: number | null;
      cleanAcceptRate: number | null;
      episodesReviewed: number;
      analyzedHours?: number;
      episodesAnalyzed?: number;
      byPipeline?: Record<
        "loupe" | "whisper",
        { analyzedHours: number; episodes: number; mentions: number; aiCostUsd: number }
      >;
    }>;
    perPodcast: Array<{
      podcastId: string;
      title: string;
      episodes: number;
      proposed: number;
      rejected: number;
      corrected: number;
      precision: number | null;
      recall: number | null;
      editRate: number | null;
    }>;
    perContent: Array<{
      contentId: string;
      podcastId: string;
      precision: number | null;
    }>;
  };
  aiSpend?: ReviewAuditAiSpend;
  /** Manual review timer hours by ISO week (all users). Only weeks with tracked time. */
  trackedReviewHoursByWeek?: Record<string, number>;
}

export default apiService;
